import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  ROOM_TYPES, FURNITURE_TYPES,
  setDragRoomType, setHoverCell, placeRoom, placeCustomRoom, removeRoom, addOpening, removeOpening, resizeOpening, recolorOpening,
  moveRoom, resizeRoom, resizeRoomCorner, clearAllRooms, renameRoom, addWallSegment, removeWallSegment, clearWallSegments, updateWallSegment, goToPhase,
  addFreeWall, updateFreeWall, removeFreeWall, addFreeRoom, removeFreeRoom, moveFreeRoom, setFreeRoomDesign,
  addFreeWallOpening, removeFreeWallOpening, updateFreeWallOpening,
  moveFreeRoomVertex, setFreeRoomEdgeBulge, setRoomEdgeBulge, addFreeRoomOpening, removeFreeRoomOpening,
  FLOOR_MATERIALS, WALL_MATERIALS,
  setDragFurnitureType, placeFurniture, moveFurniture, rotateFurniture, resizeFurniture, removeFurniture, changeFurnitureType, recolorFurniture,
  setWallHeight,
  commitBuilderHistory, builderHistoryBack, builderHistoryNext,
  placePolygonRoom, mergeRooms,
  addWallSegOpening, removeWallSegOpening, addAutoWallOpening, removeAutoWallOpening,
  updateWallSegOpening, updateAutoWallOpening, moveOpening,
  loadDesign,
  WALL_DECOR_TYPES, isWallDecorType, WALL_DECOR_META,
} from '../../store/houseSlice';
import { chainFreeWallsToLoop, edgeControlPoint, edgePointAt, edgeLength, loopSignedArea, polygonToRects } from '../../utils/freeWallRoom';

const FILE_VERSION = 1;
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { saveProjectToWeb } from '../../firebase/workspaceService';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useT, Tx } from '../../i18n';
import './Builder.css';
import {
  RoomIcon, FurniturePaletteIcon, DoorIcon, WindowIcon, ArchIcon,
  PaintingIcon, AcIcon, ClockIcon, OutletIcon, RadiatorIcon,
} from '../icons/Icons';
import { saveOrShareImage, drawLogoWatermark } from '../../utils/screenshot';
import { useGuestMode } from '../../context/GuestModeContext';
import GuestSaveButton from '../GuestSaveButton';

// Icon components for the 5 wall-decor opening types, keyed by type — used
// wherever a WALL_DECOR_TYPES entry needs its palette/list icon.
const WALL_DECOR_ICON_CMP = {
  painting: PaintingIcon, ac: AcIcon, clock: ClockIcon, outlet: OutletIcon, radiator: RadiatorIcon,
};

// Shared label/emoji/default-size lookup for any opening type (door/window/
// archway/wall-decor) — used by the wall list, edit panels, and 2D symbols
// so every UI spot that names an opening type agrees with the others.
function openingTypeMeta(type, t) {
  if (type === 'door')    return { emoji: '🚪', label: t('door') };
  if (type === 'window')  return { emoji: '🪟', label: t('window') };
  if (type === 'archway') return { emoji: '⬛', label: t('archway') };
  const meta = WALL_DECOR_META[type];
  return { emoji: meta?.icon || '🔧', label: t(type) };
}
function openingDefaultSize(type) {
  if (type === 'archway') return { width: 1.2,  height: 2.4 };
  if (type === 'door')    return { width: 0.88, height: 2.06 };
  if (type === 'window')  return { width: 0.98, height: 0.88 };
  const dz = WALL_DECOR_DEFAULT_SIZE[type];
  return dz || { width: 0.5, height: 0.5 };
}
const WALL_DECOR_DEFAULT_SIZE = {
  painting: { width: 0.80, height: 0.60 },
  ac:       { width: 0.85, height: 0.30 },
  clock:    { width: 0.35, height: 0.35 },
  outlet:   { width: 0.10, height: 0.10 },
  radiator: { width: 1.00, height: 0.50 },
};
const WALL_DECOR_DEFAULT_SILL = {
  window: 0.9, painting: 1.30, ac: 2.15, clock: 1.55, outlet: 0.30, radiator: 0.12,
};
const OPENING_TYPE_COLOR_PRESETS = {
  door:     ['#c4a87a', '#a0856a', '#8b6914', '#5c3d0e', '#d4c4aa', '#ffffff'],
  window:   ['#3a78b0', '#5499cc', '#88bbdd', '#2a5a8a', '#b0cce0', '#ffffff'],
  archway:  ['#e0d8c8', '#ccc4b0', '#d4c8a8', '#b8a880', '#8c7a5a', '#ffffff'],
  painting: ['#8a6a45', '#5c3d0e', '#2a2622', '#c4a87a', '#d4c4aa', '#ffffff'],
  ac:       ['#f2f2ef', '#d8d8d4', '#b0b0ac', '#888884', '#404040', '#1c1c1c'],
  clock:    ['#2a2622', '#5c3d0e', '#8a6a45', '#c4a87a', '#b0b0ac', '#ffffff'],
  outlet:   ['#f2f2ef', '#e8e0d0', '#d8d0c0', '#3a3a3a', '#1c1c1c', '#c4a87a'],
  radiator: ['#f2f2ef', '#e8e0d0', '#c8c8c8', '#8a8a8a', '#3a3a3a', '#1c1c1c'],
};


// ─── POLYGON → ROOM RECTS CONVERSION ─────────────────────────────────────────
// Takes a closed chain of grid-intersection points [{x,y},...] (first === last).
// Returns { rects: [{x,y,w,h},...] } or null if the chain is not axis-aligned,
// too small, or encloses no grid cells.
function closedChainToRects(chain, GRID = DEFAULT_GRID) {
  if (!chain || chain.length < 4) return null; // need ≥3 segments

  // Parse segments and validate axis-aligned
  const segs = [];
  for (let i = 0; i < chain.length - 1; i++) {
    const a = chain[i], b = chain[i + 1];
    if (a.x === b.x && a.y === b.y) continue; // zero-length, skip
    if (a.x !== b.x && a.y !== b.y) return null; // diagonal — not supported
    segs.push(
      a.x === b.x
        ? { type: 'v', x: a.x, y1: Math.min(a.y, b.y), y2: Math.max(a.y, b.y) }
        : { type: 'h', y: a.y, x1: Math.min(a.x, b.x), x2: Math.max(a.x, b.x) }
    );
  }
  if (segs.length < 3) return null;

  const G = GRID;

  // Build wall-edge sets.
  // hWalls("col,row") = horizontal wall on the TOP edge of cell (col, row)
  //   → blocks movement between cell rows row-1 and row for that col.
  // vWalls("col,row") = vertical wall on the LEFT edge of cell (col, row)
  //   → blocks movement between cell columns col-1 and col for that row.
  const hWalls = new Set();
  const vWalls = new Set();
  for (const seg of segs) {
    if (seg.type === 'h') {
      for (let c = seg.x1; c < seg.x2; c++) hWalls.add(`${c},${seg.y}`);
    } else {
      for (let r = seg.y1; r < seg.y2; r++) vWalls.add(`${seg.x},${r}`);
    }
  }

  // BFS flood-fill from the virtual cell (-1,-1) which is always outside the grid.
  // Move to neighbour (nc,nr) only when there is no wall on the shared edge.
  //   right:  vWalls("nc,r")   blocks (c,r)→(c+1,r)
  //   left:   vWalls("c,r")    blocks (c,r)→(c-1,r)
  //   down:   hWalls("c,nr")   blocks (c,r)→(c,r+1)
  //   up:     hWalls("c,r")    blocks (c,r)→(c,r-1)
  const reach = new Set(['-1,-1']);
  const queue = [[-1, -1]];
  let qi = 0;
  while (qi < queue.length) {
    const [c, r] = queue[qi++];
    const moves = [
      [c + 1, r, !vWalls.has(`${c + 1},${r}`)],
      [c - 1, r, !vWalls.has(`${c},${r}`)],
      [c, r + 1, !hWalls.has(`${c},${r + 1}`)],
      [c, r - 1, !hWalls.has(`${c},${r}`)],
    ];
    for (const [nc, nr2, passable] of moves) {
      if (nc < -1 || nc > G || nr2 < -1 || nr2 > G) continue;
      const k = `${nc},${nr2}`;
      if (!reach.has(k) && passable) { reach.add(k); queue.push([nc, nr2]); }
    }
  }

  // Interior cells = valid grid cells NOT reachable from outside
  const byRow = {};
  for (let c = 0; c < G; c++) {
    for (let r = 0; r < G; r++) {
      if (!reach.has(`${c},${r}`)) (byRow[r] = byRow[r] || []).push(c);
    }
  }
  if (Object.keys(byRow).length === 0) return null;

  // Convert rows of cell columns to horizontal strips
  const strips = [];
  for (const [rowStr, cols] of Object.entries(byRow)) {
    const row = Number(rowStr);
    cols.sort((a, b) => a - b);
    let s = cols[0], prev = cols[0];
    for (let i = 1; i < cols.length; i++) {
      if (cols[i] === prev + 1) { prev = cols[i]; }
      else { strips.push({ x: s, y: row, w: prev - s + 1, h: 1 }); s = cols[i]; prev = cols[i]; }
    }
    strips.push({ x: s, y: row, w: prev - s + 1, h: 1 });
  }

  // Merge vertically adjacent strips that share the same x and w into taller rects
  strips.sort((a, b) => a.y !== b.y ? a.y - b.y : a.x - b.x);
  const rects = [{ ...strips[0] }];
  for (let i = 1; i < strips.length; i++) {
    const last = rects[rects.length - 1], cur = strips[i];
    if (cur.y === last.y + last.h && cur.x === last.x && cur.w === last.w) {
      last.h += cur.h;
    } else {
      rects.push({ ...cur });
    }
  }

  return { rects };
}

const CELL = 26;
const DEFAULT_GRID = 50; // fallback cell-count only; the live grid size comes from state.house.gridSize (see GRID_SIZES)

// Rects/rooms sharing a common wall (touching along an edge with >0 overlap;
// corner-only contact doesn't count). Used by the room-merge tool.
const roomRectList = (r) => (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
function rectsShareWall(a, b) {
  const shareV = (a.x + a.w === b.x || b.x + b.w === a.x) && Math.max(a.y, b.y) < Math.min(a.y + a.h, b.y + b.h);
  const shareH = (a.y + a.h === b.y || b.y + b.h === a.y) && Math.max(a.x, b.x) < Math.min(a.x + a.w, b.x + b.w);
  return shareV || shareH;
}
function roomsShareWall(roomA, roomB) {
  const ra = roomRectList(roomA), rb = roomRectList(roomB);
  return ra.some(a => rb.some(b => rectsShareWall(a, b)));
}

function findFreeCell(rooms, w, h, preferCol, preferRow, GRID = DEFAULT_GRID) {
  const overlaps = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
  const isFree = (col, row) => {
    if (col < 0 || row < 0 || col + w > GRID || row + h > GRID) return false;
    const nr = { x: col, y: row, w, h };
    return !rooms.some(r => {
      const rs = r.rects?.length > 0 ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
      return rs.some(rc => overlaps(rc, nr));
    });
  };
  if (isFree(preferCol, preferRow)) return { col: preferCol, row: preferRow };
  for (let row = 0; row + h <= GRID; row++)
    for (let col = 0; col + w <= GRID; col++)
      if (isFree(col, row)) return { col, row };
  return null;
}
const SNAP = 7; // quarter-cell snap for furniture
const DW = 22;  // door/window symbol width (legacy, kept for WallStrip preview)
const PX_PER_M = CELL; // 26 px per metre

// Returns ALL exterior wall segments for a compound room (bounding-box edges AND step walls).
// hSegs: horizontal walls [ { startCol, endCol, row, wall: 'top'|'bottom' } ] (local grid coords)
// vSegs: vertical walls   [ { col, startRow, endRow, wall: 'left'|'right' } ]
function getAllExteriorSegments(rects, room) {
  const cells = new Set();
  for (const r of rects)
    for (let row = r.y; row < r.y + r.h; row++)
      for (let col = r.x; col < r.x + r.w; col++)
        cells.add(`${col},${row}`);

  const ox = room.x, oy = room.y;

  // Collect per-cell exterior faces
  const topFaces = {}, botFaces = {}, leftFaces = {}, rightFaces = {};
  for (const key of cells) {
    const [col, row] = key.split(',').map(Number);
    const lc = col - ox, lr = row - oy;
    if (!cells.has(`${col},${row - 1}`)) (topFaces[lr]   = topFaces[lr]   || []).push(lc);
    if (!cells.has(`${col},${row + 1}`)) (botFaces[lr]   = botFaces[lr]   || []).push(lc);
    if (!cells.has(`${col - 1},${row}`)) (leftFaces[lc]  = leftFaces[lc]  || []).push(lr);
    if (!cells.has(`${col + 1},${row}`)) (rightFaces[lc] = rightFaces[lc] || []).push(lr);
  }

  function mergeH(facesByRow, wall) {
    const out = [];
    for (const [rStr, cols] of Object.entries(facesByRow)) {
      const lr = Number(rStr);
      cols.sort((a, b) => a - b);
      let s = cols[0], last = cols[0];
      for (let i = 1; i < cols.length; i++) {
        if (cols[i] === last + 1) { last = cols[i]; }
        else { out.push({ startCol: s, endCol: last + 1, row: lr, wall }); s = cols[i]; last = cols[i]; }
      }
      out.push({ startCol: s, endCol: last + 1, row: lr, wall });
    }
    return out;
  }

  function mergeV(facesByCol, wall) {
    const out = [];
    for (const [cStr, rows] of Object.entries(facesByCol)) {
      const lc = Number(cStr);
      rows.sort((a, b) => a - b);
      let s = rows[0], last = rows[0];
      for (let i = 1; i < rows.length; i++) {
        if (rows[i] === last + 1) { last = rows[i]; }
        else { out.push({ col: lc, startRow: s, endRow: last + 1, wall }); s = rows[i]; last = rows[i]; }
      }
      out.push({ col: lc, startRow: s, endRow: last + 1, wall });
    }
    return out;
  }

  return {
    hSegs: [...mergeH(topFaces, 'top'), ...mergeH(botFaces, 'bottom')],
    vSegs: [...mergeV(leftFaces, 'left'), ...mergeV(rightFaces, 'right')],
  };
}

// ─── COMPOUND ROOM OUTLINE ────────────────────────────────────────────────────
// Traces the outer boundary of a union of grid-aligned rects.
// Returns an SVG path string in pixel coords relative to (originX*C, originY*C).
function getCompoundOutlinePath(rects, C, originX, originY) {
  if (!rects || rects.length === 0) return '';

  // Build cell set
  const cells = new Set();
  for (const r of rects)
    for (let row = r.y; row < r.y + r.h; row++)
      for (let col = r.x; col < r.x + r.w; col++)
        cells.add(`${col},${row}`);

  // Directed boundary half-edges (clockwise exterior)
  // Each seg: [x1,y1, x2,y2]
  const segs = [];
  for (const key of cells) {
    const [col, row] = key.split(',').map(Number);
    const x = (col - originX) * C, y = (row - originY) * C;
    if (!cells.has(`${col},${row - 1}`)) segs.push([x,     y,     x + C, y    ]); // top
    if (!cells.has(`${col + 1},${row}`)) segs.push([x + C, y,     x + C, y + C]); // right
    if (!cells.has(`${col},${row + 1}`)) segs.push([x + C, y + C, x,     y + C]); // bottom
    if (!cells.has(`${col - 1},${row}`)) segs.push([x,     y + C, x,     y    ]); // left
  }

  // Build adjacency: start-point key → list of seg indices starting there
  const adj = {};
  segs.forEach((s, i) => {
    const k = `${s[0]},${s[1]}`; // start of this seg
    (adj[k] = adj[k] || []).push(i);
  });

  const used = new Array(segs.length).fill(false);
  const paths = [];

  for (let si = 0; si < segs.length; si++) {
    if (used[si]) continue;
    const pts = [];
    let cur = si;
    for (let guard = 0; guard < segs.length + 4; guard++) {
      if (used[cur]) break;
      used[cur] = true;
      pts.push(`${segs[cur][0]} ${segs[cur][1]}`);
      // Find next unused seg whose START equals this seg's END
      const k = `${segs[cur][2]},${segs[cur][3]}`;
      const nexts = (adj[k] || []).filter(i => !used[i]);
      if (!nexts.length) break;
      cur = nexts[0];
    }
    if (pts.length > 1) paths.push(`M ${pts.join(' L ')} Z`);
  }
  return paths.join(' ');
}

// ─── COLOR PALETTES ──────────────────────────────────────────────────────────
const DOOR_COLORS    = ['#c4a87a','#e8e2d8','#9b6b3a','#3e2314','#f2eeea','#505050'];
const WINDOW_COLORS  = ['#e8e0d0','#ddd0b8','#a8c4d0','#7a5030','#2a2a2a','#4a8898'];
const ARCH_COLORS    = ['#e0d8c8','#c0b090','#8c6848','#c8a448','#222222','#7a9870'];
const FURN_COLORS    = ['#c8a870','#a89070','#987858','#6b4a32','#8b6030','#4a7890',
                        '#7a9878','#c07848','#909090','#3a3a4a','#e0d8c8','#d4b0a0'];
const WALL_DECOR_COLORS = {
  painting: ['#8a6a45','#5c3d0e','#2a2622','#c4a87a','#d4c4aa','#ffffff'],
  ac:       ['#f2f2ef','#d8d8d4','#b0b0ac','#888884','#404040','#1c1c1c'],
  clock:    ['#2a2622','#5c3d0e','#8a6a45','#c4a87a','#b0b0ac','#ffffff'],
  outlet:   ['#f2f2ef','#e8e0d0','#d8d0c0','#3a3a3a','#1c1c1c','#c4a87a'],
  radiator: ['#f2f2ef','#e8e0d0','#c8c8c8','#8a8a8a','#3a3a3a','#1c1c1c'],
};

function ColorPalette({ colors, current, onChange }) {
  return (
    <div className="color-palette">
      {colors.map(c => (
        <button key={c} title={c}
          className={`color-swatch${current === c ? ' color-swatch--active' : ''}`}
          style={{ background: c }}
          onClick={() => onChange(c)}
        />
      ))}
      <input type="color" className="color-swatch color-swatch--custom"
        value={current && /^#[0-9a-f]{6}$/i.test(current) ? current : colors[0]}
        onChange={e => onChange(e.target.value)}
        title="Custom colour"
      />
    </div>
  );
}

// ─── FURNITURE SYMBOL SVG ────────────────────────────────────────────────────

function FurnitureSymbol({ type, w, h, color }) {
  const s = { fill: color, stroke: 'rgba(0,0,0,0.35)', strokeWidth: 1.5 };
  const d = 'rgba(0,0,0,0.2)'; // dark overlay fill

  const sym = (() => {
    switch (type) {
      case 'sofa': return <>
        <rect x={0} y={h * 0.25} width={w} height={h * 0.75} rx={3} {...s} />
        <rect x={0} y={0} width={w} height={h * 0.3} rx={3} {...s} />
        <rect x={0} y={0} width={w} height={h * 0.3} rx={3} fill={d} />
        <rect x={0} y={h * 0.25} width={w * 0.1} height={h * 0.75} rx={2} fill={d} />
        <rect x={w * 0.9} y={h * 0.25} width={w * 0.1} height={h * 0.75} rx={2} fill={d} />
        <line x1={w / 2} y1={h * 0.3} x2={w / 2} y2={h} stroke="rgba(0,0,0,0.18)" strokeWidth={1.2} />
      </>;
      case 'armchair': return <>
        <rect x={0} y={h * 0.28} width={w} height={h * 0.72} rx={3} {...s} />
        <rect x={0} y={0} width={w} height={h * 0.34} rx={3} {...s} />
        <rect x={0} y={0} width={w} height={h * 0.34} rx={3} fill={d} />
        <rect x={0} y={h * 0.28} width={w * 0.15} height={h * 0.72} rx={2} fill={d} />
        <rect x={w * 0.85} y={h * 0.28} width={w * 0.15} height={h * 0.72} rx={2} fill={d} />
      </>;
      case 'table': return <>
        <rect x={3} y={3} width={w - 6} height={h - 6} rx={3} {...s} />
        <circle cx={7} cy={7} r={3.5} fill="rgba(0,0,0,0.25)" />
        <circle cx={w - 7} cy={7} r={3.5} fill="rgba(0,0,0,0.25)" />
        <circle cx={7} cy={h - 7} r={3.5} fill="rgba(0,0,0,0.25)" />
        <circle cx={w - 7} cy={h - 7} r={3.5} fill="rgba(0,0,0,0.25)" />
      </>;
      case 'chair': return <>
        <rect x={2} y={h * 0.3} width={w - 4} height={h * 0.7} rx={2} {...s} />
        <path d={`M2,${h * 0.32} Q${w / 2},${h * 0.02} ${w - 2},${h * 0.32}`} fill={d} stroke="rgba(0,0,0,0.35)" strokeWidth={1.5} />
        <circle cx={4} cy={h - 4} r={2.5} fill="rgba(0,0,0,0.3)" />
        <circle cx={w - 4} cy={h - 4} r={2.5} fill="rgba(0,0,0,0.3)" />
      </>;
      case 'bed': return <>
        <rect x={0} y={0} width={w} height={h} rx={3} {...s} />
        <rect x={0} y={0} width={w} height={h * 0.16} rx={3} fill={d} />
        <rect x={w * 0.06} y={h * 0.2} width={w * 0.38} height={h * 0.16} rx={4} fill="#f5f0e8" stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
        <rect x={w * 0.56} y={h * 0.2} width={w * 0.38} height={h * 0.16} rx={4} fill="#f5f0e8" stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
        <line x1={0} x2={w} y1={h * 0.42} y2={h * 0.42} stroke="rgba(0,0,0,0.1)" strokeWidth={1.5} />
      </>;
      case 'desk': return <>
        <rect x={0} y={0} width={w} height={h} rx={2} {...s} />
        <rect x={w * 0.18} y={h * 0.1} width={w * 0.64} height={h * 0.5} rx={2} fill="#1a2030" stroke="rgba(0,0,0,0.5)" strokeWidth={1} />
        <rect x={w * 0.14} y={h * 0.68} width={w * 0.72} height={h * 0.2} rx={1} fill="rgba(0,0,0,0.14)" stroke="rgba(0,0,0,0.18)" strokeWidth={1} />
      </>;
      case 'tv': return <>
        <rect x={0} y={0} width={w} height={h} rx={2} fill={color} stroke="rgba(80,80,100,0.7)" strokeWidth={1.5} />
        <rect x={2} y={1} width={w - 4} height={h - 2} rx={1} fill="#0a0818" />
        <line x1={w * 0.4} y1={h} x2={w * 0.6} y2={h + 5} stroke="rgba(80,80,90,0.8)" strokeWidth={3} />
      </>;
      case 'shelf': return <>
        <rect x={0} y={0} width={w} height={h} rx={2} {...s} />
        <line x1={w / 3} y1={2} x2={w / 3} y2={h - 2} stroke="rgba(0,0,0,0.2)" strokeWidth={1} />
        <line x1={w * 2 / 3} y1={2} x2={w * 2 / 3} y2={h - 2} stroke="rgba(0,0,0,0.2)" strokeWidth={1} />
        <rect x={3} y={2} width={4} height={h - 4} rx={1} fill="rgba(220,90,50,0.5)" />
        <rect x={8} y={2} width={3} height={h - 4} rx={1} fill="rgba(50,110,200,0.5)" />
        <rect x={12} y={2} width={5} height={h - 4} rx={1} fill="rgba(50,150,70,0.5)" />
      </>;
      case 'wardrobe': return <>
        <rect x={0} y={0} width={w} height={h} rx={2} {...s} />
        <line x1={w / 2} y1={2} x2={w / 2} y2={h - 2} stroke="rgba(0,0,0,0.28)" strokeWidth={1.5} />
        <circle cx={w * 0.37} cy={h / 2} r={3} fill="rgba(200,170,130,0.9)" />
        <circle cx={w * 0.63} cy={h / 2} r={3} fill="rgba(200,170,130,0.9)" />
        <line x1={2} y1={h * 0.14} x2={w - 2} y2={h * 0.14} stroke="rgba(0,0,0,0.1)" strokeWidth={1} />
        <line x1={2} y1={h * 0.86} x2={w - 2} y2={h * 0.86} stroke="rgba(0,0,0,0.1)" strokeWidth={1} />
      </>;
      case 'bathtub': return <>
        <rect x={0} y={0} width={w} height={h} rx={8} fill={color} stroke="rgba(100,130,150,0.6)" strokeWidth={1.5} />
        <rect x={5} y={5} width={w - 10} height={h - 10} rx={6} fill="#c8e4f0" stroke="rgba(100,140,160,0.4)" strokeWidth={1} />
        <circle cx={w / 2} cy={h - 8} r={4} fill="rgba(160,170,180,0.7)" />
        <circle cx={w / 2 - 9} cy={h - 8} r={2.5} fill="rgba(160,170,180,0.6)" />
        <circle cx={w / 2 + 9} cy={h - 8} r={2.5} fill="rgba(160,170,180,0.6)" />
      </>;
      case 'toilet': return <>
        <rect x={1} y={0} width={w - 2} height={h * 0.32} rx={2} fill="rgba(230,230,220,0.95)" stroke="rgba(100,110,100,0.5)" strokeWidth={1.5} />
        <ellipse cx={w / 2} cy={h * 0.72} rx={w / 2 - 1} ry={h * 0.3} fill={color} stroke="rgba(100,110,100,0.5)" strokeWidth={1.5} />
        <ellipse cx={w / 2} cy={h * 0.72} rx={w / 2 - 4} ry={h * 0.25} fill="none" stroke="rgba(100,110,100,0.35)" strokeWidth={1} />
      </>;
      case 'sink': return <>
        <rect x={0} y={0} width={w} height={h} rx={3} fill={color} stroke="rgba(100,120,130,0.6)" strokeWidth={1.5} />
        <rect x={4} y={4} width={w - 8} height={h - 8} rx={5} fill="#cce4f0" stroke="rgba(100,140,160,0.4)" strokeWidth={1} />
        <circle cx={w / 2} cy={h / 2} r={3.5} fill="rgba(120,130,140,0.7)" />
        <rect x={w / 2 - 4} y={1} width={8} height={7} rx={2} fill="rgba(180,180,190,0.8)" />
      </>;
      case 'lamp': return <>
        <circle cx={w / 2} cy={h / 2} r={w / 2 - 1} fill={color} stroke="rgba(160,130,60,0.7)" strokeWidth={1.5} />
        <circle cx={w / 2} cy={h / 2} r={w / 2 - 5} fill="rgba(255,220,120,0.4)" />
        <circle cx={w / 2} cy={h / 2} r={3} fill="rgba(200,160,60,0.9)" />
      </>;
      case 'plant': return <>
        <circle cx={w / 2} cy={h / 2} r={w / 2 - 1} fill={color} stroke="rgba(40,100,40,0.5)" strokeWidth={1.5} />
        <circle cx={w / 2} cy={h / 2} r={w / 2 - 5} fill="rgba(80,160,60,0.35)" />
        <circle cx={w / 2} cy={h / 2} r={4} fill="rgba(40,80,30,0.6)" />
      </>;
      case 'rug': return <>
        <rect x={0} y={0} width={w} height={h} rx={4} fill={color} stroke="rgba(0,0,0,0.2)" strokeWidth={1.5} />
        <rect x={5} y={5} width={w - 10} height={h - 10} rx={2} fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
        <rect x={10} y={10} width={w - 20} height={h - 20} rx={2} fill="rgba(0,0,0,0.06)" />
        <line x1={w / 2} y1={10} x2={w / 2} y2={h - 10} stroke="rgba(0,0,0,0.08)" strokeWidth={1} />
        <line x1={10} y1={h / 2} x2={w - 10} y2={h / 2} stroke="rgba(0,0,0,0.08)" strokeWidth={1} />
      </>;
      default: return <rect x={2} y={2} width={w - 4} height={h - 4} rx={3} fill={color} stroke="rgba(0,0,0,0.35)" strokeWidth={1.5} />;
    }
  })();

  return (
    <svg
      width={w} height={h}
      style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}
    >
      {sym}
    </svg>
  );
}

// ─── FURNITURE PIECE (placed on grid) ────────────────────────────────────────

function FurniturePiece({ item, isSelected, onSelect, zoom = 1, GRID = DEFAULT_GRID, onDragReport }) {
  const dispatch = useDispatch();
  const ft = FURNITURE_TYPES.find(f => f.type === item.type);
  const [dragOffset, setDragOffset] = useState(null);
  const dragHappenedRef = useRef(false);
  const clickTimerRef   = useRef(null);
  const furnitureDivRef = useRef(null);
  const lastTapTimeRef  = useRef(0);
  // Always-current refs so touch closures always see fresh values
  const itemRef     = useRef(item);  itemRef.current = item;
  const zoomRef     = useRef(zoom);  zoomRef.current = zoom;
  const dispatchRef = useRef(dispatch); dispatchRef.current = dispatch;

  useEffect(() => () => { if (clickTimerRef.current) clearTimeout(clickTimerRef.current); }, []);

  // Native touch drag (mobile): single-touch → drag; double-tap cancels settings timer
  useEffect(() => {
    const el = furnitureDivRef.current;
    if (!el) return;
    let startX = 0, startY = 0, startItemX = 0, startItemY = 0;
    let capDisplayW = 0, capDisplayH = 0;
    let isDragging = false, currentDx = 0, currentDy = 0;

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      const now = Date.now();
      const gap = now - lastTapTimeRef.current;
      if (gap < 300 && gap > 0) {
        // Double-tap — cancel pending settings timer, skip panel
        e.preventDefault();
        lastTapTimeRef.current = 0;
        if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
        if (navigator.vibrate) navigator.vibrate(30);
        return;
      }
      lastTapTimeRef.current = now;
      const cur = itemRef.current;
      const rot = cur.rotation || 0;
      capDisplayW = rot % 180 === 0 ? cur.w : cur.h;
      capDisplayH = rot % 180 === 0 ? cur.h : cur.w;
      const touch = e.touches[0];
      startX = touch.clientX; startY = touch.clientY;
      startItemX = cur.x;    startItemY = cur.y;
      isDragging = false; currentDx = 0; currentDy = 0;
      dragHappenedRef.current = false;
    };

    const onTouchMove = (e) => {
      if (!e.touches[0]) return;
      currentDx = e.touches[0].clientX - startX;
      currentDy = e.touches[0].clientY - startY;
      if (Math.abs(currentDx) < 5 && Math.abs(currentDy) < 5) return;
      e.preventDefault();
      if (!isDragging) {
        if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
        isDragging = true;
        dragHappenedRef.current = true;
      }
      setDragOffset({ dx: currentDx, dy: currentDy });
    };

    const onTouchEnd = () => {
      setDragOffset(null);
      if (isDragging) {
        const z = zoomRef.current;
        const raw  = Math.round(currentDx / (SNAP * z)) * SNAP;
        const rawY = Math.round(currentDy / (SNAP * z)) * SNAP;
        const newX = Math.max(0, Math.min(GRID * CELL - capDisplayW, startItemX + raw));
        const newY = Math.max(0, Math.min(GRID * CELL - capDisplayH, startItemY + rawY));
        dispatchRef.current(moveFurniture({ id: itemRef.current.id, x: newX, y: newY }));
        dragHappenedRef.current = true;
      }
      isDragging = false; dragHappenedRef.current = false;
    };

    el.addEventListener('touchstart',  onTouchStart, { passive: false });
    el.addEventListener('touchmove',   onTouchMove,  { passive: false });
    el.addEventListener('touchend',    onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart',  onTouchStart);
      el.removeEventListener('touchmove',   onTouchMove);
      el.removeEventListener('touchend',    onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [item.id]);

  const rotation = item.rotation || 0;
  const displayW = rotation % 180 === 0 ? item.w : item.h;
  const displayH = rotation % 180 === 0 ? item.h : item.w;

  // Report the live grid-space bbox up to the Builder while dragging so it can
  // draw the furniture↔wall dimension lines that track the piece in real time
  // (and clear them — null — the moment the drag ends).
  useEffect(() => {
    if (!onDragReport) return;
    onDragReport(item.id, dragOffset
      ? { x: item.x + dragOffset.dx, y: item.y + dragOffset.dy, w: displayW, h: displayH }
      : null);
  }, [dragOffset]); // deliberately only re-fires on drag position change

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragHappenedRef.current = false;
    const startX = e.clientX;
    const startY = e.clientY;
    const startItemX = item.x;
    const startItemY = item.y;

    const onMouseMove = (mv) => {
      const dx = mv.clientX - startX;
      const dy = mv.clientY - startY;
      if (!dragHappenedRef.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      dragHappenedRef.current = true;
      setDragOffset({ dx, dy });
    };

    const onMouseUp = (mu) => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setDragOffset(null);
      if (dragHappenedRef.current) {
        const raw = Math.round((mu.clientX - startX) / (SNAP * zoom)) * SNAP;
        const rawY = Math.round((mu.clientY - startY) / (SNAP * zoom)) * SNAP;
        const newX = Math.max(0, Math.min(GRID * CELL - displayW, startItemX + raw));
        const newY = Math.max(0, Math.min(GRID * CELL - displayH, startItemY + rawY));
        dispatch(moveFurniture({ id: item.id, x: newX, y: newY }));
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Single click → open settings after delay; double click → just move (no panel)
  const handleClick = (e) => {
    e.stopPropagation();
    if (dragHappenedRef.current) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      if (navigator.vibrate) navigator.vibrate(30);
      return; // double-click: furniture is always draggable, just skip opening panel
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      onSelect();
    }, 260);
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startDisplayW = displayW;
    const startDisplayH = displayH;
    const MIN = 2.6; // 0.1m floor (26px/m), matches every other item's minimum

    const onMouseMove = (mv) => {
      const newDisplayW = Math.max(MIN, Math.round((startDisplayW + (mv.clientX - startX) / zoom) / SNAP) * SNAP);
      const newDisplayH = Math.max(MIN, Math.round((startDisplayH + (mv.clientY - startY) / zoom) / SNAP) * SNAP);
      // Map display dims back to stored dims, accounting for rotation
      const newW = rotation % 180 === 0 ? newDisplayW : newDisplayH;
      const newH = rotation % 180 === 0 ? newDisplayH : newDisplayW;
      dispatch(resizeFurniture({ id: item.id, w: newW, h: newH }));
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      dispatch(commitBuilderHistory());
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const innerTransform = rotation !== 0 ? {
    position: 'absolute',
    left: (displayW - item.w) / 2,
    top: (displayH - item.h) / 2,
    width: item.w,
    height: item.h,
    transform: `rotate(${rotation}deg)`,
    transformOrigin: 'center',
  } : { position: 'absolute', inset: 0 };

  return (
    <div
      ref={furnitureDivRef}
      className={`furniture-piece${isSelected ? ' furniture-piece--selected' : ''}${dragOffset ? ' furniture-piece--dragging' : ''}`}
      style={{
        left: item.x,
        top: item.y,
        width: displayW,
        height: displayH,
        transform: dragOffset ? `translate(${dragOffset.dx}px, ${dragOffset.dy}px)` : undefined,
        zIndex: dragOffset ? 60 : (isSelected ? 40 : 30),
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <div style={innerTransform}>
        <FurnitureSymbol type={item.type} w={item.w} h={item.h} color={item.color || ft?.color || '#a89070'} />
      </div>

      {/* Label — visible on hover when piece is big enough */}
      <span className="furniture-piece-label">{ft?.label}</span>

      {/* Controls — always inside the piece so grid overflow:hidden doesn't clip them */}
      <button
        className="furniture-btn furniture-btn-delete"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); dispatch(removeFurniture(item.id)); }}
        title="Remove"
      >×</button>

      <button
        className="furniture-btn furniture-btn-rotate"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); dispatch(rotateFurniture(item.id)); }}
        title="Rotate 90°"
      >↻</button>

      <div className="furniture-resize-handle" onMouseDown={handleResizeMouseDown} title="Drag to resize" />
    </div>
  );
}

// ─── FURNITURE ↔ WALL DIMENSION LINES ────────────────────────────────────────
// When a furniture item is selected or being dragged, draw the clear gap from
// each of its sides to the surrounding room's walls as measured dimension
// lines with a metre label — like an architectural floor plan.
const DIM_COLOR = '#2f7de1';

// Live wall-segment preview (the line that becomes the actual wall) — the
// tool's original blue, kept distinct from the green perpendicular distance
// guides below and from the red used for a blocked/overlapping wall.
const WALL_PREVIEW_COLOR = '#5096dc';
const WALL_PREVIEW_LABEL_COLOR = '#7ec8f7';

// Perpendicular gap guides (distance from the segment being drawn to nearby
// walls, on either side of it) — green, so they read as an auxiliary
// reference measurement rather than the wall segment itself.
const DIST_GUIDE_COLOR = '#37b06b';
const DIST_GUIDE_LABEL_COLOR = '#8fe8ae';

function fmtMeters(px) {
  return `${Math.round((px / CELL) * 100) / 100}m`;
}

// One dimension line with end ticks + a metre label. `horizontal` picks the
// tick orientation and keeps the label upright (vertical dims read bottom-up).
function DimLine({ x1, y1, x2, y2, horizontal }) {
  const T = 4;
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const label = fmtMeters(horizontal ? Math.abs(x2 - x1) : Math.abs(y2 - y1));
  return (
    <g style={{ pointerEvents: 'none' }}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={DIM_COLOR} strokeWidth={1} />
      {horizontal ? (
        <>
          <line x1={x1} y1={y1 - T} x2={x1} y2={y1 + T} stroke={DIM_COLOR} strokeWidth={1} />
          <line x1={x2} y1={y2 - T} x2={x2} y2={y2 + T} stroke={DIM_COLOR} strokeWidth={1} />
        </>
      ) : (
        <>
          <line x1={x1 - T} y1={y1} x2={x1 + T} y2={y1} stroke={DIM_COLOR} strokeWidth={1} />
          <line x1={x2 - T} y1={y2} x2={x2 + T} y2={y2} stroke={DIM_COLOR} strokeWidth={1} />
        </>
      )}
      <g transform={horizontal ? `translate(${mx},${my})` : `translate(${mx},${my}) rotate(-90)`}>
        <rect x={-17} y={-8} width={34} height={16} rx={3} fill="rgba(255,255,255,0.94)" stroke={DIM_COLOR} strokeWidth={0.75} />
        <text x={0} y={0.5} textAnchor="middle" dominantBaseline="central"
          fontSize={9.5} fontWeight={700} fontFamily="system-ui, sans-serif" fill={DIM_COLOR}>{label}</text>
      </g>
    </g>
  );
}

function FurnitureDimensions({ bbox, rooms }) {
  if (!bbox) return null;
  const cx = bbox.x + bbox.w / 2, cy = bbox.y + bbox.h / 2;
  // Measure against whichever room's bounding box contains the item's centre.
  const room = rooms.find(r => {
    const rx = r.x * CELL, ry = r.y * CELL;
    return cx >= rx && cx <= rx + r.w * CELL && cy >= ry && cy <= ry + r.h * CELL;
  });
  if (!room) return null;
  const rx = room.x * CELL, ry = room.y * CELL, rw = room.w * CELL, rh = room.h * CELL;
  const bx1 = bbox.x, by1 = bbox.y, bx2 = bbox.x + bbox.w, by2 = bbox.y + bbox.h;
  const MIN = 3; // ≈0.1m — below this the side is basically flush, skip its line
  return (
    <g>
      <rect x={bx1} y={by1} width={bbox.w} height={bbox.h} fill="none"
        stroke={DIM_COLOR} strokeWidth={1} strokeDasharray="4 3" opacity={0.75} />
      {bx1 - rx > MIN      && <DimLine x1={rx}  y1={cy} x2={bx1}    y2={cy} horizontal />}
      {rx + rw - bx2 > MIN && <DimLine x1={bx2} y1={cy} x2={rx + rw} y2={cy} horizontal />}
      {by1 - ry > MIN      && <DimLine x1={cx} y1={ry}  x2={cx} y2={by1} />}
      {ry + rh - by2 > MIN && <DimLine x1={cx} y1={by2} x2={cx} y2={ry + rh} />}
    </g>
  );
}

// Nearest wall coordinate from grid point `pt` travelling in one cardinal
// direction, considering only walls that actually span across the ray. Returns
// the wall's coordinate on the travel axis (grid units), or null if none.
// `walls` are {x1,y1,x2,y2} segments in grid units (auto-walls + freestanding).
function nearestWallInDir(pt, dir, walls) {
  const EPS = 1e-6;
  let best = null;
  for (const w of walls) {
    const isH = w.y1 === w.y2;
    if (dir === 'up' || dir === 'down') {
      if (!isH) continue;
      const lo = Math.min(w.x1, w.x2), hi = Math.max(w.x1, w.x2);
      if (pt.x < lo - EPS || pt.x > hi + EPS) continue;
      const wy = w.y1;
      if (dir === 'down' && wy > pt.y + EPS && (best === null || wy < best)) best = wy;
      if (dir === 'up'   && wy < pt.y - EPS && (best === null || wy > best)) best = wy;
    } else {
      if (isH) continue;
      const lo = Math.min(w.y1, w.y2), hi = Math.max(w.y1, w.y2);
      if (pt.y < lo - EPS || pt.y > hi + EPS) continue;
      const wx = w.x1;
      if (dir === 'right' && wx > pt.x + EPS && (best === null || wx < best)) best = wx;
      if (dir === 'left'  && wx < pt.x - EPS && (best === null || wx > best)) best = wx;
    }
  }
  return best;
}

// Static (non-dragging) px bounding box of a furniture item, rotation-aware.
function staticFurnitureBBox(item) {
  const rot = item.rotation || 0;
  return {
    x: item.x, y: item.y,
    w: rot % 180 === 0 ? item.w : item.h,
    h: rot % 180 === 0 ? item.h : item.w,
  };
}

// ─── OPENING COMPONENTS ───────────────────────────────────────────────────────

function WallStrip({ wall, roomW, roomH, onPlace }) {
  const [hoverPos, setHoverPos] = useState(null);
  const isH = wall === 'top' || wall === 'bottom';

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = isH
      ? (e.clientX - rect.left) / rect.width
      : (e.clientY - rect.top) / rect.height;
    setHoverPos(Math.max(0.08, Math.min(0.92, pos)));
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (hoverPos !== null) onPlace(hoverPos);
  };

  const style = {
    position: 'absolute', zIndex: 30, cursor: 'crosshair',
    ...(wall === 'top'    && { top: 0, left: 0, right: 0, height: 12 }),
    ...(wall === 'bottom' && { bottom: 0, left: 0, right: 0, height: 12 }),
    ...(wall === 'left'   && { top: 0, left: 0, bottom: 0, width: 12 }),
    ...(wall === 'right'  && { top: 0, right: 0, bottom: 0, width: 12 }),
  };

  return (
    <div className="wall-strip" style={style}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverPos(null)}
      onClick={handleClick}
    >
      {hoverPos !== null && (
        <div className="wall-strip-preview" style={{
          position: 'absolute',
          ...(isH
            ? { left: hoverPos * roomW - DW / 2, top: 0, width: DW, height: '100%' }
            : { top: hoverPos * roomH - DW / 2, left: 0, width: '100%', height: DW }),
        }} />
      )}
    </div>
  );
}

// Wall strip for a specific pixel-positioned segment (compound rooms)
function SegmentWallStrip({ isH, pxLeft, pxTop, pxW, pxH, onPlace }) {
  const [hoverPos, setHoverPos] = useState(null);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = isH
      ? (e.clientX - rect.left) / rect.width
      : (e.clientY - rect.top) / rect.height;
    setHoverPos(Math.max(0.05, Math.min(0.95, pos)));
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (hoverPos !== null) onPlace(hoverPos);
  };

  return (
    <div className="wall-strip" style={{ position: 'absolute', zIndex: 30, cursor: 'crosshair', left: pxLeft, top: pxTop, width: pxW, height: pxH }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverPos(null)}
      onClick={handleClick}
    >
      {hoverPos !== null && (
        <div className="wall-strip-preview" style={{
          position: 'absolute',
          ...(isH
            ? { left: hoverPos * pxW - DW / 2, top: 0, width: DW, height: '100%' }
            : { top: hoverPos * pxH - DW / 2, left: 0, width: '100%', height: DW }),
        }} />
      )}
    </div>
  );
}

function OpeningSymbol({ opening, roomW, roomH, roomColor, isSelected, onSelect, transform }) {
  const { type, wall, pos } = opening;
  const sw = (opening.width || openingDefaultSize(type).width) * PX_PER_M; // symbol width in px, scaled to actual size

  // absX/absY: absolute SVG coords for step-wall openings; fallback to bounding-box fractions
  let t;
  // Free-form rooms supply their own transform (a point + tangent angle along the
  // outline), so the identical symbol can sit on an angled or curved wall.
  if (transform) {
    t = transform;
  } else if (opening.absX !== undefined && opening.absY !== undefined) {
    t = wall === 'top'    ? `translate(${opening.absX},${opening.absY})` :
        wall === 'bottom' ? `translate(${opening.absX},${opening.absY}) rotate(180)` :
        wall === 'left'   ? `translate(${opening.absX},${opening.absY}) rotate(-90)` :
        /* right */         `translate(${opening.absX},${opening.absY}) rotate(90)`;
  } else {
    const cx = (wall === 'top' || wall === 'bottom') ? pos * roomW : 0;
    const cy = (wall === 'left' || wall === 'right') ? pos * roomH : 0;
    t = wall === 'top'    ? `translate(${cx},0)` :
        wall === 'bottom' ? `translate(${cx},${roomH}) rotate(180)` :
        wall === 'left'   ? `translate(0,${cy}) rotate(-90)` :
        /* right */         `translate(${roomW},${cy}) rotate(90)`;
  }

  // Wall-decor items don't cut a void, so they get a small mounted badge
  // instead of the jamb-cut marks + door/window/archway silhouette below.
  if (isWallDecorType(type)) {
    const bw = Math.max(sw, 16), bh = 16;
    const dc = opening.color || '#8a7a68';
    return (
      <g transform={t} onClick={(e) => { e.stopPropagation(); onSelect(opening.id); }}
        style={{ cursor: 'pointer', pointerEvents: 'all' }}>
        {isSelected && (
          <rect x={-bw / 2 - 4} y={-bh / 2 - 4} width={bw + 8} height={bh + 8}
            fill="rgba(184,147,90,0.18)" stroke="#b8935a" strokeWidth={1.5} rx={2} />
        )}
        <rect x={-bw / 2} y={-bh / 2} width={bw} height={bh} rx={3} fill={dc} stroke="#26211c" strokeWidth={1} />
        <text x={0} y={0.5} textAnchor="middle" dominantBaseline="central" fontSize={10}>{WALL_DECOR_META[type]?.icon}</text>
      </g>
    );
  }

  return (
    <g transform={t} onClick={(e) => { e.stopPropagation(); onSelect(opening.id); }}
      style={{ cursor: 'pointer', pointerEvents: 'all' }}>
      {isSelected && (
        <rect x={-sw / 2 - 4} y={-6} width={sw + 8} height={sw + 10}
          fill="rgba(184,147,90,0.18)" stroke="#b8935a" strokeWidth={1.5} rx={2} />
      )}
      {/* Orange jamb marks at each side of the opening — visible on the wall edge */}
      <line x1={-sw/2} y1={-5} x2={-sw/2} y2={4} stroke="#e07830" strokeWidth={3} strokeLinecap="round"/>
      <line x1={ sw/2} y1={-5} x2={ sw/2} y2={4} stroke="#e07830" strokeWidth={3} strokeLinecap="round"/>

      {type === 'door' ? (
        <>
          {(() => { const dc = opening.color || '#c4a87a'; return (<>
          <rect x={-sw / 2} y={-3} width={sw} height={5} fill={roomColor} />
          <line x1={sw / 2}  y1={0}  x2={sw / 2}  y2={sw} stroke={dc} strokeWidth={1.5} />
          <path d={`M${sw/2},${sw} A${sw},${sw} 0 0,0 ${-sw/2},0`} fill={dc + '40'} stroke={dc} strokeWidth={1} />
          </>); })()}
        </>
      ) : type === 'archway' ? (
        <>
          {(() => { const ac = opening.color || '#e0d8c8'; return (<>
          <rect x={-sw / 2} y={-3} width={sw} height={5} fill={roomColor} />
          <path d={`M${-sw/2},${sw*0.45} A${sw/2},${sw/2} 0 0,1 ${sw/2},${sw*0.45}`}
                fill={ac + '30'} stroke={ac} strokeWidth={1.5} />
          </>); })()}
        </>
      ) : (
        <>
          {(() => { const wc = opening.color || '#3a78b0'; return (<>
          <rect x={-sw / 2} y={-4} width={sw} height={8} fill={roomColor} />
          <rect x={-sw / 2} y={-3.5} width={sw} height={7} fill="none" stroke={wc} strokeWidth={1.5} />
          <line x1={-sw / 2} y1={0} x2={sw / 2} y2={0} stroke={wc} strokeWidth={1.5} />
          </>); })()}
        </>
      )}
    </g>
  );
}

// ─── WALL OPENING SYMBOL ─────────────────────────────────────────────────────
// Renders a door / window / archway symbol on a standalone wall segment.
// cx,cy: pixel center on the wall; isH: horizontal wall → symbol faces south.

// `angle` (degrees) overrides the horizontal/vertical rotation for a FREE wall,
// which runs at any angle — everything else about the symbol is identical, so a
// free wall's openings look and behave exactly like a straight wall's.
function WallOpeningSymbol({ type, cx, cy, isH, angle = null, width, color, isSelected, onClick, onPointerDown, armed, opacity = 1 }) {
  const rotDeg = angle != null ? angle : (isH ? 0 : 90);
  const armedCursor = angle != null ? 'move' : (isH ? 'ew-resize' : 'ns-resize');
  const isDecor = isWallDecorType(type);
  const sw = (width || (type === 'archway' ? 1.2 : type === 'door' ? 0.88 : type === 'window' ? 0.98 : 0.5)) * PX_PER_M;
  const dc = color || (type === 'door' ? '#c4a87a' : type === 'archway' ? '#e0d8c8' : type === 'window' ? '#3a78b0' : '#8a7a68');

  // Wall-decor items (painting/AC/clock/outlet/radiator) don't cut a gap in the
  // wall — WallLines renders it solid underneath — so draw a small badge marker
  // mounted flush on the wall face instead of the door/window "gap" symbols.
  if (isDecor) {
    const bw = Math.max(sw, 16), bh = 16;
    return (
      <g transform={`translate(${cx},${cy}) rotate(${rotDeg})`} opacity={opacity}
        onClick={onClick} onPointerDown={onPointerDown}
        style={{ cursor: armed ? armedCursor : onClick ? 'pointer' : 'default', pointerEvents: onClick ? 'all' : 'none', touchAction: armed ? 'none' : undefined }}>
        {armed && (
          <rect x={-bw/2-6} y={-bh/2-6} width={bw+12} height={bh+12}
            fill="none" stroke="#d4903a" strokeWidth={1.5} strokeDasharray="3 2" rx={3}/>
        )}
        {isSelected && (
          <rect x={-bw/2-4} y={-bh/2-4} width={bw+8} height={bh+8}
            fill="rgba(80,150,220,0.18)" stroke="#5096dc" strokeWidth={1.5} rx={2}/>
        )}
        <rect x={-bw/2} y={-bh/2} width={bw} height={bh} rx={3} fill={dc} stroke="#26211c" strokeWidth={1}/>
        <text x={0} y={0.5} textAnchor="middle" dominantBaseline="central" fontSize={10}>{WALL_DECOR_META[type]?.icon}</text>
      </g>
    );
  }

  return (
    <g transform={`translate(${cx},${cy}) rotate(${rotDeg})`} opacity={opacity}
      onClick={onClick} onPointerDown={onPointerDown}
      style={{ cursor: armed ? armedCursor : onClick ? 'pointer' : 'default', pointerEvents: onClick ? 'all' : 'none', touchAction: armed ? 'none' : undefined }}>
      {armed && (
        <rect x={-sw/2-6} y={-9} width={sw+12} height={sw+17}
          fill="none" stroke="#d4903a" strokeWidth={1.5} strokeDasharray="3 2" rx={3}/>
      )}
      {isSelected && (
        <rect x={-sw/2-4} y={-7} width={sw+8} height={sw+13}
          fill="rgba(80,150,220,0.18)" stroke="#5096dc" strokeWidth={1.5} rx={2}/>
      )}
      {/* Orange jamb marks — rendered by WallLines; symbol draws on top */}
      {type === 'door' && <>
        <rect x={-sw/2} y={-5} width={sw} height={10} fill="#e2ddd7"/>
        <line x1={sw/2}  y1={0}  x2={sw/2}  y2={sw} stroke={dc} strokeWidth={1.5}/>
        <path d={`M${sw/2},${sw} A${sw},${sw} 0 0,0 ${-sw/2},0`} fill={dc+'40'} stroke={dc} strokeWidth={1}/>
      </>}
      {type === 'archway' && <>
        <rect x={-sw/2} y={-5} width={sw} height={10} fill="#e2ddd7"/>
        <path d={`M${-sw/2},${sw*0.45} A${sw/2},${sw/2} 0 0,1 ${sw/2},${sw*0.45}`}
          fill={dc+'30'} stroke={dc} strokeWidth={1.5}/>
      </>}
      {type === 'window' && <>
        <rect x={-sw/2} y={-5} width={sw} height={10} fill="#e2ddd7"/>
        <rect x={-sw/2} y={-4} width={sw} height={8} fill="none" stroke={dc} strokeWidth={1.5}/>
        <line x1={-sw/2} y1={0} x2={sw/2} y2={0} stroke={dc} strokeWidth={1.5}/>
      </>}
    </g>
  );
}

// Finds room.openings (doors/windows placed via a room's own opening tool —
// notably the 3D room view's "Add Opening" modal) that fall on a given
// room-boundary wall `w`, expressed with a `t` (0–1 fraction along `w`) and
// `roomId` so they can be rendered/selected/dragged alongside autoWallOpenings.
// Step-wall openings (absX/absY) are matched by their exact position; plain
// pos-fraction openings only match when the room's own bounding-box edge
// coincides with `w` (matches the logic in ThreeDRoom.jsx / houseSlice.js).
function getRoomOpeningsOnWall(rooms, w, dedupeAgainst) {
  const isH = w.y1 === w.y2;
  const result = [];
  for (const room of rooms) {
    for (const op of (room.openings || [])) {
      let worldPos;
      if (isH) {
        if (op.wall !== 'top' && op.wall !== 'bottom') continue;
        if (op.absX !== undefined && op.absY !== undefined) {
          const gy = room.y + op.absY / CELL;
          if (Math.abs(gy - w.y1) > 0.02) continue;
          worldPos = room.x + op.absX / CELL;
        } else {
          const wallSide = room.y === w.y1 ? 'top' : (room.y + room.h) === w.y1 ? 'bottom' : null;
          if (wallSide !== op.wall) continue;
          worldPos = room.x + op.pos * room.w;
        }
        if (worldPos <= w.x1 || worldPos >= w.x2) continue;
      } else {
        if (op.wall !== 'left' && op.wall !== 'right') continue;
        if (op.absX !== undefined && op.absY !== undefined) {
          const gx = room.x + op.absX / CELL;
          if (Math.abs(gx - w.x1) > 0.02) continue;
          worldPos = room.y + op.absY / CELL;
        } else {
          const wallSide = room.x === w.x1 ? 'left' : (room.x + room.w) === w.x1 ? 'right' : null;
          if (wallSide !== op.wall) continue;
          worldPos = room.y + op.pos * room.h;
        }
        if (worldPos <= w.y1 || worldPos >= w.y2) continue;
      }
      const t = isH ? (worldPos - w.x1) / (w.x2 - w.x1) : (worldPos - w.y1) / (w.y2 - w.y1);
      if (dedupeAgainst?.some(a => Math.abs(a.t - t) < 0.08)) continue;
      if (result.some(a => Math.abs(a.t - t) < 0.08)) continue;
      result.push({ ...op, t, roomId: room.id });
    }
  }
  return result;
}

// ─── WALL LINES (with opening gaps) ──────────────────────────────────────────
// Renders a pair of double-line wall strokes with gaps cut for openings.

// Returns true when two wall segments are collinear and share more than a single endpoint.
function wallSegOverlaps(a, b) {
  const [ax1, ax2] = a.x1 <= a.x2 ? [a.x1, a.x2] : [a.x2, a.x1];
  const [ay1, ay2] = a.y1 <= a.y2 ? [a.y1, a.y2] : [a.y2, a.y1];
  const [bx1, bx2] = b.x1 <= b.x2 ? [b.x1, b.x2] : [b.x2, b.x1];
  const [by1, by2] = b.y1 <= b.y2 ? [b.y1, b.y2] : [b.y2, b.y1];
  const aIsH = ay1 === ay2;
  const bIsH = by1 === by2;
  if (aIsH !== bIsH) return false;
  if (aIsH) return ay1 === by1 && ax1 < bx2 && bx1 < ax2;
  return ax1 === bx1 && ay1 < by2 && by1 < ay2;
}


// The OUTER edges of a room — the ones it actually draws a wall along.
//
// Taking all four sides of every rect was wrong for a COMPOUND room. A room
// merged from several rects has edges where two of its own rects meet, and
// those are interior: no wall is drawn there, and the merged room is one open
// space across them. Emitting them made the wall tool refuse any wall drawn
// inside such a room, which is a normal thing to want — splitting a space,
// adding a partition, tracing an imported plan.
//
// So the outline is traced from CELL OCCUPANCY instead: a unit edge is on the
// boundary only when the cell on one side belongs to the room and the cell on
// the other does not. That is the same test getCompoundPath uses to draw the
// room's outline, so the blocked edges are exactly the ones the user can see a
// wall on.
function getRoomBoundarySegments(rooms) {
  const segs = [];
  for (const room of rooms || []) {
    const rects = (room.rects && room.rects.length > 0)
      ? room.rects
      : [{ x: room.x, y: room.y, w: room.w, h: room.h }];

    const cells = new Set();
    for (const r of rects)
      for (let y = r.y; y < r.y + r.h; y++)
        for (let x = r.x; x < r.x + r.w; x++)
          cells.add(`${x},${y}`);

    for (const key of cells) {
      const [x, y] = key.split(',').map(Number);
      if (!cells.has(`${x},${y - 1}`)) segs.push({ x1: x,     y1: y,     x2: x + 1, y2: y });
      if (!cells.has(`${x},${y + 1}`)) segs.push({ x1: x,     y1: y + 1, x2: x + 1, y2: y + 1 });
      if (!cells.has(`${x - 1},${y}`)) segs.push({ x1: x,     y1: y,     x2: x,     y2: y + 1 });
      if (!cells.has(`${x + 1},${y}`)) segs.push({ x1: x + 1, y1: y,     x2: x + 1, y2: y + 1 });
    }
  }
  return segs;
}

// A candidate wall conflicts when it would be stacked on geometry that already
// draws a wall there: another freestanding wall, or a room's OUTER edge.
//
// Both leave two walls in one place, which is the mistake worth preventing.
// Drawing through a room's interior is not a conflict and is allowed.
function wallConflicts(seg, wallSegments, rooms) {
  if ((wallSegments || []).some(s => wallSegOverlaps(seg, s))) return true;
  return getRoomBoundarySegments(rooms).some(s => wallSegOverlaps(seg, s));
}

function WallLines({ x1, y1, x2, y2, openings, strokeOuter, strokeInner }) {
  const isH = y1 === y2;
  // totalLen is signed — negative when the wall was drawn in reverse direction.
  // All gap/segment arithmetic uses the same sign so that lerpP(t) = (x1+t, y1)
  // always produces correct pixel coordinates regardless of wall direction.
  const totalLen = isH ? (x2 - x1) : (y2 - y1);
  const sgn = totalLen >= 0 ? 1 : -1; // +1 normal, -1 reversed

  if (!openings || openings.length === 0) {
    return <>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={strokeOuter} strokeWidth={10} strokeLinecap="square" style={{ pointerEvents: 'none' }}/>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={strokeInner} strokeWidth={5}  strokeLinecap="square" style={{ pointerEvents: 'none' }}/>
    </>;
  }

  // Sort openings by their signed position along the wall
  const sorted = [...openings].sort((a, b) => sgn * (a.t - b.t));
  const gaps = sorted.map(op => {
    const hw = (op.width || 0.88) * PX_PER_M / 2;
    const center = op.t * totalLen; // signed distance from (x1,y1)
    // Clamp to [0, totalLen] in the direction of sgn
    const lo = sgn >= 0 ? 0 : totalLen;
    const hi = sgn >= 0 ? totalLen : 0;
    return { t0: Math.max(lo, center - hw * sgn), t1: Math.min(hi, center + hw * sgn) };
  });

  const lerpP = t => isH ? [x1 + t, y1] : [x1, y1 + t];

  const segs = [];
  let cur = 0;
  for (const g of gaps) {
    if (sgn * g.t0 > sgn * cur + 0.5) segs.push([cur, g.t0]);
    cur = g.t1;
  }
  if (sgn * cur < sgn * totalLen - 0.5) segs.push([cur, totalLen]);

  const J = 7; // jamb line half-length perpendicular to wall

  return <>
    {segs.map(([t0, t1], i) => {
      const [ax, ay] = lerpP(t0), [bx, by] = lerpP(t1);
      return <React.Fragment key={i}>
        <line x1={ax} y1={ay} x2={bx} y2={by} stroke={strokeOuter} strokeWidth={10} strokeLinecap="butt" style={{ pointerEvents: 'none' }}/>
        <line x1={ax} y1={ay} x2={bx} y2={by} stroke={strokeInner} strokeWidth={5}  strokeLinecap="butt" style={{ pointerEvents: 'none' }}/>
      </React.Fragment>;
    })}
    {gaps.map((g, i) => {
      const [ax, ay] = lerpP(g.t0);
      const [bx, by] = lerpP(g.t1);
      return <React.Fragment key={`j${i}`}>
        {isH ? <>
          <line x1={ax} y1={ay - J} x2={ax} y2={ay + J} stroke="#e07830" strokeWidth={3} strokeLinecap="round" style={{ pointerEvents: 'none' }}/>
          <line x1={bx} y1={by - J} x2={bx} y2={by + J} stroke="#e07830" strokeWidth={3} strokeLinecap="round" style={{ pointerEvents: 'none' }}/>
        </> : <>
          <line x1={ax - J} y1={ay} x2={ax + J} y2={ay} stroke="#e07830" strokeWidth={3} strokeLinecap="round" style={{ pointerEvents: 'none' }}/>
          <line x1={bx - J} y1={by} x2={bx + J} y2={by} stroke="#e07830" strokeWidth={3} strokeLinecap="round" style={{ pointerEvents: 'none' }}/>
        </>}
      </React.Fragment>;
    })}
  </>;
}

// ─── PLACED ROOM ──────────────────────────────────────────────────────────────

// ── MOVE MODE IS THE PARENT'S STATE, NOT THIS COMPONENT'S ────────────────────
// It used to be `useState` in here, which was right while the only control that
// turned it on was the floating bar this component renders. The button now also
// lives in the room's EDIT PANEL — a sibling rendered by FloorPlan — and two
// controls over one flag cannot each own a private copy of it.
//
// So `moveMode` arrives as a prop and `onMoveMode(next)` asks for a change.
// FloorPlan holds one `moveRoomId`, which also gives the invariant for free:
// one room at a time can be in move mode, because one id is one room.
function PlacedRoom({ room, isSelected, onSelect, onHover, openingTool, onPlaceOpening, selectedOpeningId, onSelectOpening, zoom = 1, onFreeOpeningClick, selectedFreeEdge, onSelectFreeEdge, onFreeOpeningDrag, freeOpeningDrag, moveMode = false, onMoveMode }) {
  const dispatch = useDispatch();
  const rt = ROOM_TYPES.find(r => r.type === room.type);
  const [dragOffset, setDragOffset] = useState(null);
  const setMoveMode = onMoveMode;
  const dragHappenedRef = useRef(false);
  const moveModeRef     = useRef(false);
  moveModeRef.current   = moveMode;
  const lastTapTimeRef  = useRef(0);
  const clickTimerRef   = useRef(null);
  const roomDivRef      = useRef(null);
  // Always-current refs for use inside useEffect closures
  const roomPosRef  = useRef({ x: room.x, y: room.y });
  roomPosRef.current = { x: room.x, y: room.y };
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const { t } = useT();

  const roomColor = rt?.color || '#d8d0c8';
  const roomW = room.w * CELL;
  const roomH = room.h * CELL;

  // The bar-placement calculation that used to live here went with the floating
  // Move bar it positioned — nothing on the room hangs outside it any more.
  const rects = (room.rects && room.rects.length > 0) ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
  const isCompound = rects.length > 1;

  // Exiting move mode on deselect is FloorPlan's job now, not an effect here.
  // Every unselected room runs this component, so `if (!isSelected) clear()`
  // would have each of them clearing the one shared `moveRoomId` — including
  // the room that is legitimately in move mode, cleared by its neighbours.

  // Cancel pending click timer on unmount
  useEffect(() => () => { if (clickTimerRef.current) clearTimeout(clickTimerRef.current); }, []);

  // ── Mouse drag — always active on desktop (no double-click required) ─────────
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragHappenedRef.current = false;
    const startX     = e.clientX;
    const startY     = e.clientY;
    const startRoomX = room.x;
    const startRoomY = room.y;
    let lastDx = 0, lastDy = 0;

    const onMouseMove = (mv) => {
      lastDx = mv.clientX - startX;
      lastDy = mv.clientY - startY;
      if (!dragHappenedRef.current && Math.abs(lastDx) < 4 && Math.abs(lastDy) < 4) return;
      if (!dragHappenedRef.current && clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      dragHappenedRef.current = true;
      setDragOffset({ dx: lastDx, dy: lastDy });
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
      setDragOffset(null);
      if (dragHappenedRef.current) {
        const newX = Math.max(0, startRoomX + Math.round(lastDx / (CELL * zoom)));
        const newY = Math.max(0, startRoomY + Math.round(lastDy / (CELL * zoom)));
        dispatch(moveRoom({ id: room.id, x: newX, y: newY }));
        dispatch(commitBuilderHistory());
      }
      // Don't reset dragHappenedRef here — handleClick checks it to skip after a drag
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
  };

  // Single click → open settings after delay; double click (second click within delay) → move mode
  const handleClick = (e) => {
    e.stopPropagation();
    if (dragHappenedRef.current) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      setMoveMode(true);
      if (navigator.vibrate) navigator.vibrate(40);
      return;
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      onSelect();
    }, 260);
  };

  // ── Touch: double-tap → move mode; drag while move mode is on ─────────────────
  useEffect(() => {
    const el = roomDivRef.current;
    if (!el) return;

    let startX = 0, startY = 0, startRoomX = 0, startRoomY = 0;
    let currentDx = 0, currentDy = 0;
    let isDragging = false;

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      const now  = Date.now();
      const gap  = now - lastTapTimeRef.current;

      if (gap < 300 && gap > 0) {
        // Double-tap → enter move mode; cancel any pending single-tap open-settings timer
        e.preventDefault(); // stops browser zoom on double-tap
        lastTapTimeRef.current = 0;
        if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
        setMoveMode(true);
        if (navigator.vibrate) navigator.vibrate(40);
        return;
      }
      lastTapTimeRef.current = now;

      if (!moveModeRef.current) return; // not in move mode — normal tap, let click fire

      const touch = e.touches[0];
      startX    = touch.clientX;
      startY    = touch.clientY;
      startRoomX = roomPosRef.current.x;
      startRoomY = roomPosRef.current.y;
      currentDx = 0;
      currentDy = 0;
      isDragging = false;
      dragHappenedRef.current = false;
    };

    const onTouchMove = (e) => {
      if (!moveModeRef.current || !e.touches[0]) return;
      currentDx = e.touches[0].clientX - startX;
      currentDy = e.touches[0].clientY - startY;
      if (Math.abs(currentDx) < 4 && Math.abs(currentDy) < 4) return;
      e.preventDefault();
      isDragging = true;
      dragHappenedRef.current = true;
      setDragOffset({ dx: currentDx, dy: currentDy });
    };

    const onTouchEnd = () => {
      if (!moveModeRef.current) return;
      setDragOffset(null);
      if (isDragging) {
        const z    = zoomRef.current;
        const newX = Math.max(0, startRoomX + Math.round(currentDx / (CELL * z)));
        const newY = Math.max(0, startRoomY + Math.round(currentDy / (CELL * z)));
        dispatchRef.current(moveRoom({ id: room.id, x: newX, y: newY }));
        dispatchRef.current(commitBuilderHistory());
      }
      isDragging = false;
      dragHappenedRef.current = false;
    };

    el.addEventListener('touchstart',  onTouchStart, { passive: false });
    el.addEventListener('touchmove',   onTouchMove,  { passive: false });
    el.addEventListener('touchend',    onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart',  onTouchStart);
      el.removeEventListener('touchmove',   onTouchMove);
      el.removeEventListener('touchend',    onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [room.id]);
  // ────────────────────────────────────────────────────────────────────────────

  const handleCornerResizeMouseDown = (corner, e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = room.w;
    const startH = room.h;
    const startRX = room.x;
    const startRY = room.y;

    const onMouseMove = (mv) => {
      const dCols = Math.round((mv.clientX - startX) / (CELL * zoom));
      const dRows = Math.round((mv.clientY - startY) / (CELL * zoom));
      let x = startRX, y = startRY, w = startW, h = startH;
      if (corner === 'se') { w = startW + dCols; h = startH + dRows; }
      else if (corner === 'sw') { x = startRX + dCols; w = startW - dCols; h = startH + dRows; }
      else if (corner === 'ne') { y = startRY + dRows; h = startH - dRows; w = startW + dCols; }
      else if (corner === 'nw') { x = startRX + dCols; w = startW - dCols; y = startRY + dRows; h = startH - dRows; }
      dispatch(resizeRoomCorner({ id: room.id, x, y, w, h }));
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      dispatch(commitBuilderHistory());
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const [isHovered, setIsHovered] = useState(false);
  const strokeColor = isSelected ? '#b8935a' : isHovered ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.10)';
  const strokeW = isSelected ? 2.5 : 1.5;
  // Warm gold wash laid over the room's own colour while it's selected, so the
  // selected room is obvious at a glance (matches the gold selection outline).
  const SELECT_TINT = 'rgba(212, 160, 90, 0.42)';
  // A free-form room carries a smooth outline (`pts`, angled/curved) alongside
  // its rects. Prefer that for drawing so the room LOOKS like what was drawn,
  // while the rects keep every room tool working.
  const freePath = (room.pts && room.pts.length >= 3)
    ? (() => {
        const P = room.pts;
        const px = p => (p.x - room.x) * CELL, py = p => (p.y - room.y) * CELL;
        let d = `M ${px(P[0])} ${py(P[0])}`;
        for (let i = 0; i < P.length; i++) {
          const a = P[i], b = P[(i + 1) % P.length];
          if (a.bulge) {
            const c = edgeControlPoint(a.x, a.y, b.x, b.y, a.bulge);
            d += ` Q ${(c.cx - room.x) * CELL} ${(c.cy - room.y) * CELL} ${px(b)} ${py(b)}`;
          } else d += ` L ${px(b)} ${py(b)}`;
        }
        // A merged free-form room also carries the straight rooms it absorbed —
        // append them as extra subpaths so the fill is their union (nonzero rule).
        if (room.mergeRects?.length) {
          d += ' ' + getCompoundOutlinePath(room.mergeRects, CELL, room.x, room.y);
        }
        return d + ' Z';
      })()
    : null;
  const compoundPath = freePath || (isCompound ? getCompoundOutlinePath(rects, CELL, room.x, room.y) : null);
  // Which side of an outline edge is the room's INSIDE — from the loop's winding,
  // so wall labels land inside the room whichever way it was drawn.
  const ptsInwardSign = (room.pts && room.pts.length >= 3 && loopSignedArea(room.pts) < 0) ? -1 : 1;
  const compoundFilter = isCompound ? (dragOffset ? 'drop-shadow(0 8px 20px rgba(0,0,0,0.28))' : isSelected ? 'drop-shadow(0 0 6px rgba(184,147,90,0.55))' : isHovered ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.18))' : undefined) : undefined;

  // Weighted centroid of compound shape (in pixels relative to bounding-box origin)
  const centroid = isCompound ? (() => {
    let totalArea = 0, cx = 0, cy = 0;
    for (const r of rects) {
      const area = r.w * r.h;
      cx += (r.x + r.w / 2 - room.x) * area;
      cy += (r.y + r.h / 2 - room.y) * area;
      totalArea += area;
    }
    return totalArea > 0 ? { x: (cx / totalArea) * CELL, y: (cy / totalArea) * CELL } : { x: roomW / 2, y: roomH / 2 };
  })() : null;

  // For compound rooms: find which corner of the topmost row is the rightmost actual cell,
  // so the remove button is always positioned inside the real shape.
  const compoundRemovePos = isCompound ? (() => {
    let maxRight = 0;
    for (const r of rects) {
      if (r.y === room.y) {
        const re = (r.x + r.w - room.x) * CELL;
        if (re > maxRight) maxRight = re;
      }
    }
    return { top: 4, right: roomW - maxRight + 4 };
  })() : null;

  return (
    <div
      ref={roomDivRef}
      className={`placed-room${!isCompound && isSelected ? ' placed-room--selected' : ''}${isCompound && isSelected ? ' placed-room--compound-selected' : ''}${dragOffset ? ' placed-room--dragging' : ''}${moveMode ? ' placed-room--move-mode' : ''}`}
      style={{
        left: room.x * CELL + (isCompound ? 0 : 1),
        top: room.y * CELL + (isCompound ? 0 : 1),
        width: roomW - (isCompound ? 0 : 2),
        height: roomH - (isCompound ? 0 : 2),
        background: (isCompound || freePath)
          ? 'transparent'
          : (isSelected ? `linear-gradient(${SELECT_TINT}, ${SELECT_TINT}), ${roomColor}` : roomColor),
        border: (isCompound || freePath) ? 'none' : undefined,
        borderRadius: (isCompound || freePath) ? 0 : undefined,
        boxShadow: (isCompound || freePath) ? 'none' : undefined,
        zIndex: dragOffset ? 50 : moveMode ? 25 : isSelected ? 20 : undefined,
        transform: dragOffset ? `translate(${dragOffset.dx}px, ${dragOffset.dy}px)` : undefined,
        // Free-form rooms draw their own wall strokes, which extend beyond the
        // outline — clipping to the path would shave them off.
        clipPath: (!freePath && isCompound && compoundPath) ? `path('${compoundPath}')` : undefined,
        opacity: isCompound && dragOffset ? 0.88 : undefined,
        cursor: dragOffset ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onMouseEnter={() => { setIsHovered(true); onHover && onHover(room.id); }}
      onMouseLeave={() => { setIsHovered(false); onHover && onHover(null); }}
    >
      <svg style={{ position: 'absolute', inset: 0, overflow: 'visible', width: roomW, height: roomH, pointerEvents: 'none' }}>
        {freePath ? (
          /* Free-form room: floor fill + real wall strokes (dark outer + light
             inner) following the true angled/curved outline. Auto-walls are
             skipped for these rooms, so these ARE the room's walls. */
          /* Fill first, then the wall stroked ON TOP and CENTRED on the outline —
             dark 10 + light 5, the exact widths WallLines uses for auto-walls and
             hand-drawn walls. Every wall in the plan therefore straddles its own
             line by the same 5px, so a straight wall continuing out of the room
             meets its wall flush instead of stepping half a wall's width.
             (This used to stroke at double width UNDER the fill, which left only
             the outer half showing — a wall sitting entirely outside the outline.
             That hid the interior seam of a merged free-form room, but merging a
             drawn room is blocked now, so nothing new can carry such a seam.)
             Miter joins keep corners square like the rectangular walls. */
          (() => {
            const dark = isSelected ? '#c04000' : '#26211c';
            const light = isSelected ? '#f0a060' : '#e2ddd7';
            const J = { strokeLinejoin: 'miter', strokeMiterlimit: 8 };
            return (
              <>
                <path d={freePath} fill={roomColor} stroke="none" />
                {isSelected && <path d={freePath} fill={SELECT_TINT} stroke="none" style={{ pointerEvents: 'none' }} />}
                <path d={freePath} fill="none" stroke={dark}  strokeWidth={10} {...J} />
                <path d={freePath} fill="none" stroke={light} strokeWidth={5}  {...J} />
              </>
            );
          })()
        ) : (isCompound && compoundPath && (
          <>
            <path d={compoundPath} fill={roomColor} stroke={strokeColor} strokeWidth={strokeW} strokeLinejoin="round"
              style={compoundFilter ? { filter: compoundFilter } : undefined} />
            {isSelected && <path d={compoundPath} fill={SELECT_TINT} stroke="none" style={{ pointerEvents: 'none' }} />}
          </>
        ))}
        {/* Free-form outline: each edge is its own clickable wall — click to select
            it (then add/edit openings), or click with an opening tool armed to
            drop that opening straight onto the wall. */}
        {freePath && room.pts && room.pts.map((a, i) => {
          const b = room.pts[(i + 1) % room.pts.length];
          const px = p => (p.x - room.x) * CELL, py = p => (p.y - room.y) * CELL;
          let d = `M ${px(a)} ${py(a)}`;
          if (a.bulge) {
            const c = edgeControlPoint(a.x, a.y, b.x, b.y, a.bulge);
            d += ` Q ${(c.cx - room.x) * CELL} ${(c.cy - room.y) * CELL} ${px(b)} ${py(b)}`;
          } else d += ` L ${px(b)} ${py(b)}`;
          const isSelEdge = selectedFreeEdge != null && selectedFreeEdge === i;
          return (
            <g key={`fe-${i}`}>
              {/* Selected-wall highlight — the SAME orange/weight the straight
                  walls use, so a selected wall looks the same whichever kind it is. */}
              {isSelEdge && (
                <path d={d} fill="none" stroke="#e05800" strokeWidth={14} strokeLinecap="round"
                  opacity={0.35} style={{ pointerEvents: 'none' }} />
              )}
              <path d={d} fill="none" stroke="transparent" strokeWidth={18}
                style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
                /* Stop the press reaching the room div underneath — otherwise it
                   selects (and starts dragging) the ROOM instead of the wall. */
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onFreeOpeningClick && onFreeOpeningClick(e)) return;
                  onSelectFreeEdge && onSelectFreeEdge(i);
                }} />
              {/* Length label. A rectangle room gets these from its auto-walls,
                  which a free-form room has none of (its walls follow the smooth
                  outline, not cell edges) — so each edge states its own length
                  here. Curved edges report ARC length, what you'd actually build. */}
              {(() => {
                const lenM = edgeLength(a, b);
                if (lenM < 0.5) return null;   // too short to letter without clutter
                const q = edgePointAt(a, b, 0.5);
                // Sit just INSIDE the room: which side that is follows the
                // outline's winding, so it holds for either drawing direction.
                const s = ptsInwardSign;
                const lx = (q.x - room.x) * CELL - Math.sin(q.angle) * s * 16;
                const ly = (q.y - room.y) * CELL + Math.cos(q.angle) * s * 16;
                let deg = q.angle * 180 / Math.PI;      // keep the text upright
                if (deg > 90) deg -= 180; else if (deg < -90) deg += 180;
                return (
                  <text
                    x={lx} y={ly}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={11} fontFamily="system-ui, sans-serif" fontWeight="600"
                    fill={isSelEdge ? '#e05800' : '#26211c'}
                    stroke="rgba(255,255,255,0.85)" strokeWidth={3} paintOrder="stroke"
                    transform={`rotate(${deg}, ${lx}, ${ly})`}
                    style={{ pointerEvents: 'none' }}
                  >{lenM.toFixed(2)}m</text>
                );
              })()}
            </g>
          );
        })}
        {(room.openings || []).map(op => {
          if (freePath && op.edge != null && room.pts) {
            // While dragging, follow the live preview instead of the stored spot.
            const drag = freeOpeningDrag?.openingId === op.id ? freeOpeningDrag : null;
            const eIdx = drag ? drag.edge : op.edge;
            const eT   = drag ? drag.t : op.t;
            const a = room.pts[eIdx], b = room.pts[(eIdx + 1) % room.pts.length];
            if (!a || !b) return null;
            const q = edgePointAt(a, b, eT);
            // Orient the symbol along the wall with its "inside" (+Y local, where
            // the door swing / window reveal is drawn) pointing INTO the room, by
            // testing which side the outline's centroid falls on.
            let deg = q.angle * 180 / Math.PI;
            const cxAll = room.pts.reduce((s, p) => s + p.x, 0) / room.pts.length;
            const cyAll = room.pts.reduce((s, p) => s + p.y, 0) / room.pts.length;
            const nx = -Math.sin(q.angle), ny = Math.cos(q.angle); // +Y local in world
            if ((cxAll - q.x) * nx + (cyAll - q.y) * ny < 0) deg += 180;
            return (
              <g key={op.id}
                 style={{ cursor: 'grab' }}
                 /* The room starts its own drag on mousedown, which is a SEPARATE
                    event from pointerdown — both must be stopped or dragging an
                    opening drags the whole room with it. */
                 onMouseDown={(e) => e.stopPropagation()}
                 onPointerDown={(e) => { if (onFreeOpeningDrag) { e.stopPropagation(); onFreeOpeningDrag(e, op); } }}>
                <OpeningSymbol opening={op} roomW={roomW - 2} roomH={roomH - 2}
                  roomColor={roomColor}
                  isSelected={selectedOpeningId === op.id}
                  onSelect={(id) => onSelectOpening(id)}
                  transform={`translate(${(q.x - room.x) * CELL},${(q.y - room.y) * CELL}) rotate(${deg})`}
                />
              </g>
            );
          }
          return (
            <OpeningSymbol key={op.id} opening={op} roomW={roomW - 2} roomH={roomH - 2}
              roomColor={roomColor}
              isSelected={selectedOpeningId === op.id}
              onSelect={(id) => onSelectOpening(id)}
            />
          );
        })}
      </svg>

      {isCompound && centroid ? (
        <div style={{ position: 'absolute', left: centroid.x, top: centroid.y, transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, pointerEvents: 'none' }}>
          <span className="placed-room-icon"><RoomIcon type={room.type} size={22} /></span>
          <span className="placed-room-label">{room.label}</span>
        </div>
      ) : (
        <>
          <span className="placed-room-icon"><RoomIcon type={room.type} size={22} /></span>
          <span className="placed-room-label">{room.label}</span>
        </>
      )}

      {moveMode && (
        <div className="placed-room-move-badge">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M8 1v14M1 8h14M8 1L5 4M8 1l3 3M8 15l-3-3M8 15l3-3M1 8l3-3M1 8l3 3M15 8l-3-3M15 8l-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
      <div className="placed-room-hover-card"
        style={isCompound && centroid ? { bottom: 'auto', top: centroid.y + 26, left: centroid.x, transform: 'translateX(-50%)' } : {}}>
        <span className="placed-room-hover-size">{room.w} × {room.h} m</span>
        {(room.openings || []).length > 0 && (
          <span className="placed-room-hover-openings">{(room.openings || []).length} {t('openings_label').toLowerCase()}</span>
        )}
      </div>
      <button className="placed-room-remove"
        style={compoundRemovePos ? { top: compoundRemovePos.top, right: compoundRemovePos.right } : {}}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); dispatch(removeRoom(room.id)); }}
        title="Remove room"
      >×</button>

      {/* The floating Move bar that used to sit on the room is gone. Starting
          a move is the button in the room edit panel now, and a second control
          over the grid was the duplicate this one had become. Move mode is left
          by tapping off the room, which deselects it and clears the mode. */}
      {!isCompound && <>
        <div className="resize-handle resize-handle--nw" onMouseDown={(e) => handleCornerResizeMouseDown('nw', e)} />
        <div className="resize-handle resize-handle--ne" onMouseDown={(e) => handleCornerResizeMouseDown('ne', e)} />
        <div className="resize-handle resize-handle--sw" onMouseDown={(e) => handleCornerResizeMouseDown('sw', e)} />
        <div className="resize-handle resize-handle--se" onMouseDown={(e) => handleCornerResizeMouseDown('se', e)} />
      </>}

      {isSelected && openingTool && !isCompound && ['top', 'right', 'bottom', 'left'].map(wall => (
        <WallStrip key={wall} wall={wall} roomW={roomW} roomH={roomH}
          onPlace={(pos) => onPlaceOpening(wall, pos)}
        />
      ))}
      {isSelected && openingTool && isCompound && (() => {
        const { hSegs, vSegs } = getAllExteriorSegments(rects, room);
        const strips = [];

        for (const seg of hSegs) {
          const pxL  = seg.startCol * CELL;
          const pxW2 = (seg.endCol - seg.startCol) * CELL;
          // top face → strip at top edge of that row; bottom face → strip at bottom edge
          const pxT  = seg.wall === 'top' ? seg.row * CELL : (seg.row + 1) * CELL - 12;
          const wallY = seg.wall === 'top' ? seg.row * CELL : (seg.row + 1) * CELL;
          // Is this a bounding-box edge? (top row=0, bottom row=room.h-1)
          const isBB = (seg.wall === 'top' && seg.row === 0) ||
                       (seg.wall === 'bottom' && seg.row === room.h - 1);
          strips.push(<SegmentWallStrip key={`h_${seg.wall}_${seg.row}_${seg.startCol}`}
            isH={true} pxLeft={pxL} pxTop={pxT} pxW={pxW2} pxH={12}
            onPlace={f => {
              if (isBB) {
                // legacy: pos as fraction of bounding-box width
                onPlaceOpening(seg.wall, (seg.startCol + f * (seg.endCol - seg.startCol)) / room.w);
              } else {
                // step wall: absolute SVG coords
                onPlaceOpening(seg.wall, f, pxL + f * pxW2, wallY);
              }
            }} />);
        }

        for (const seg of vSegs) {
          const pxT2 = seg.startRow * CELL;
          const pxH2 = (seg.endRow - seg.startRow) * CELL;
          const pxL2 = seg.wall === 'left' ? seg.col * CELL : (seg.col + 1) * CELL - 12;
          const wallX = seg.wall === 'left' ? seg.col * CELL : (seg.col + 1) * CELL;
          const isBB = (seg.wall === 'left' && seg.col === 0) ||
                       (seg.wall === 'right' && seg.col === room.w - 1);
          strips.push(<SegmentWallStrip key={`v_${seg.wall}_${seg.col}_${seg.startRow}`}
            isH={false} pxLeft={pxL2} pxTop={pxT2} pxW={12} pxH={pxH2}
            onPlace={f => {
              if (isBB) {
                onPlaceOpening(seg.wall, (seg.startRow + f * (seg.endRow - seg.startRow)) / room.h);
              } else {
                onPlaceOpening(seg.wall, f, wallX, pxT2 + f * pxH2);
              }
            }} />);
        }

        return strips;
      })()}
    </div>
  );
}

function GhostPreview({ type, hoverCell }) {
  const rt = ROOM_TYPES.find(r => r.type === type);
  if (!rt || !hoverCell) return null;
  return (
    <div className="ghost-preview" style={{
      left: hoverCell.col * CELL, top: hoverCell.row * CELL,
      width: rt.w * CELL, height: rt.h * CELL,
    }} />
  );
}

// ─── ENCLOSED AREA DETECTION ─────────────────────────────────────────────────
// Flood-fills from outside the grid; unvisited non-room cells are enclosed.

function detectEnclosedCells(rooms, wallSegs, GRID) {
  const G = GRID;
  // hBlocked[r][c] = wall between cell (r,c) and (r+1,c)
  const hBlocked = Array.from({ length: G }, () => new Uint8Array(G));
  // vBlocked[r][c] = wall between cell (r,c) and (r,c+1)
  const vBlocked = Array.from({ length: G }, () => new Uint8Array(G));

  // Horizontal wall at grid-row gy from grid-col gx1 to gx2
  const markH = (gx1, gy, gx2) => {
    const xMin = Math.min(gx1, gx2), xMax = Math.max(gx1, gx2);
    for (let c = xMin; c < xMax; c++)
      if (gy > 0 && gy <= G) hBlocked[gy - 1][c] = 1;
  };
  // Vertical wall at grid-col gx from grid-row gy1 to gy2
  const markV = (gx, gy1, gy2) => {
    const yMin = Math.min(gy1, gy2), yMax = Math.max(gy1, gy2);
    for (let r = yMin; r < yMax; r++)
      if (gx > 0 && gx <= G) vBlocked[r][gx - 1] = 1;
  };

  // Mark auto-wall edges derived from room perimeters
  for (const room of rooms) {
    const cells = new Set();
    const addCells = (rx, ry, rw, rh) => {
      for (let cy = ry; cy < ry + rh; cy++)
        for (let cx = rx; cx < rx + rw; cx++)
          cells.add(`${cx},${cy}`);
    };
    if (room.rects && room.rects.length > 0) room.rects.forEach(r => addCells(r.x, r.y, r.w, r.h));
    else addCells(room.x, room.y, room.w, room.h);
    for (const key of cells) {
      const [cx, cy] = key.split(',').map(Number);
      if (!cells.has(`${cx},${cy - 1}`)) markH(cx, cy, cx + 1);
      if (!cells.has(`${cx + 1},${cy}`)) markV(cx + 1, cy, cy + 1);
      if (!cells.has(`${cx},${cy + 1}`)) markH(cx, cy + 1, cx + 1);
      if (!cells.has(`${cx - 1},${cy}`)) markV(cx, cy, cy + 1);
    }
  }

  // Mark manual wall segments
  for (const w of wallSegs) {
    const x1 = Math.min(w.x1, w.x2), x2 = Math.max(w.x1, w.x2);
    const y1 = Math.min(w.y1, w.y2), y2 = Math.max(w.y1, w.y2);
    if (y1 === y2) markH(x1, y1, x2); else markV(x1, y1, y2);
  }

  // BFS from all border cells (they're reachable from "outside the grid")
  const visited = Array.from({ length: G }, () => new Uint8Array(G));
  const queue = [];
  const seed = (r, c) => {
    if (r >= 0 && r < G && c >= 0 && c < G && !visited[r][c]) {
      visited[r][c] = 1; queue.push([r, c]);
    }
  };
  for (let c = 0; c < G; c++) { seed(0, c); seed(G - 1, c); }
  for (let r = 1; r < G - 1; r++) { seed(r, 0); seed(r, G - 1); }
  let qi = 0;
  while (qi < queue.length) {
    const [r, c] = queue[qi++];
    if (r > 0     && !hBlocked[r-1][c]   && !visited[r-1][c])   seed(r-1, c);
    if (r < G - 1 && !hBlocked[r][c]     && !visited[r+1][c])   seed(r+1, c);
    if (c > 0     && !vBlocked[r][c-1]   && !visited[r][c-1])   seed(r, c-1);
    if (c < G - 1 && !vBlocked[r][c]     && !visited[r][c+1])   seed(r, c+1);
  }

  // Exclude cells already belonging to existing rooms
  const roomCells = new Set();
  for (const room of rooms) {
    const addCells = (rx, ry, rw, rh) => {
      for (let cy = ry; cy < ry + rh; cy++)
        for (let cx = rx; cx < rx + rw; cx++)
          roomCells.add(`${cy},${cx}`);
    };
    if (room.rects && room.rects.length > 0) room.rects.forEach(r => addCells(r.x, r.y, r.w, r.h));
    else addCells(room.x, room.y, room.w, room.h);
  }

  const enclosed = [];
  for (let r = 0; r < G; r++)
    for (let c = 0; c < G; c++)
      if (!visited[r][c] && !roomCells.has(`${r},${c}`)) enclosed.push([r, c]);
  return enclosed;
}

function groupConnectedCells(cells) {
  const cellSet = new Set(cells.map(([r, c]) => `${r},${c}`));
  const seen = new Set();
  const components = [];
  for (const [sr, sc] of cells) {
    const startKey = `${sr},${sc}`;
    if (seen.has(startKey)) continue;
    const comp = [], queue = [[sr, sc]];
    seen.add(startKey);
    while (queue.length) {
      const [r, c] = queue.shift();
      comp.push([r, c]);
      for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]]) {
        const nk = `${r+dr},${c+dc}`;
        if (cellSet.has(nk) && !seen.has(nk)) { seen.add(nk); queue.push([r+dr, c+dc]); }
      }
    }
    components.push(comp);
  }
  return components;
}

// ─── BUILDER (main) ───────────────────────────────────────────────────────────

export default function Builder() {
  const dispatch   = useDispatch();
  const { projectId } = useParams();
  const { user }      = useAuth();
  const isGuest        = useGuestMode();
  const { markSaved, isDirty, changeToken, confirmIfDirty } = useUnsavedChanges();
  const houseState = useSelector(s => s.house);
  const { dragRoomType, hoverCell, rooms, dragFurnitureType, furniture, nextId, nextFurnitureId, nextOpeningId, wallHeight, builderHistory, builderHistoryIndex, wallSegments, autoWallOpenings, nextWallOpeningId, gridSize } = houseState;
  const freeWalls = houseState.freeWalls || [];
  const freeRooms = houseState.freeRooms || [];
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  // ── LEAVING THE EDITOR ─────────────────────────────────────────────────────
  // History first, a fixed destination only as a fallback — the same rule the
  // site's BackBar follows, and for the same reason: someone who opened this
  // project from their workspace expects Back to return there, scrolled and
  // filtered as they left it, which a hard link cannot restore.
  //
  // `idx` is React Router's own position in its history stack; 0 means this
  // entry is the first one and there is nothing of ours behind it — a shared
  // link, or a new tab — where `navigate(-1)` would leave the app entirely.
  //
  // Wrapped in `confirmIfDirty`, so unsaved work asks before it is abandoned.
  // A guest's provider passes that straight through (see App.jsx), and a guest
  // has no workspace, so they fall back to the landing page instead.
  const goBackOut = () => confirmIfDirty(() => {
    const canGoBack = location.key !== 'default' && (window.history.state?.idx ?? 0) > 0;
    if (canGoBack) navigate(-1);
    else navigate(isGuest ? '/' : '/workspace', { replace: true });
  });

  // Live grid cell-count for this project ('S'/'M'/'L' → 50/100/150, see GRID_SIZES) —
  // shadows the module-level DEFAULT_GRID fallback for every usage in this component.
  const GRID = gridSize || DEFAULT_GRID;


  const [sidebarTab, setSidebarTab] = useState('furniture');
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  // WHICH ROOM IS BEING DRAGGED BY TOUCH, or null. Lifted out of PlacedRoom
  // because the button that starts a move now sits in the room's edit panel,
  // which this component renders — see the note above PlacedRoom. An id rather
  // than a boolean, so "only one room at a time" needs no enforcing.
  const [moveRoomId, setMoveRoomId] = useState(null);
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  const [openingTool, setOpeningTool] = useState(null);
  const [openingSize, setOpeningSize] = useState({
    door:    { width: 0.88, height: 2.06 },
    window:  { width: 0.98, height: 0.88, sill: 0.9 },
    archway: { width: 1.2,  height: 2.4  },
  });
  const [selectedOpeningId, setSelectedOpeningId] = useState(null);
  const [selectedFurnitureId, setSelectedFurnitureId] = useState(null);
  const [furnitureHover, setFurnitureHover] = useState(null);
  // Live bbox of the furniture piece currently being dragged ({ id, bbox }),
  // used to draw its dimension lines in real time; null when nothing is moving.
  const [furnitureDrag, setFurnitureDrag] = useState(null);
  const reportFurnitureDrag = useCallback((id, bbox) => {
    setFurnitureDrag(prev => bbox ? { id, bbox } : (prev && prev.id === id ? null : prev));
  }, []);
  // Starts zoomed in so the grid squares read larger by default (each square
  // still represents the same real 1m — this only affects how many screen
  // pixels it's drawn with). The zoom buttons' "reset" still snaps to true
  // 100% (see setZoom(1) below).
  // Default zoom by screen size. 180% suits a desktop canvas and is far too
  // close on a phone — the plan fills the screen before you have drawn
  // anything. A lazy initialiser, so this is the STARTING zoom only: once the
  // user pinches or taps the controls their choice stands, and resizing the
  // window never yanks the canvas back.
  const [zoom, setZoom] = useState(() => {
    const w = typeof window === 'undefined' ? 1920 : window.innerWidth;
    if (w < 540) return 0.8;
    if (w < 768) return 1.0;
    if (w < 1024) return 1.2;
    return 1.8;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const zoomRef = useRef(1);
  const gridRef = useRef(null);
  const mainRef = useRef(null);

  // ── Save / Load ──
  const fileRef       = useRef(null);
  const flashTimerRef = useRef(null);
  const [saveMsg,    setSaveMsg]    = useState(null);
  const [webSaving,  setWebSaving]  = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteFurniture, setConfirmDeleteFurniture] = useState(false);

  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  // Center the scrollable canvas on the room layout (or the grid's middle,
  // if this project is still empty) the moment the Builder mounts —
  // otherwise `.builder-main`'s native overflow:auto scroll defaults to the
  // grid's top-left corner, which usually isn't where the rooms are.
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    let cx, cy;
    if (rooms.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      rooms.forEach(r => {
        minX = Math.min(minX, r.x); minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.w); maxY = Math.max(maxY, r.y + r.h);
      });
      cx = (minX + maxX) / 2; cy = (minY + maxY) / 2;
    } else {
      cx = GRID / 2; cy = GRID / 2;
    }
    el.scrollLeft = Math.max(0, cx * CELL * zoomRef.current - el.clientWidth / 2);
    el.scrollTop  = Math.max(0, cy * CELL * zoomRef.current - el.clientHeight / 2);
  }, []);

  function flashSave(text, ok = true) {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setSaveMsg({ text, ok });
    flashTimerRef.current = setTimeout(() => setSaveMsg(null), 2200);
  }

  function handleSave() {
    const s = houseState;
    const payload = {
      version:           FILE_VERSION,
      savedAt:           new Date().toISOString(),
      phase:             s.phase,
      rooms:             s.rooms,
      designs:           s.designs,
      furniture:         s.furniture,
      lighting:          s.lighting          || [],
      wallSegments:      s.wallSegments      || [],
      freeWalls:         s.freeWalls         || [],
      nextFreeWallId:    s.nextFreeWallId    || 1,
      freeRooms:         s.freeRooms         || [],
      nextFreeRoomId:    s.nextFreeRoomId    || 1,
      autoWallOpenings:  s.autoWallOpenings  || {},
      wallHeight:        s.wallHeight        ?? 2.8,
      nextId:            s.nextId,
      nextFurnitureId:   s.nextFurnitureId,
      nextLightingId:    s.nextLightingId    || 1,
      nextOpeningId:     s.nextOpeningId,
      nextWallId:        s.nextWallId        || 1,
      nextWallOpeningId: s.nextWallOpeningId || 1,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `house-design-${new Date().toISOString().slice(0,10)}.housedesign`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    flashSave(t('design_saved'));
  }

  async function handleWebSave() {
    if (!projectId) { flashSave('Open a project from Workspace first', false); return; }
    if (!user)      { flashSave('Not signed in', false); return; }
    setWebSaving(true);
    const s = houseState;
    const sceneData = JSON.stringify({
      version:           FILE_VERSION,
      savedAt:           new Date().toISOString(),
      phase:             s.phase,
      rooms:             s.rooms,
      designs:           s.designs,
      furniture:         s.furniture,
      lighting:          s.lighting          || [],
      wallSegments:      s.wallSegments      || [],
      freeWalls:         s.freeWalls         || [],
      nextFreeWallId:    s.nextFreeWallId    || 1,
      freeRooms:         s.freeRooms         || [],
      nextFreeRoomId:    s.nextFreeRoomId    || 1,
      autoWallOpenings:  s.autoWallOpenings  || {},
      wallHeight:        s.wallHeight        ?? 2.8,
      nextId:            s.nextId,
      nextFurnitureId:   s.nextFurnitureId,
      nextLightingId:    s.nextLightingId    || 1,
      nextOpeningId:     s.nextOpeningId,
      nextWallId:        s.nextWallId        || 1,
      nextWallOpeningId: s.nextWallOpeningId || 1,
    });
    try {
      await saveProjectToWeb(projectId, sceneData);
      markSaved();
      flashSave('Saved to cloud ✓');
    } catch (err) {
      console.error('Web save failed:', err);
      flashSave('Cloud save failed', false);
    } finally {
      setWebSaving(false);
    }
  }

  // Autosave to the cloud shortly after the user stops editing.
  useAutoSave({
    isDirty, changeToken, save: handleWebSave,
    enabled: !!projectId && !!user,
  });

  function handleLoad() { fileRef.current?.click(); }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.version !== FILE_VERSION) { flashSave(t('unsupported_version'), false); return; }
        dispatch(loadDesign(data));
        flashSave(t('design_loaded'));
      } catch { flashSave(t('invalid_file'), false); }
    };
    reader.readAsText(file);
  }

  const [drawMode, setDrawMode] = useState(false);
  const [drawing, setDrawing] = useState(null); // { col, row, w, h } in grid cells
  const drawStartRef = useRef(null);
  // Room ids placed while "Add room" has been continuously active — lets the
  // floating Prev/Cancel/Done bar undo one, or discard all, of this session's
  // rectangles (each rectangle commits to the store immediately on mouse-up,
  // unlike the wall chain which stays uncommitted until Done).
  const [drawSessionRoomIds, setDrawSessionRoomIds] = useState([]);
  const store = useStore();
  // Local clipboard for the copy/paste feature — { kind: 'furniture'|'opening', ... }
  const clipboardRef = useRef(null);

  const [wallHeightOpen, setWallHeightOpen] = useState(false);
  const [wallHeightEditing, setWallHeightEditing] = useState(false);
  const [wallHeightDraft, setWallHeightDraft] = useState('');
  const [wallMode, setWallMode] = useState(false);
  const [wallChain, setWallChain] = useState([]); // [{x,y}] grid intersections
  const [wallMousePos, setWallMousePos] = useState(null);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [selectedAutoWallKey, setSelectedAutoWallKey] = useState(null);
  const [wallOpeningTool, setWallOpeningTool] = useState(null);
  const [wallOpeningSize, setWallOpeningSize] = useState({
    door:     { width: 0.88, height: 2.06 },
    window:   { width: 0.98, height: 0.88, sill: 0.9 },
    archway:  { width: 1.2,  height: 2.4 },
    painting: { width: 0.80, height: 0.60, sill: 1.30 },
    ac:       { width: 0.85, height: 0.30, sill: 2.15 },
    clock:    { width: 0.35, height: 0.35, sill: 1.55 },
    outlet:   { width: 0.10, height: 0.10, sill: 0.30 },
    radiator: { width: 1.00, height: 0.50, sill: 0.12 },
  });
  // Exactly ONE wall is selected at a time. A free-form room edge, a drawn wall
  // segment and an auto-wall each open their own panel, so picking one has to
  // drop the others — otherwise two wall panels stack up on screen, both
  // claiming to be the selection. (Defined here, next to the states they own;
  // `selectedFreeEdge` itself is declared with the other free-form state.)
  const selectFreeEdge = (v)  => { setSelectedFreeEdge(v);      setSelectedWallId(null);      setSelectedAutoWallKey(null); };
  const selectWallSeg  = (id) => { setSelectedWallId(id);       setSelectedAutoWallKey(null); setSelectedFreeEdge(null); };
  const selectAutoWall = (k)  => { setSelectedAutoWallKey(k);   setSelectedWallId(null);      setSelectedFreeEdge(null); };

  const [wallHoverInfo, setWallHoverInfo] = useState(null); // { segId?, autoKey?, t }
  const [selectedWallOpeningInfo, setSelectedWallOpeningInfo] = useState(null); // { wallId?, autoKey?, openingId }
  const [openingMoveArmed, setOpeningMoveArmed] = useState(false); // must click the move icon before a drag can reposition the opening
  const [wallListOpen, setWallListOpen] = useState(true);
  const [wallDrag, setWallDrag] = useState(null);
  const [dragWallPreview, setDragWallPreview] = useState(null);
  const dragPreviewRef = useRef(null);
  const wallTouchMovingRef     = useRef(null); // wall id in touch-move mode (sync, for handlers)
  const [wallTouchMoving, setWallTouchMoving] = useState(null); // same, for rendering
  const wallTouchMoveStartRef  = useRef(null); // { touchX, touchY, origX1, origY1, origX2, origY2 }
  const [openingDragPreview, setOpeningDragPreview] = useState(null); // { wallId, openingId, t } for live position
  const wallPointerDragRef = useRef(null); // { pointerId } while a touch "tap and drag" wall gesture is active
  const wallMoveRafRef = useRef(null);     // rAF handle coalescing wallMousePos updates to one per frame
  const wallMoveLatestRef = useRef(null);  // latest computed point, applied by the pending rAF

  // ── FREE-FORM WALL tool (any angle + curve) ─────────────────────────────────
  // A small state machine, all in float grid-cell units:
  //   draft = null                            → idle (click sets the start point)
  //   draft = { x1,y1, stage:'end' }          → picking the end point (angle-snapped)
  //   draft = { x1,y1,x2,y2, stage:'confirm' }→ straight wall drawn, waiting on the
  //                                             place / curve buttons at its midpoint
  //   draft = { x1,y1,x2,y2, stage:'bulge' }  → bowing the arc (click to commit)
  // The 'confirm' step exists so the tool doesn't slide into curving on its own:
  // most walls are straight, and moving the mouse used to bow them immediately.
  const [freeWallMode, setFreeWallMode] = useState(false);
  const [freeWallDraft, setFreeWallDraft] = useState(null);
  const [freeWallCursor, setFreeWallCursor] = useState(null); // live {x,y} in cells
  // Walls placed since the tool was turned on — what Prev/Cancel step back.
  const [freeWallSessionIds, setFreeWallSessionIds] = useState([]);
  // Re-curving something already placed (a free wall, or one edge of a free-form
  // room) from its panel: the bowing step runs without the tool being armed.
  const freeWallRecurving = !!freeWallDraft && (freeWallDraft.editId != null || !!freeWallDraft.editRoom);
  const [selectedFreeWallId, setSelectedFreeWallId] = useState(null);
  const [selectedFreeRoomId, setSelectedFreeRoomId] = useState(null);
  const [selectedFreeEdge, setSelectedFreeEdge] = useState(null); // { roomId, edge }
  const [freeOpeningDrag, setFreeOpeningDrag] = useState(null);   // live drag preview
  const [freeRoomDragOffset, setFreeRoomDragOffset] = useState(null); // { id, dx, dy } live
  const freeRoomDragRef = useRef(null);
  const shiftDownRef = useRef(false);

  // Toast shown when a wall commit is blocked because it overlaps an existing
  // wall or a room's own boundary — the live preview already turns red for
  // this (see wallConflicts below), but a commit that's silently a no-op
  // reads as a bug rather than an intentional block without this.
  const [wallBlockedMsg, setWallBlockedMsg] = useState(null);
  const wallBlockedTimerRef = useRef(null);
  const flashWallBlocked = () => {
    setWallBlockedMsg(t('wall_blocked_msg'));
    clearTimeout(wallBlockedTimerRef.current);
    wallBlockedTimerRef.current = setTimeout(() => setWallBlockedMsg(null), 2500);
  };
  useEffect(() => () => clearTimeout(wallBlockedTimerRef.current), []);

  // ── ROOM MERGE ──────────────────────────────────────────────────────────
  // In merge mode, click a room to select it, then click an adjacent room to
  // fuse them (they must share a wall). The merged room stays selected so more
  // rooms can be added to it. Non-adjacent picks flash a "can't merge" toast.
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeIds, setMergeIds] = useState([]);
  const [mergeMsg, setMergeMsg] = useState(null);
  const mergeMsgTimerRef = useRef(null);
  const flashMergeMsg = (msg) => {
    setMergeMsg(msg);
    clearTimeout(mergeMsgTimerRef.current);
    mergeMsgTimerRef.current = setTimeout(() => setMergeMsg(null), 2500);
  };
  useEffect(() => () => clearTimeout(mergeMsgTimerRef.current), []);

  const toggleMergeMode = () => {
    setMergeMode(m => {
      const next = !m;
      setMergeIds([]);
      if (next) { disableAllModes(); setSelectedRoomId(null); setSelectedFurnitureId(null); }
      return next;
    });
  };

  const handleMergeClick = (roomId) => {
    // A DRAWN room (free walls — angled/curved outline in `pts`) can't take part
    // in a merge. Its outline and a rectangle room's cells are two different
    // shape representations, so the result could only ever be the two of them
    // drawn side by side — which left the now-internal shared wall standing in
    // the plan and in 3D. Blocked at the click so the bad shape never exists.
    const clickedRoom = rooms.find(r => r.id === roomId);
    if (clickedRoom?.pts?.length >= 3) { flashMergeMsg(t('merge_free_msg')); return; }

    if (mergeIds.length === 0) { setMergeIds([roomId]); return; }
    if (mergeIds.includes(roomId)) { setMergeIds(prev => prev.filter(id => id !== roomId)); return; }
    const clicked = clickedRoom;
    const selected = mergeIds.map(id => rooms.find(r => r.id === id)).filter(Boolean);
    if (!clicked || selected.length === 0) return;
    const adjacent = selected.some(sr => roomsShareWall(sr, clicked));
    if (!adjacent) { flashMergeMsg(t('merge_fail_msg')); return; }
    dispatch(mergeRooms([...mergeIds, roomId]));
    setMergeIds([mergeIds[0]]); // first id survives as the merged room
  };

  // Mutual exclusion helper
  const resetFreeWall = () => { setFreeWallDraft(null); setFreeWallCursor(null); };
  const enableDrawMode  = () => { setDrawMode(true);  setWallMode(false); setWallChain([]); setWallMousePos(null); setDrawSessionRoomIds([]); setMergeMode(false); setMergeIds([]); setFreeWallMode(false); resetFreeWall(); };
  const enableWallMode  = () => { setWallMode(true);  setDrawMode(false); setDrawing(null); setMergeMode(false); setMergeIds([]); setFreeWallMode(false); resetFreeWall(); };
  // Each turn of the tool starts a fresh session, so Prev/Cancel only ever step
  // back walls drawn this time round.
  const enableFreeWallMode = () => { setFreeWallMode(true); setWallMode(false); setDrawMode(false); setDrawing(null); setWallChain([]); setWallMousePos(null); setMergeMode(false); setMergeIds([]); setSelectedRoomId?.(null); setFreeWallSessionIds([]); resetFreeWall(); };
  const disableAllModes = () => { setDrawMode(false); setWallMode(false); setDrawing(null); setWallChain([]); setWallMousePos(null); setDrawSessionRoomIds([]); setFreeWallMode(false); setFreeWallSessionIds([]); resetFreeWall(); };

  // ── Free-wall geometry helpers ──────────────────────────────────────────────
  // Mouse event → point in float grid-cell units, clamped to the grid.
  const freePointFromEvent = (e) => {
    if (!gridRef.current) return null;
    const rect = gridRef.current.getBoundingClientRect();
    const z = zoomRef.current;
    const x = (e.clientX - rect.left) / (CELL * z);
    const y = (e.clientY - rect.top)  / (CELL * z);
    return { x: Math.max(0, Math.min(GRID, x)), y: Math.max(0, Math.min(GRID, y)) };
  };
  // Snap an endpoint so the chord from `from` lands on a 15° angle + rounded
  // length (Shift = fully free). Keeps angled walls clean without a rigid grid.
  const snapFreeEnd = (from, to) => {
    if (shiftDownRef.current) return { x: Math.round(to.x * 4) / 4, y: Math.round(to.y * 4) / 4 };
    const dx = to.x - from.x, dy = to.y - from.y;
    let ang = Math.atan2(dy, dx);
    const step = Math.PI / 12; // 15°
    ang = Math.round(ang / step) * step;
    let len = Math.hypot(dx, dy);
    len = Math.round(len * 2) / 2; // half-cell length steps
    return { x: from.x + Math.cos(ang) * len, y: from.y + Math.sin(ang) * len };
  };
  // Signed perpendicular distance from the chord (x1,y1)->(x2,y2) to point p —
  // used as the live bulge while bowing the arc.
  const perpBulge = (x1, y1, x2, y2, p) => {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    return ((p.x - x1) * dy - (p.y - y1) * dx) / len;
  };
  // SVG path (in board px) for a free wall — a line, or a quadratic arc bowed by
  // `bulge`. The control point sits 2× the sagitta off the chord midpoint so the
  // curve passes through chord-mid + perp·bulge (see perpBulge's sign).
  const freeWallPath = (x1, y1, x2, y2, bulge) => {
    const X1 = x1 * CELL, Y1 = y1 * CELL, X2 = x2 * CELL, Y2 = y2 * CELL;
    if (!bulge) return `M ${X1} ${Y1} L ${X2} ${Y2}`;
    const dx = X2 - X1, dy = Y2 - Y1;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dy / len, ny = -dx / len;                 // perp unit (+bulge side)
    const cx = (X1 + X2) / 2 + nx * bulge * CELL * 2;
    const cy = (Y1 + Y2) / 2 + ny * bulge * CELL * 2;
    return `M ${X1} ${Y1} Q ${cx} ${cy} ${X2} ${Y2}`;
  };

  // Snap a point onto the nearest existing wall endpoint — free walls, grid wall
  // segments, AND the corners of rooms already placed — so a free wall (e.g. an
  // arch) closes an outline whose straight sides are grid walls, and corners meet
  // EXACTLY. Room corners count as much as loose wall ends: a new room is nearly
  // always started or finished on the corner of one already standing, and landing
  // a hair off leaves the outline open, so no room gets built.
  const snapToFreeEndpoints = (p, threshold = 0.7) => {
    let best = null, bestD = threshold;
    const consider = (x, y) => { const dd = Math.hypot(p.x - x, p.y - y); if (dd < bestD) { bestD = dd; best = { x, y }; } };
    for (const w of freeWalls)    { consider(w.x1, w.y1); consider(w.x2, w.y2); }
    for (const s of wallSegments) { consider(s.x1, s.y1); consider(s.x2, s.y2); }
    for (const r of rooms) {
      // A free-form room's corners are its outline vertices; a rectangle room's
      // are its rects' corners. (A free-form room's rects are the rasterised
      // cells — snapping to those would offer hundreds of meaningless points.)
      if (r.pts && r.pts.length >= 3) { for (const v of r.pts) consider(v.x, v.y); }
      else for (const rc of (r.rects?.length ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }])) {
        consider(rc.x, rc.y);        consider(rc.x + rc.w, rc.y);
        consider(rc.x, rc.y + rc.h); consider(rc.x + rc.w, rc.y + rc.h);
      }
    }
    return best || p;
  };

  // The walls of rooms that already exist, as loop candidates. A new room is
  // very often closed off against the side of one already standing — without
  // these that side isn't in the list at all, so the outline can never close and
  // no room appears. `r`/`a` ids are ignored by splitUsed, so these walls stay
  // with the room that owns them instead of being consumed.
  const roomBoundaryWalls = () => [
    // Free-form room: its real outline edges (angle + curve intact).
    ...rooms.flatMap(r => (r.pts && r.pts.length >= 3)
      ? r.pts.map((a, i) => {
          const b = r.pts[(i + 1) % r.pts.length];
          return { x1: a.x, y1: a.y, x2: b.x, y2: b.y, bulge: a.bulge || 0, id: `r${r.id}_${i}` };
        })
      : []),
    // Rectangle rooms: their perimeter is exactly the auto-walls already derived.
    ...autoWalls.map((w, i) => ({ x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2, bulge: 0, id: `a${i}` })),
  ];

  // All walls (free + grid + existing rooms') as a uniform list for loop
  // detection, each with a namespaced id so we can tell which store array to
  // consume from afterwards. Walls the user drew come FIRST so the search
  // prefers them over an existing room's when both continue a chain.
  const allWallsForLoop = (extra = null) => [
    ...(extra ? [{ ...extra, id: 'new' }] : []),
    ...freeWalls.map(w => ({ x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2, bulge: w.bulge || 0, id: `f${w.id}` })),
    ...wallSegments.map(s => ({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2, bulge: 0, id: `s${s.id}` })),
    ...roomBoundaryWalls(),
  ];
  // Split a usedIds list back into free-wall ids and grid-segment ids to consume.
  const splitUsed = (usedIds) => ({
    freeIds: usedIds.filter(id => id[0] === 'f').map(id => +id.slice(1)),
    segIds:  usedIds.filter(id => id[0] === 's').map(id => +id.slice(1)),
  });

  // Click on the free-wall capture layer: run the 3-stage state machine. The
  // endpoint snapping lets an outline close on itself; when it does, the loose
  // walls auto-become a ROOM (floor) — matching how straight Draw-Room walls do.
  const handleFreeWallClick = (e) => {
    const raw = freePointFromEvent(e);
    if (!raw) return;
    const d = freeWallDraft;
    if (!d) {
      const p = snapToFreeEndpoints(raw);
      setFreeWallDraft({ x1: p.x, y1: p.y, stage: 'end' });
      return;
    }
    if (d.stage === 'end') {
      const snap = snapToFreeEndpoints(raw);           // endpoint snap wins over
      const end = snap !== raw ? snap : snapFreeEnd({ x: d.x1, y: d.y1 }, raw); // 15° snap
      if (Math.hypot(end.x - d.x1, end.y - d.y1) < 0.25) return; // too short
      setFreeWallDraft({ ...d, x2: end.x, y2: end.y, bulge: 0, stage: 'confirm' });
      return;
    }
    // Waiting on the place/curve buttons — a stray click on the canvas mustn't
    // decide for the user.
    if (d.stage === 'confirm') return;
    // stage 'bulge' → commit with however far the cursor has bowed it.
    commitFreeWall(perpBulge(d.x1, d.y1, d.x2, d.y2, raw));
  };

  // Place the drafted wall. If it closes an outline (free + grid walls) into a
  // loop, build a ROOM from it instead of leaving a loose wall.
  const commitFreeWall = (bulge = 0) => {
    const d = freeWallDraft;
    if (!d || d.x2 == null) return;
    // Re-curving something that already exists (from a wall panel) — just update
    // it; nothing new is being created, so no loop detection, no session entry.
    if (d.editId != null) {
      dispatch(updateFreeWall({ id: d.editId, bulge }));
      setFreeWallDraft(null); setFreeWallCursor(null);
      return;
    }
    if (d.editRoom) {
      dispatch(setRoomEdgeBulge({ roomId: d.editRoom.roomId, edge: d.editRoom.edge, bulge }));
      setFreeWallDraft(null); setFreeWallCursor(null);
      return;
    }
    const closing = { x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2, bulge };
    // Only a loop through the wall just drawn counts — otherwise closing any
    // wall could spawn a room somewhere else entirely (existing rooms' walls are
    // candidates now, and those already form loops of their own).
    const res = chainFreeWallsToLoop(allWallsForLoop(closing), { requireId: 'new' });
    // If the loop can't actually be rasterised into a room, keep the wall rather
    // than dropping it — losing the segment the user just drew is worse than
    // leaving the outline open.
    if (res && makeRoomFromLoop(res)) {
      setFreeWallSessionIds([]);   // the loose walls became a room — nothing to step back to
    } else {
      setFreeWallSessionIds(prev => [...prev, houseState.nextFreeWallId]);
      dispatch(addFreeWall(closing));
    }
    setFreeWallDraft(null); setFreeWallCursor(null);
  };

  // Turn a detected wall loop into a REAL room: rasterise the outline into the
  // rect list the room system is built on (so every existing tool — resize,
  // wall clicks, openings, merge, 2D plan, 3D editor — works on it) and keep the
  // smooth outline in `pts` for curved rendering.
  const makeRoomFromLoop = (res) => {
    const rects = polygonToRects(res.loop, GRID);
    if (!rects.length) { flashMergeMsg(t('free_room_fail')); return false; }
    const { freeIds, segIds } = splitUsed(res.usedIds);
    dispatch(placePolygonRoom({
      type: 'custom', rects, pts: res.loop,
      consumedWallIds: freeIds, consumedSegIds: segIds,
    }));
    return true;
  };
  const handleFreeWallMove = (e) => {
    const p = freePointFromEvent(e);
    if (p) setFreeWallCursor(p);
  };

  // Drag a free-form room to reposition it (live preview via an SVG translate,
  // committed once on release so undo history isn't flooded).
  const handleFreeRoomPointerDown = (e, r) => {
    if (freeWallMode || wallMode || drawMode || mergeMode) return;
    e.stopPropagation();
    setSelectedFreeRoomId(r.id);
    setSelectedRoomId?.(null);
    const p = freePointFromEvent(e);
    if (!p) return;
    freeRoomDragRef.current = { id: r.id, x0: p.x, y0: p.y, moved: false };
    const onMove = (mv) => {
      const cur = freePointFromEvent(mv);
      const st = freeRoomDragRef.current;
      if (!cur || !st) return;
      st.moved = true;
      setFreeRoomDragOffset({ id: st.id, dx: cur.x - st.x0, dy: cur.y - st.y0 });
    };
    const onUp = (mu) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const st = freeRoomDragRef.current;
      freeRoomDragRef.current = null;
      const cur = freePointFromEvent(mu);
      if (st && cur && st.moved) {
        const dx = cur.x - st.x0, dy = cur.y - st.y0;
        if (Math.abs(dx) + Math.abs(dy) > 0.02) dispatch(moveFreeRoom({ id: st.id, dx, dy }));
      }
      setFreeRoomDragOffset(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // RESIZE / RESHAPE a free room: drag a corner vertex, or an edge's curve
  // handle (which bows that edge). Live preview, committed on release.
  const [freeRoomEdit, setFreeRoomEdit] = useState(null); // { id, kind:'vertex'|'bulge', index, x, y, bulge }
  const handleFreeRoomHandleDown = (e, r, kind, index) => {
    e.stopPropagation();
    const start = freePointFromEvent(e);
    if (!start) return;
    const onMove = (mv) => {
      const p = freePointFromEvent(mv);
      if (!p) return;
      if (kind === 'vertex') {
        const q = shiftDownRef.current ? p : { x: Math.round(p.x * 2) / 2, y: Math.round(p.y * 2) / 2 };
        setFreeRoomEdit({ id: r.id, kind, index, x: q.x, y: q.y });
      } else {
        const a = r.pts[index], b = r.pts[(index + 1) % r.pts.length];
        setFreeRoomEdit({ id: r.id, kind, index, bulge: perpBulge(a.x, a.y, b.x, b.y, p) });
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setFreeRoomEdit(cur => {
        if (cur && cur.id === r.id && cur.index === index) {
          if (cur.kind === 'vertex') dispatch(moveFreeRoomVertex({ id: r.id, index, x: cur.x, y: cur.y }));
          else dispatch(setFreeRoomEdgeBulge({ id: r.id, index, bulge: cur.bulge }));
        }
        return null;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  // OPENINGS on a free room: find the closest point on its outline to a click,
  // returning which edge and where along it (t) — so a door/window lands on the
  // wall the user clicked, straight or curved.
  const nearestFreeRoomEdge = (r, p, steps = 24) => {
    const pts = r.pts;
    let best = null;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const q = edgePointAt(a, b, t);
        const dd = Math.hypot(q.x - p.x, q.y - p.y);
        if (!best || dd < best.dist) best = { dist: dd, edge: i, t };
      }
    }
    return best;
  };
  // Click on a free room while an opening tool is armed → place that opening.
  const handleFreeRoomOpeningClick = (e, r) => {
    if (!wallOpeningTool) return false;
    const p = freePointFromEvent(e);
    if (!p) return false;
    const hit = nearestFreeRoomEdge(r, p);
    if (!hit || hit.dist > 1.2) return false;   // must click near a wall
    const size = wallOpeningSize[wallOpeningTool] || {};
    dispatch(addFreeRoomOpening({
      roomId: r.id, edge: hit.edge, t: hit.t, type: wallOpeningTool,
      width: size.width, height: size.height, sill: size.sill,
    }));
    return true;
  };

  // Same, for a REAL room that carries a free-form outline (`pts`): the opening
  // is stored on the room itself via the standard addOpening action, tagged with
  // the outline edge + t so it renders on the curved wall.
  const handlePtsRoomOpeningClick = (e, room) => {
    if (!openingTool && !wallOpeningTool) return false;
    const type = openingTool || wallOpeningTool;
    const p = freePointFromEvent(e);
    if (!p) return false;
    const hit = nearestFreeRoomEdge({ pts: room.pts }, p);
    if (!hit || hit.dist > 1.2) return false;
    const size = (openingTool ? openingSize[type] : wallOpeningSize[type]) || {};
    dispatch(addOpening({
      roomId: room.id, type, wall: 'top', pos: hit.t,
      edge: hit.edge, t: hit.t,
      ...size,
    }));
    setOpeningTool(null);
    return true;
  };

  // Drag an opening along a free-form room's outline: it follows the cursor to
  // the nearest point on the outline, so it can slide along its wall AND hop to
  // an adjacent wall — the free-form equivalent of dragging along a straight one.
  const handleFreeOpeningDrag = (e, room, op) => {
    setSelectedOpeningId(op.id);
    setSelectedRoomId(room.id);
    setSelectedFreeEdge(null);
    let last = null;
    let raf = null;
    const apply = (p) => {
      raf = null;
      const hit = nearestFreeRoomEdge({ pts: room.pts }, p, 96); // fine sampling
      if (!hit) return;
      last = hit;
      // Live preview only — committing to the store on every move would push a
      // history entry per pixel and make the drag stutter.
      setFreeOpeningDrag({ roomId: room.id, openingId: op.id, edge: hit.edge, t: hit.t });
    };
    const onMove = (mv) => {
      const p = freePointFromEvent(mv);
      if (!p || raf) return;
      raf = requestAnimationFrame(() => apply(p));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (raf) cancelAnimationFrame(raf);
      setFreeOpeningDrag(null);
      if (last) {
        dispatch(moveOpening({ roomId: room.id, openingId: op.id, wall: op.wall, pos: last.t, edge: last.edge, t: last.t }));
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // The room's points with any in-flight edit applied (for live preview).
  const freeRoomPts = (r) => {
    const ed = freeRoomEdit;
    if (!ed || ed.id !== r.id) return r.pts;
    return r.pts.map((p, i) => {
      if (i !== ed.index) return p;
      return ed.kind === 'vertex' ? { ...p, x: ed.x, y: ed.y } : { ...p, bulge: ed.bulge };
    });
  };
  // The wall currently being previewed (draft + live cursor) in cell units.
  const freeWallPreview = (() => {
    const d = freeWallDraft, c = freeWallCursor;
    if (!d) return null;
    if (d.stage === 'end') {
      if (!c) return null;
      const snap = snapToFreeEndpoints(c);
      const end = snap !== c ? snap : snapFreeEnd({ x: d.x1, y: d.y1 }, c);
      return { x1: d.x1, y1: d.y1, x2: end.x, y2: end.y, bulge: 0, snapped: snap !== c };
    }
    // 'confirm' holds the wall straight until Curve is picked — and the preview
    // has to stand on its own here, with no cursor to derive it from.
    const bulge = (d.stage === 'bulge' && c) ? perpBulge(d.x1, d.y1, d.x2, d.y2, c) : 0;
    return { x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2, bulge };
  })();

  useEffect(() => {
    const onDown = (e) => {
      if (e.key === 'Shift') shiftDownRef.current = true;
      if (!freeWallMode && selectedFreeWallId == null && selectedFreeRoomId == null) return;
      if (e.key === 'Escape') {
        if (freeWallDraft) { setFreeWallDraft(null); setFreeWallCursor(null); }
        else if (freeWallMode) disableAllModes();
        setSelectedFreeWallId(null); setSelectedFreeRoomId(null);
      } else if (e.key === 'Enter' && freeWallDraft && (freeWallDraft.stage === 'confirm' || freeWallDraft.stage === 'bulge')) {
        commitFreeWall(0);   // Enter always places it straight
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedFreeWallId != null) { dispatch(removeFreeWall(selectedFreeWallId)); setSelectedFreeWallId(null); }
        if (selectedFreeRoomId != null) { dispatch(removeFreeRoom(selectedFreeRoomId)); setSelectedFreeRoomId(null); }
      }
    };
    const onUp = (e) => { if (e.key === 'Shift') shiftDownRef.current = false; };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, [freeWallMode, freeWallDraft, selectedFreeWallId, selectedFreeRoomId]); // eslint-disable-line

  // ── FREE WALL: Prev / Cancel / Done ────────────────────────────────────────
  // Free walls commit to the store as they're placed (there's no pending list
  // like the wall chain), so stepping back means removing them again — the same
  // shape as the "Add room" session controls.
  const handleFreeWallPrev = () => {
    // A half-drawn wall is the most recent thing on screen — drop that first.
    if (freeWallDraft) { setFreeWallDraft(null); setFreeWallCursor(null); return; }
    setFreeWallSessionIds(prev => {
      if (prev.length === 0) return prev;
      dispatch(removeFreeWall(prev[prev.length - 1]));
      return prev.slice(0, -1);
    });
  };
  const handleFreeWallCancel = () => {
    freeWallSessionIds.forEach(id => dispatch(removeFreeWall(id)));
    setFreeWallSessionIds([]);
    setFreeWallDraft(null); setFreeWallCursor(null);
    disableAllModes();
  };
  // Done also closes the shape off: if the walls form ONE closed loop, turn it
  // into a room with a floor (what the old "Make Room" button did) — otherwise
  // just stop drawing and leave the walls as they are.
  const handleFreeWallDone = () => {
    // A loop made only of existing rooms' walls is just those rooms' own
    // outlines — it has to use at least one wall the user drew.
    const res = chainFreeWallsToLoop(allWallsForLoop(), {
      accept: ids => ids.some(id => id[0] === 'f' || id[0] === 's'),
    });
    if (res) makeRoomFromLoop(res);
    setFreeWallSessionIds([]);
    setFreeWallDraft(null); setFreeWallCursor(null);
    disableAllModes();
  };
  // SVG floor path (board px) for a free-room polygon — straight + arced edges.
  const freeRoomFloorPath = (pts) => {
    if (!pts || pts.length < 3) return '';
    let d = `M ${pts[0].x * CELL} ${pts[0].y * CELL}`;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      if (a.bulge) {
        const { cx, cy } = edgeControlPoint(a.x, a.y, b.x, b.y, a.bulge);
        d += ` Q ${cx * CELL} ${cy * CELL} ${b.x * CELL} ${b.y * CELL}`;
      } else {
        d += ` L ${b.x * CELL} ${b.y * CELL}`;
      }
    }
    return d + ' Z';
  };

  // Saves the current in-progress chain as permanent wall segments, then exits wall mode.
  // Used by Escape and the wall-mode toggle so partial walls are never silently discarded.
  const exitWallModeAndSave = () => {
    if (wallChain.length >= 2) {
      let anyBlocked = false;
      for (let i = 0; i < wallChain.length - 1; i++) {
        const a = wallChain[i], b = wallChain[i + 1];
        if ((a.x === b.x || a.y === b.y) && Math.abs(a.x - b.x) + Math.abs(a.y - b.y) > 0) {
          const seg = { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
          if (!wallConflicts(seg, wallSegments, rooms)) dispatch(addWallSegment(seg));
          else anyBlocked = true;
        }
      }
      if (anyBlocked) flashWallBlocked();
    }
    disableAllModes();
  };

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (wallMode) exitWallModeAndSave();
        else disableAllModes();
        return;
      }

      // Ignore shortcuts while typing (room name, wall height input, etc.) so
      // native text editing — including the browser's own undo/redo/delete —
      // isn't hijacked.
      const t = e.target;
      const isEditable = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (isEditable) return;

      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const isUndo = mod && key === 'z' && !e.shiftKey;
      const isRedo = mod && (key === 'y' || (key === 'z' && e.shiftKey));
      if (isUndo || isRedo) {
        e.preventDefault();
        if (isRedo) dispatch(builderHistoryNext());
        else dispatch(builderHistoryBack());
        return;
      }

      if (mod && key === 'c') {
        e.preventDefault();
        handleCopySelected();
        return;
      }
      if (mod && key === 'v') {
        e.preventDefault();
        handlePasteClipboard();
        return;
      }

      if (key === 'delete' || key === 'backspace') {
        // Whichever item is currently selected, most-specific first.
        if (selectedWallOpeningInfo) {
          e.preventDefault();
          if (selectedWallOpeningInfo.autoKey) {
            dispatch(removeAutoWallOpening({ key: selectedWallOpeningInfo.autoKey, openingId: selectedWallOpeningInfo.openingId }));
          } else if (selectedWallOpeningInfo.roomId) {
            dispatch(removeOpening({ roomId: selectedWallOpeningInfo.roomId, openingId: selectedWallOpeningInfo.openingId }));
          } else {
            dispatch(selectedWallOpeningInfo.freeWallId != null
              ? removeFreeWallOpening({ wallId: selectedWallOpeningInfo.freeWallId, openingId: selectedWallOpeningInfo.openingId })
              : removeWallSegOpening({ wallId: selectedWallOpeningInfo.wallId, openingId: selectedWallOpeningInfo.openingId }));
          }
          setSelectedWallOpeningInfo(null);
        } else if (selectedOpeningId != null && selectedRoomId != null) {
          e.preventDefault();
          dispatch(removeOpening({ roomId: selectedRoomId, openingId: selectedOpeningId }));
          setSelectedOpeningId(null);
        } else if (selectedWallId != null) {
          e.preventDefault();
          dispatch(removeWallSegment(selectedWallId));
          setSelectedWallId(null);
        } else if (selectedFurnitureId != null) {
          e.preventDefault();
          dispatch(removeFurniture(selectedFurnitureId));
          setSelectedFurnitureId(null);
        } else if (selectedRoomId != null) {
          e.preventDefault();
          dispatch(removeRoom(selectedRoomId));
          setSelectedRoomId(null);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    wallMode, wallChain, dispatch,
    selectedWallOpeningInfo, selectedOpeningId, selectedRoomId,
    selectedWallId, selectedFurnitureId,
    rooms, wallSegments, autoWallOpenings, furniture,
    nextFurnitureId, nextOpeningId, nextWallOpeningId,
  ]);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const oldZoom = zoomRef.current;
      const newZoom = Math.min(2.5, Math.max(0.2, oldZoom * (e.deltaY < 0 ? 1.1 : 0.9)));
      zoomRef.current = newZoom;
      setZoom(newZoom);
      requestAnimationFrame(() => {
        const gridX = (mouseX + el.scrollLeft) / oldZoom;
        const gridY = (mouseY + el.scrollTop) / oldZoom;
        el.scrollLeft = gridX * newZoom - mouseX;
        el.scrollTop = gridY * newZoom - mouseY;
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const selectedRoom = rooms.find(r => r.id === selectedRoomId) || null;
  const selectedFurniture = furniture.find(f => f.id === selectedFurnitureId) || null;
  // Dimension-line target: the live bbox while dragging, otherwise the selected
  // piece's static bbox. Drives <FurnitureDimensions> in the wall overlay SVG.
  const dimBBox = furnitureDrag?.bbox
    || (selectedFurniture ? staticFurnitureBBox(selectedFurniture) : null);
  // ── MOVE MODE ENDS WITH THE SELECTION ─────────────────────────────────────
  // Done here rather than in each PlacedRoom: every unselected room renders one
  // of those, so an effect there would have all of them clearing the single
  // shared id, the room actually being moved included.
  useEffect(() => {
    setMoveRoomId(id => (id != null && id !== selectedRoomId ? null : id));
  }, [selectedRoomId]);

  // Show panel for selected room, or preview panel for hovered room (rooms tab only)
  //
  // AND NOTHING AT ALL WHILE THAT ROOM IS BEING MOVED. The panel is a large
  // floating card over the canvas, so on a phone it covers the very room you
  // just asked to drag — you would be moving something you cannot see. Turning
  // move mode on therefore closes it, and turning move mode off (the button on
  // the room, or tapping away) brings it straight back. Expressed by withholding
  // `panelRoom` because the panel's container renders only when something is
  // selected, so this closes the whole card rather than emptying it.
  const movingThisRoom = moveRoomId != null && moveRoomId === selectedRoomId;
  const panelRoom = movingThisRoom
    ? null
    : selectedRoom || (sidebarTab === 'rooms' ? (rooms.find(r => r.id === hoveredRoomId) || null) : null);


  const handleDragStart = useCallback((type) => {
    dispatch(setDragRoomType(type));
  }, [dispatch]);

  const handleGridDragLeave = () => {
    dispatch(setHoverCell(null));
  };

  const handleRoomDragOver = (e) => {
    e.preventDefault();
    const rt = ROOM_TYPES.find(r => r.type === dragRoomType);
    if (!rt || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const col = Math.floor((e.clientX - rect.left) / (CELL * zoom));
    const row = Math.floor((e.clientY - rect.top) / (CELL * zoom));
    dispatch(setHoverCell({ col, row }));
  };

  const handleRoomDrop = (e) => {
    e.preventDefault();
    const rt = ROOM_TYPES.find(r => r.type === dragRoomType);
    if (!rt || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const col = Math.floor((e.clientX - rect.left) / (CELL * zoom));
    const row = Math.floor((e.clientY - rect.top) / (CELL * zoom));
    const newRoomId = nextId;
    dispatch(placeRoom({ type: dragRoomType, col, row }));
    dispatch(setDragRoomType(null));
    dispatch(setHoverCell(null));
    setSelectedRoomId(newRoomId);
    setSelectedFurnitureId(null);
  };

  const handleResize = (dw, dh) => {
    if (!selectedRoom) return;
    dispatch(resizeRoom({ id: selectedRoom.id, w: selectedRoom.w + dw, h: selectedRoom.h + dh }));
  };

  const handleSelectRoom = (id) => {
    setSelectedRoomId(id);
    setSelectedFurnitureId(null);
    setOpeningTool(null);
    setSelectedOpeningId(null);
    setSelectedFreeEdge(null);   // room panel and wall panel are mutually exclusive
    setSidebarTab('furniture');
  };

  const handleSelectFurniture = (id) => {
    setSelectedFurnitureId(id);
    setSelectedRoomId(null);
    setOpeningTool(null);
    setSidebarTab('furniture');
  };

  // Derive wall segments from room perimeters (auto-walls)
  const autoWalls = useMemo(() => {
    // Build a set of unique exterior unit-cell edges for all rooms
    const edgeMap = new Map();

    const addEdge = (x1, y1, x2, y2) => {
      const isH = y1 === y2;
      const key = isH
        ? `h:${y1}:${Math.min(x1, x2)}:${Math.max(x1, x2)}`
        : `v:${x1}:${Math.min(y1, y2)}:${Math.max(y1, y2)}`;
      if (!edgeMap.has(key)) {
        edgeMap.set(key, {
          x1: isH ? Math.min(x1, x2) : x1,
          y1: isH ? y1 : Math.min(y1, y2),
          x2: isH ? Math.max(x1, x2) : x1,
          y2: isH ? y1 : Math.max(y1, y2),
        });
      }
    };

    for (const room of rooms) {
      // Free-form rooms draw their walls along their real angled/curved outline
      // (see PlacedRoom's freePath) — tracing their rasterised cells here would
      // ring them with a staircase of 1-cell wall steps.
      if (room.pts && room.pts.length >= 3) continue;
      // Collect all 1×1 cells belonging to this room
      const cells = new Set();
      const addCells = (rx, ry, rw, rh) => {
        for (let cy = ry; cy < ry + rh; cy++)
          for (let cx = rx; cx < rx + rw; cx++)
            cells.add(`${cx},${cy}`);
      };
      if (room.rects && room.rects.length > 0)
        room.rects.forEach(r => addCells(r.x, r.y, r.w, r.h));
      else
        addCells(room.x, room.y, room.w, room.h);

      // Add only the 4 edges that are NOT shared with another cell in this room
      for (const key of cells) {
        const [cx, cy] = key.split(',').map(Number);
        if (!cells.has(`${cx},${cy - 1}`)) addEdge(cx, cy, cx + 1, cy);       // top
        if (!cells.has(`${cx + 1},${cy}`)) addEdge(cx + 1, cy, cx + 1, cy + 1); // right
        if (!cells.has(`${cx},${cy + 1}`)) addEdge(cx, cy + 1, cx + 1, cy + 1); // bottom
        if (!cells.has(`${cx - 1},${cy}`)) addEdge(cx, cy, cx, cy + 1);       // left
      }
    }

    // Merge collinear adjacent unit segments into longer wall lines
    const rawSegs = Array.from(edgeMap.values());

    const hRaw = rawSegs.filter(s => s.y1 === s.y2).sort((a, b) => a.y1 - b.y1 || a.x1 - b.x1);
    const mergeH = [];
    for (const s of hRaw) {
      const last = mergeH[mergeH.length - 1];
      if (last && last.y1 === s.y1 && last.x2 === s.x1) last.x2 = s.x2;
      else mergeH.push({ ...s });
    }

    const vRaw = rawSegs.filter(s => s.x1 === s.x2).sort((a, b) => a.x1 - b.x1 || a.y1 - b.y1);
    const mergeV = [];
    for (const s of vRaw) {
      const last = mergeV[mergeV.length - 1];
      if (last && last.x1 === s.x1 && last.y2 === s.y1) last.y2 = s.y2;
      else mergeV.push({ ...s });
    }

    const autoWallKey = (w) => w.y1 === w.y2
      ? `h:${w.y1}:${w.x1}:${w.x2}`
      : `v:${w.x1}:${w.y1}:${w.y2}`;
    return [...mergeH, ...mergeV].map(w => ({ ...w, key: autoWallKey(w) }));
  }, [rooms]);

  const handleDeselect = () => {
    setSelectedRoomId(null);
    setSelectedFurnitureId(null);
    setOpeningTool(null);
    setSelectedOpeningId(null);
    setSelectedWallId(null);
    setSelectedAutoWallKey(null);
    setWallOpeningTool(null);
    setSelectedWallOpeningInfo(null);
    setSidebarTab('furniture');
    setConfirmDelete(false);
    setConfirmDeleteFurniture(false);
  };

  // ── COPY / PASTE ─────────────────────────────────────────────────────────
  // Copies whichever item is selected (furniture, or an opening/wall item in
  // any of its three placement forms — auto-wall, room, or standalone wall
  // segment) into a local clipboard, keeping every parameter (type, size,
  // rotation, color). Paste re-creates it nearby — same wall, slightly
  // shifted position — and selects the new copy.
  const handleCopySelected = () => {
    if (selectedWallOpeningInfo) {
      const isAutoWall = !!selectedWallOpeningInfo.autoKey;
      const isRoomOpening = !!selectedWallOpeningInfo.roomId;
      let opening = null;
      if (isAutoWall) {
        opening = ((autoWallOpenings || {})[selectedWallOpeningInfo.autoKey] || [])
          .find(o => o.id === selectedWallOpeningInfo.openingId);
      } else if (isRoomOpening) {
        const room = rooms.find(r => r.id === selectedWallOpeningInfo.roomId);
        opening = (room?.openings || []).find(o => o.id === selectedWallOpeningInfo.openingId);
      } else {
        const wall = selectedWallOpeningInfo.freeWallId != null
          ? (freeWalls || []).find(w => w.id === selectedWallOpeningInfo.freeWallId)
          : (wallSegments || []).find(w => w.id === selectedWallOpeningInfo.wallId);
        opening = (wall?.openings || []).find(o => o.id === selectedWallOpeningInfo.openingId);
      }
      if (!opening) return;
      clipboardRef.current = {
        kind: 'opening',
        via: isAutoWall ? 'autoWall' : isRoomOpening ? 'room' : 'wallSeg',
        autoKey: selectedWallOpeningInfo.autoKey, wallId: selectedWallOpeningInfo.wallId, roomId: selectedWallOpeningInfo.roomId,
        data: {
          type: opening.type, width: opening.width, height: opening.height, sill: opening.sill, color: opening.color,
          style: opening.style,
          t: opening.t, wall: opening.wall, pos: opening.pos, absX: opening.absX, absY: opening.absY,
          edge: opening.edge,
        },
      };
      return;
    }
    if (selectedOpeningId != null && selectedRoomId != null) {
      const room = rooms.find(r => r.id === selectedRoomId);
      const opening = (room?.openings || []).find(o => o.id === selectedOpeningId);
      if (!opening) return;
      clipboardRef.current = {
        kind: 'opening', via: 'room', roomId: selectedRoomId,
        data: {
          type: opening.type, width: opening.width, height: opening.height, sill: opening.sill, color: opening.color,
          style: opening.style,
          wall: opening.wall, pos: opening.pos, absX: opening.absX, absY: opening.absY,
          // Outline edge of a DRAWN room — the copy has to land on the same
          // wall, which wall/pos alone can't express.
          edge: opening.edge, t: opening.t,
        },
      };
      return;
    }
    if (selectedFurnitureId != null) {
      const item = furniture.find(f => f.id === selectedFurnitureId);
      if (!item) return;
      clipboardRef.current = {
        kind: 'furniture',
        data: { type: item.type, w: item.w, h: item.h, rotation: item.rotation || 0, color: item.color, style: item.style, x: item.x, y: item.y },
      };
    }
  };

  const handlePasteClipboard = () => {
    const c = clipboardRef.current;
    if (!c) return;

    if (c.kind === 'furniture') {
      const OFFSET = 20; // px — visibly offset from the original
      const newId = nextFurnitureId;
      dispatch(placeFurniture({
        type: c.data.type, w: c.data.w, h: c.data.h, rotation: c.data.rotation,
        color: c.data.color, style: c.data.style,
        x: c.data.x + OFFSET, y: c.data.y + OFFSET,
      }));
      handleDeselect();
      setSelectedFurnitureId(newId);
      return;
    }

    if (c.kind === 'opening') {
      const { type, width, height, sill, color, style, t, wall, pos, absX, absY, edge } = c.data;
      if (c.via === 'autoWall') {
        const newT = Math.max(0.02, Math.min(0.98, t + 0.08));
        const newId = nextWallOpeningId;
        dispatch(addAutoWallOpening({ key: c.autoKey, type, t: newT, width, height, sill, color }));
        handleDeselect();
        setSelectedWallOpeningInfo({ autoKey: c.autoKey, openingId: newId });
        selectAutoWall(c.autoKey);
      } else if (c.via === 'wallSeg') {
        const newT = Math.max(0.02, Math.min(0.98, t + 0.08));
        const newId = nextWallOpeningId;
        dispatch(addWallSegOpening({ wallId: c.wallId, type, t: newT, width, height, sill, color }));
        handleDeselect();
        setSelectedWallOpeningInfo({ wallId: c.wallId, openingId: newId });
        selectWallSeg(c.wallId);
      } else if (c.via === 'room') {
        const newPos = Math.max(0.05, Math.min(0.95, (pos ?? 0.5) + 0.08));
        const newId = nextOpeningId;
        let placement;
        if (edge != null) {
          // DRAWN room: keep the copy on the SAME outline edge, nudged along it.
          const newT = Math.max(0.05, Math.min(0.95, (t ?? 0.5) + 0.08));
          placement = { wall: wall || 'top', pos: newT, edge, t: newT };
        } else if (absX !== undefined && absY !== undefined) {
          const isH = wall === 'top' || wall === 'bottom';
          const SHIFT = CELL * 0.5;
          placement = isH ? { wall, pos: newPos, absX: absX + SHIFT, absY } : { wall, pos: newPos, absX, absY: absY + SHIFT };
        } else {
          placement = { wall, pos: newPos };
        }
        dispatch(addOpening({ roomId: c.roomId, type, width, height, sill, color, style, ...placement }));
        handleDeselect();
        setSelectedWallOpeningInfo({ roomId: c.roomId, openingId: newId });
      }
    }
  };

  // ── Wall segment edit helpers ──────────────────────────────────────────────

  const getWallT = (e, w) => {
    if (!gridRef.current) return 0.5;
    const rect = gridRef.current.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / (CELL * zoomRef.current);
    const my = (e.clientY - rect.top)  / (CELL * zoomRef.current);
    const isH = w.y1 === w.y2;
    return isH
      ? Math.max(0.02, Math.min(0.98, (mx - w.x1) / (w.x2 - w.x1)))
      : Math.max(0.02, Math.min(0.98, (my - w.y1) / (w.y2 - w.y1)));
  };

  const handleWallPointerDown = (e, w, dragType) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    // If an opening tool is active, place the opening instead of dragging
    if (wallOpeningTool) {
      const t = getWallT(e, w);
      const sz = wallOpeningSize[wallOpeningTool];
      dispatch(addWallSegOpening({ wallId: w.id, type: wallOpeningTool, t, ...sz }));
      selectWallSeg(w.id);
      setWallOpeningTool(null);
      return;
    }
    if (!gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const gx = Math.max(0, Math.min(GRID, Math.round((e.clientX - rect.left) / (CELL * zoomRef.current))));
    const gy = Math.max(0, Math.min(GRID, Math.round((e.clientY - rect.top)  / (CELL * zoomRef.current))));
    selectWallSeg(w.id);
    setWallDrag({ dragType, id: w.id, origX1: w.x1, origY1: w.y1, origX2: w.x2, origY2: w.y2, startGx: gx, startGy: gy });
  };

  // An opening is draggable as soon as it's selected (or freshly added, which
  // auto-selects it) — no need to arm move first. Selecting a different opening
  // re-arms that one; deselecting disarms. The move icon still toggles it off
  // if you want to click without any chance of nudging it.
  useEffect(() => {
    setOpeningMoveArmed(!!selectedWallOpeningInfo);
  }, [selectedWallOpeningInfo?.wallId, selectedWallOpeningInfo?.autoKey, selectedWallOpeningInfo?.openingId, selectedWallOpeningInfo?.roomId, selectedWallOpeningInfo?.freeWallId]);

  // Where along a free wall a board point sits (0..1), by sampling the arc and
  // taking the nearest — works the same for a straight run or a bowed one.
  const nearestTOnFreeWall = (w, p) => {
    const a = { x: w.x1, y: w.y1, bulge: w.bulge || 0 }, b = { x: w.x2, y: w.y2 };
    let bestT = 0.5, bestD = Infinity;
    for (let i = 0; i <= 96; i++) {
      const t = i / 96;
      const q = edgePointAt(a, b, t);
      const d = (q.x - p.x) ** 2 + (q.y - p.y) ** 2;
      if (d < bestD) { bestD = d; bestT = t; }
    }
    return bestT;
  };

  // Dragging an armed opening — pointer capture on the marker itself (not a
  // full-screen overlay) so the drag keeps receiving events no matter what's
  // layered on top of it (the mobile sidebar shares a z-index with the old
  // overlay and was swallowing the touch before it ever reached it).
  const startOpeningDrag = useCallback((e, ctx) => {
    e.stopPropagation();
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const pointerId = e.pointerId;
    const startX = e.clientX, startY = e.clientY;
    const absTotalLen = Math.abs(ctx.totalLen);
    const halfWPx = (ctx.width * PX_PER_M) / 2;
    const minT = Math.min(0.45, halfWPx / absTotalLen);
    let moved = false;
    let lastT = ctx.startT;

    const onMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      if (Math.abs(ev.clientX - startX) > 3 || Math.abs(ev.clientY - startY) > 3) moved = true;
      if (ctx.freeWall) {
        // A free wall runs at any angle and may curve, so there's no single axis
        // to take a pixel delta along — project the cursor onto the wall itself
        // and use the nearest point's t.
        const p = freePointFromEvent(ev);
        if (p) lastT = Math.max(minT, Math.min(1 - minT, nearestTOnFreeWall(ctx.freeWall, p)));
      } else {
        const z = zoomRef.current;
        const dPx = ctx.isH ? (ev.clientX - startX) / z : (ev.clientY - startY) / z;
        lastT = Math.max(minT, Math.min(1 - minT, ctx.startT + dPx / absTotalLen));
      }
      setOpeningDragPreview({ wallId: ctx.wallId, autoKey: ctx.autoKey, roomId: ctx.roomId, freeWallId: ctx.freeWallId, openingId: ctx.openingId, t: lastT });
    };
    const onUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      el.releasePointerCapture(pointerId);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      setOpeningDragPreview(null);
      if (moved) {
        if (ctx.autoKey) {
          dispatch(updateAutoWallOpening({ key: ctx.autoKey, openingId: ctx.openingId, changes: { t: lastT } }));
        } else if (ctx.roomId) {
          // room.openings (e.g. placed via the 3D room view) store a precise
          // absX/absY on compound rooms, or a bbox-relative pos on simple ones —
          // compute the exact world position this wall's drag landed on and
          // convert to whichever format this room needs.
          const worldGridPos = ctx.wallStartGrid + lastT * (ctx.wallEndGrid - ctx.wallStartGrid);
          const ABS_CELL = 26; // matches ThreeDRoom.jsx's CELL for step-wall openings
          const payload = { roomId: ctx.roomId, openingId: ctx.openingId, wall: ctx.wallSide, pos: 0.5 };
          if (ctx.isCompound) {
            if (ctx.isH) {
              payload.absX = (worldGridPos - ctx.roomX) * ABS_CELL;
              payload.absY = (ctx.wallLineGrid - ctx.roomY) * ABS_CELL;
            } else {
              payload.absX = (ctx.wallLineGrid - ctx.roomX) * ABS_CELL;
              payload.absY = (worldGridPos - ctx.roomY) * ABS_CELL;
            }
          } else {
            payload.pos = Math.max(0.05, Math.min(0.95, ctx.isH
              ? (worldGridPos - ctx.roomX) / ctx.roomW
              : (worldGridPos - ctx.roomY) / ctx.roomH));
          }
          dispatch(moveOpening(payload));
        } else if (ctx.freeWallId != null) {
          dispatch(updateFreeWallOpening({ wallId: ctx.freeWallId, openingId: ctx.openingId, changes: { t: lastT } }));
        } else {
          dispatch(updateWallSegOpening({ wallId: ctx.wallId, openingId: ctx.openingId, changes: { t: lastT } }));
        }
      }
      setOpeningMoveArmed(false);
    };
    const onCancel = (ev) => {
      if (ev.pointerId !== pointerId) return;
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      setOpeningDragPreview(null);
      setOpeningMoveArmed(false);
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
  }, [dispatch]);

  // On mobile/tablet: open sidebar when any wall is selected (50ms lets the touch sequence finish first).
  useEffect(() => {
    if (window.innerWidth > 1024) return;
    if (selectedWallId !== null && !wallTouchMovingRef.current) {
      const t = setTimeout(() => setSidebarOpen(true), 50);
      return () => clearTimeout(t);
    }
    if (selectedAutoWallKey !== null) {
      const t = setTimeout(() => setSidebarOpen(true), 50);
      return () => clearTimeout(t);
    }
  }, [selectedWallId, selectedAutoWallKey]);

  // Close wall panels on mousedown outside any [data-wall-ui] element
  useEffect(() => {
    if (selectedWallId === null && selectedAutoWallKey === null) return;
    const onDown = (e) => {
      if (!e.target.closest('[data-wall-ui]')) {
        setSelectedWallId(null);
        setSelectedAutoWallKey(null);
        setWallOpeningTool(null);
        setSelectedWallOpeningInfo(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selectedWallId, selectedAutoWallKey]);

  // Close wall-height dropdown on click outside
  useEffect(() => {
    if (!wallHeightOpen) return;
    const onDown = (e) => {
      if (!e.target.closest('[data-wh-ui]')) setWallHeightOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [wallHeightOpen]);

  useEffect(() => {
    if (!wallDrag) return;
    const { dragType, id, origX1, origY1, origX2, origY2, startGx, startGy, isTouch } = wallDrag;
    document.body.style.userSelect = 'none';
    if (!isTouch) document.body.style.cursor = dragType === 'move' ? 'grabbing' : 'ew-resize';

    const computeCoords = (clientX, clientY) => {
      if (!gridRef.current) return null;
      const rect = gridRef.current.getBoundingClientRect();
      const gx = Math.max(0, Math.min(GRID, Math.round((clientX - rect.left) / (CELL * zoomRef.current))));
      const gy = Math.max(0, Math.min(GRID, Math.round((clientY - rect.top)  / (CELL * zoomRef.current))));
      const dx = gx - startGx, dy = gy - startGy;
      if (dragType === 'move') {
        return {
          x1: Math.max(0, Math.min(GRID, origX1 + dx)), y1: Math.max(0, Math.min(GRID, origY1 + dy)),
          x2: Math.max(0, Math.min(GRID, origX2 + dx)), y2: Math.max(0, Math.min(GRID, origY2 + dy)),
        };
      } else if (dragType === 'ep1') {
        const isH = origY1 === origY2;
        return isH
          ? { x1: Math.max(0, Math.min(origX2 - 1, origX1 + dx)), y1: origY1, x2: origX2, y2: origY2 }
          : { x1: origX1, y1: Math.max(0, Math.min(origY2 - 1, origY1 + dy)), x2: origX2, y2: origY2 };
      } else {
        const isH = origY1 === origY2;
        return isH
          ? { x1: origX1, y1: origY1, x2: Math.max(origX1 + 1, Math.min(GRID, origX2 + dx)), y2: origY2 }
          : { x1: origX1, y1: origY1, x2: origX2, y2: Math.max(origY1 + 1, Math.min(GRID, origY2 + dy)) };
      }
    };

    const onMove = (e) => {
      const nc = computeCoords(e.clientX, e.clientY);
      if (!nc) return;
      dragPreviewRef.current = nc;
      setDragWallPreview({ ...nc });
    };

    const onTouchMove = (e) => {
      if (!e.touches[0]) return;
      e.preventDefault();
      const nc = computeCoords(e.touches[0].clientX, e.touches[0].clientY);
      if (!nc) return;
      dragPreviewRef.current = nc;
      setDragWallPreview({ ...nc });
    };

    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (isTouch) wallTouchMovingRef.current = null;
      const final = dragPreviewRef.current;
      if (final) dispatch(updateWallSegment({ id, ...final }));
      dragPreviewRef.current = null;
      setWallDrag(null);
      setDragWallPreview(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    if (isTouch) {
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onUp);
      window.addEventListener('touchcancel', onUp);
    }
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onUp);
    };
  }, [wallDrag]);

  // Called when user taps Door/Window/Arch while a manual wall is selected.
  const handleQuickAddOpening = (type) => {
    if (selectedWallId == null) return;
    const sz = wallOpeningSize[type];
    const newId = nextWallOpeningId;
    dispatch(addWallSegOpening({ wallId: selectedWallId, type, t: 0.5, ...sz }));
    const wallId = selectedWallId;
    setSelectedWallId(null);
    setSidebarOpen(false);
    setSelectedWallOpeningInfo({ wallId, openingId: newId });
  };

  // Called when user taps Door/Window/Arch while a room (auto) wall is selected.
  const handleQuickAddOpeningAuto = (type) => {
    if (!selectedAutoWallKey) return;
    const sz = wallOpeningSize[type];
    const newId = nextWallOpeningId;
    dispatch(addAutoWallOpening({ key: selectedAutoWallKey, type, t: 0.5, ...sz }));
    const autoKey = selectedAutoWallKey;
    setSelectedAutoWallKey(null);
    setSidebarOpen(false);
    setSelectedWallOpeningInfo({ autoKey, openingId: newId });
  };

  const handleRotateWall = (w) => {
    const isH = w.y1 === w.y2;
    const cx = Math.round((w.x1 + w.x2) / 2);
    const cy = Math.round((w.y1 + w.y2) / 2);
    if (isH) {
      const half = Math.round(Math.abs(w.x2 - w.x1) / 2);
      dispatch(updateWallSegment({ id: w.id, x1: cx, y1: Math.max(0, cy - half), x2: cx, y2: Math.min(GRID, cy + half) }));
    } else {
      const half = Math.round(Math.abs(w.y2 - w.y1) / 2);
      dispatch(updateWallSegment({ id: w.id, x1: Math.max(0, cx - half), y1: cy, x2: Math.min(GRID, cx + half), y2: cy }));
    }
  };

  const handleNudgeWall = (w, dx, dy) => {
    dispatch(updateWallSegment({
      id: w.id,
      x1: Math.max(0, Math.min(GRID, w.x1 + dx)),
      y1: Math.max(0, Math.min(GRID, w.y1 + dy)),
      x2: Math.max(0, Math.min(GRID, w.x2 + dx)),
      y2: Math.max(0, Math.min(GRID, w.y2 + dy)),
    }));
  };

  const handleResizeWallEp = (w, ep, delta) => {
    const isH = w.y1 === w.y2;
    if (ep === 1) {
      dispatch(updateWallSegment({
        id: w.id,
        x1: isH ? Math.max(0, Math.min(w.x2 - 1, w.x1 + delta)) : w.x1,
        y1: isH ? w.y1 : Math.max(0, Math.min(w.y2 - 1, w.y1 + delta)),
        x2: w.x2, y2: w.y2,
      }));
    } else {
      dispatch(updateWallSegment({
        id: w.id,
        x1: w.x1, y1: w.y1,
        x2: isH ? Math.max(w.x1 + 1, Math.min(GRID, w.x2 + delta)) : w.x2,
        y2: isH ? w.y2 : Math.max(w.y1 + 1, Math.min(GRID, w.y2 + delta)),
      }));
    }
  };

  // Furniture drag-over overlay handlers
  const handleFurnitureDragOver = (e) => {
    e.preventDefault();
    const ft = FURNITURE_TYPES.find(f => f.type === dragFurnitureType);
    if (!ft || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const rawX = (e.clientX - rect.left) / zoom;
    const rawY = (e.clientY - rect.top) / zoom;
    const x = Math.max(0, Math.min(GRID * CELL - ft.w, Math.round((rawX - ft.w / 2) / SNAP) * SNAP));
    const y = Math.max(0, Math.min(GRID * CELL - ft.h, Math.round((rawY - ft.h / 2) / SNAP) * SNAP));
    setFurnitureHover({ x, y });
  };

  const handleFurnitureDrop = (e) => {
    e.preventDefault();
    if (furnitureHover && dragFurnitureType) {
      const newId = nextFurnitureId; // placeFurniture assigns this id (see reducer)
      dispatch(placeFurniture({ type: dragFurnitureType, x: furnitureHover.x, y: furnitureHover.y }));
      setFurnitureHover(null);
      // Auto-select the dropped piece so its distance-to-wall dimensions show at once
      setSelectedFurnitureId(newId);
      setSidebarTab('furniture');
    }
  };

  const handleDrawMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const getCell = (ev) => {
      if (!gridRef.current) return null;
      const rect = gridRef.current.getBoundingClientRect();
      const col = Math.floor((ev.clientX - rect.left) / (CELL * zoomRef.current));
      const row = Math.floor((ev.clientY - rect.top)  / (CELL * zoomRef.current));
      return { col: Math.max(0, Math.min(GRID - 1, col)), row: Math.max(0, Math.min(GRID - 1, row)) };
    };
    const start = getCell(e);
    if (!start) return;
    drawStartRef.current = start;
    setDrawing({ col: start.col, row: start.row, w: 1, h: 1 });

    const onMove = (mv) => {
      const cur = getCell(mv);
      const s = drawStartRef.current;
      if (!cur || !s) return;
      setDrawing({
        col: Math.min(s.col, cur.col),
        row: Math.min(s.row, cur.row),
        w: Math.abs(cur.col - s.col) + 1,
        h: Math.abs(cur.row - s.row) + 1,
      });
    };

    const onUp = (mu) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const cur = getCell(mu);
      const s = drawStartRef.current;
      drawStartRef.current = null;
      setDrawing(null);
      if (!s) return;
      const c = cur || s;
      // placeCustomRoom silently no-ops on overlap (nextId isn't consumed), so
      // only track this drag's id in the session for Prev/Cancel if it actually landed.
      const idBefore = store.getState().house.nextId;
      dispatch(placeCustomRoom({
        type: 'custom',
        x: Math.min(s.col, c.col),
        y: Math.min(s.row, c.row),
        w: Math.abs(c.col - s.col) + 1,
        h: Math.abs(c.row - s.row) + 1,
      }));
      if (store.getState().house.nextId > idBefore) {
        setDrawSessionRoomIds(prev => [...prev, idBefore]);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // `magnetic: true` (touch only) snaps onto any existing chain vertex within a
  // 44px-diameter target radius, so closing the loop or continuing from a point
  // doesn't require pixel-precise placement on the (visually small) dot.
  const getIntersection = (e, { magnetic = false } = {}) => {
    if (!gridRef.current) return null;
    const rect = gridRef.current.getBoundingClientRect();
    const z = zoomRef.current;
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;

    if (magnetic && wallChain.length > 0) {
      const TOUCH_SNAP_PX = 22; // half of the 44px minimum touch target
      for (const pt of wallChain) {
        const ptX = pt.x * CELL * z, ptY = pt.y * CELL * z;
        if (Math.hypot(rawX - ptX, rawY - ptY) <= TOUCH_SNAP_PX) return { x: pt.x, y: pt.y };
      }
    }

    const col = Math.round(rawX / (CELL * z));
    const row = Math.round(rawY / (CELL * z));
    return { x: Math.max(0, Math.min(GRID, col)), y: Math.max(0, Math.min(GRID, row)) };
  };

  const cancelPendingWallMoveFrame = () => {
    if (wallMoveRafRef.current) {
      cancelAnimationFrame(wallMoveRafRef.current);
      wallMoveRafRef.current = null;
    }
  };

  // Coalesces rapid pointermove events (mouse hover or touch drag) to at most
  // one wallMousePos update per animation frame, keeping the live preview
  // smooth at 60fps instead of re-rendering on every raw move event.
  const scheduleWallMousePos = (pt) => {
    wallMoveLatestRef.current = pt;
    if (wallMoveRafRef.current) return;
    wallMoveRafRef.current = requestAnimationFrame(() => {
      wallMoveRafRef.current = null;
      setWallMousePos(wallMoveLatestRef.current);
    });
  };

  // Given a resolved grid point, snap it to the dominant axis from the chain's
  // last vertex (walls are always axis-aligned) — shared by hover-preview and
  // point-commit so both agree on exactly where the wall will land.
  const snapToAxis = (pt) => {
    if (wallChain.length === 0) return pt;
    const last = wallChain[wallChain.length - 1];
    if (pt.x === last.x || pt.y === last.y) return pt;
    const dx = Math.abs(pt.x - last.x);
    const dy = Math.abs(pt.y - last.y);
    return dx >= dy ? { x: pt.x, y: last.y } : { x: last.x, y: pt.y };
  };

  // The segments already placed in THIS in-progress chain (as {x1,y1,x2,y2}).
  const buildChainSegments = () => {
    const segs = [];
    for (let i = 0; i < wallChain.length - 1; i++) {
      const a = wallChain[i], b = wallChain[i + 1];
      if (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) > 0)
        segs.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    return segs;
  };

  // A wall-in-progress conflicts if it would lie on top of a committed
  // freestanding wall, any room's own boundary, OR a segment already drawn in
  // the current chain. This is the single source of truth used by BOTH the
  // live red preview and the click-to-place rejection below, so what you see
  // (red) is exactly what gets blocked.
  const segConflicts = (seg) =>
    wallConflicts(seg, wallSegments, rooms) ||
    buildChainSegments().some(s => wallSegOverlaps(seg, s));

  const commitWallPoint = (pt) => {
    if (!pt) return;

    if (wallChain.length === 0) {
      setWallChain([pt]);
      return;
    }

    const last = wallChain[wallChain.length - 1];
    const start = wallChain[0];
    const isClosing =
      Math.abs(pt.x - start.x) + Math.abs(pt.y - start.y) === 0 &&
      wallChain.length >= 3;

    if (isClosing) {
      // If the closing segment (last point → first point) is diagonal, auto-insert
      // an L-corner so the user doesn't need to manually place the 4th corner of
      // a rectangle. E.g. drawing 3 corners + close still produces a valid room.
      const lastPt = wallChain[wallChain.length - 1];
      let closedChain = [...wallChain, start];
      if (lastPt.x !== start.x && lastPt.y !== start.y) {
        closedChain = [...wallChain, { x: start.x, y: lastPt.y }, start];
      }

      const result = closedChainToRects(closedChain, GRID);

      if (result && result.rects.length > 0) {
        // Room created — the room's border shows the shape, no need for extra wall lines.
        const newRoomId = nextId;
        dispatch(placePolygonRoom({ type: 'custom', rects: result.rects }));
        disableAllModes();
        setSelectedRoomId(newRoomId);
        setSidebarTab('furniture');
      } else {
        // Polygon invalid — save drawn segments as standalone walls so they're not lost.
        let anyBlocked = false;
        for (let i = 0; i < closedChain.length - 1; i++) {
          const a = closedChain[i], b = closedChain[i + 1];
          if ((a.x === b.x || a.y === b.y) && Math.abs(a.x - b.x) + Math.abs(a.y - b.y) > 0) {
            const seg = { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
            if (!wallConflicts(seg, wallSegments, rooms)) dispatch(addWallSegment(seg));
            else anyBlocked = true;
          }
        }
        if (anyBlocked) flashWallBlocked();
        disableAllModes();
      }
    } else {
      // Snap to axis-aligned direction from last point so no diagonal segments
      // can ever be placed — any off-axis click snaps to the dominant direction.
      const snappedPt = snapToAxis(pt);
      if (Math.abs(snappedPt.x - last.x) + Math.abs(snappedPt.y - last.y) > 0) {
        // Reject the click outright if this segment would sit on top of an
        // existing wall / room boundary / the chain so far — the point never
        // enters the chain, so there's nothing to silently drop at save time.
        const seg = { x1: last.x, y1: last.y, x2: snappedPt.x, y2: snappedPt.y };
        if (segConflicts(seg)) { flashWallBlocked(); return; }
        setWallChain(prev => [...prev, snappedPt]);
      }
    }
  };

  // Unified Pointer Events handlers for the wall-mode capture overlay.
  // Mouse: unchanged hover-then-click flow (pointerdown is a no-op for mouse;
  // commit happens on pointerup, same moment `onClick` used to fire).
  // Touch: "tap and drag" — pointerdown arms the gesture and shows an
  // immediate preview at the touch point, pointermove drags the live preview
  // (coalesced to 60fps via rAF), and lifting the finger (pointerup) commits.
  const handleWallDrawPointerDown = (e) => {
    if (e.pointerType !== 'touch') return; // mouse keeps its existing hover/click behavior untouched
    if (!e.isPrimary) return;
    e.preventDefault();
    const pt = getIntersection(e, { magnetic: true });
    if (!pt) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    wallPointerDragRef.current = { pointerId: e.pointerId };
    cancelPendingWallMoveFrame();
    setWallMousePos(wallChain.length > 0 ? snapToAxis(pt) : pt);
  };

  const handleWallDrawPointerMove = (e) => {
    if (e.pointerType === 'touch' && wallPointerDragRef.current?.pointerId !== e.pointerId) return;
    const raw = getIntersection(e, { magnetic: e.pointerType === 'touch' });
    if (!raw) { setWallMousePos(null); return; }
    scheduleWallMousePos(wallChain.length > 0 ? snapToAxis(raw) : raw);
  };

  const handleWallDrawPointerUp = (e) => {
    if (e.pointerType === 'touch') {
      if (wallPointerDragRef.current?.pointerId !== e.pointerId) return;
      wallPointerDragRef.current = null;
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      cancelPendingWallMoveFrame();
      const pt = getIntersection(e, { magnetic: true });
      commitWallPoint(pt);
      return;
    }
    if (e.button !== 0) return;
    cancelPendingWallMoveFrame();
    commitWallPoint(getIntersection(e));
  };

  const handleWallDrawPointerLeave = (e) => {
    if (e.pointerType === 'touch') return; // captured touch keeps dragging even if it slides off the element
    cancelPendingWallMoveFrame();
    setWallMousePos(null);
  };

  const handleFinishChain = () => {
    if (wallChain.length >= 2) {
      // Collect proposed segments from the chain
      const proposed = [];
      for (let i = 0; i < wallChain.length - 1; i++) {
        const a = wallChain[i], b = wallChain[i + 1];
        if (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) > 0)
          proposed.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }

      // Detect enclosed areas formed by existing walls + proposed segments
      const allSegs = [...(wallSegments || []), ...proposed];
      const enclosedCells = detectEnclosedCells(rooms, allSegs, GRID);
      const enclosedSet = new Set(enclosedCells.map(([r, c]) => `${r},${c}`));
      let anyRoomCreated = false;

      if (enclosedCells.length > 0) {
        const components = groupConnectedCells(enclosedCells);
        for (const comp of components) {
          if (!comp.length) continue;
          const rows = comp.map(([r]) => r);
          const cols = comp.map(([, c]) => c);
          const minR = Math.min(...rows), maxR = Math.max(...rows);
          const minC = Math.min(...cols), maxC = Math.max(...cols);
          const w = maxC - minC + 1, h = maxR - minR + 1;

          if (comp.length === w * h) {
            // Perfect rectangle — use the simpler action
            dispatch(placeCustomRoom({ type: 'custom', x: minC, y: minR, w, h }));
          } else {
            // Irregular shape — use polygon room with individual 1×1 rects
            dispatch(placePolygonRoom({
              type: 'custom',
              rects: comp.map(([r, c]) => ({ x: c, y: r, w: 1, h: 1 })),
            }));
          }
          anyRoomCreated = true;
        }
      }

      // A segment is a boundary wall of the enclosed area if every unit-edge it
      // covers has exactly one side inside enclosedSet (XOR: one in, one out).
      const isEnclosedBoundary = (seg) => {
        if (!anyRoomCreated) return false;
        const sx1 = Math.min(seg.x1, seg.x2), sx2 = Math.max(seg.x1, seg.x2);
        const sy1 = Math.min(seg.y1, seg.y2), sy2 = Math.max(seg.y1, seg.y2);
        if (sy1 === sy2) { // horizontal: wall between rows sy1-1 and sy1
          for (let c = sx1; c < sx2; c++) {
            const above = enclosedSet.has(`${sy1 - 1},${c}`);
            const below = enclosedSet.has(`${sy1},${c}`);
            if (above === below) return false;
          }
        } else { // vertical: wall between cols sx1-1 and sx1
          for (let r = sy1; r < sy2; r++) {
            const left  = enclosedSet.has(`${r},${sx1 - 1}`);
            const right = enclosedSet.has(`${r},${sx1}`);
            if (left === right) return false;
          }
        }
        return true;
      };

      // Add proposed segments not on the enclosed boundary (they stay as walls)
      let anyBlocked = false;
      for (const seg of proposed) {
        if (isEnclosedBoundary(seg)) continue;
        if (!wallConflicts(seg, wallSegments, rooms)) dispatch(addWallSegment(seg));
        else anyBlocked = true;
      }
      if (anyBlocked) flashWallBlocked();

      // Remove existing manual wall segments now made redundant by new rooms
      if (anyRoomCreated) {
        for (const seg of (wallSegments || [])) {
          if (isEnclosedBoundary(seg)) dispatch(removeWallSegment(seg.id));
        }
      }
    }
    disableAllModes();
  };
  // "Prev" — step back one placed point (undo the last segment of the chain
  // being drawn). Empty chain has nothing to remove.
  const handleWallPrev = () => setWallChain(prev => prev.slice(0, -1));

  // "Add room" Prev/Cancel — each dragged rectangle commits to the store the
  // instant you release the mouse (there's no pending/uncommitted state like
  // the wall chain), so undoing means actually removing the room again.
  const handleDrawPrev = () => {
    setDrawSessionRoomIds(prev => {
      if (prev.length === 0) return prev;
      dispatch(removeRoom(prev[prev.length - 1]));
      return prev.slice(0, -1);
    });
  };
  const handleDrawCancel = () => {
    drawSessionRoomIds.forEach(id => dispatch(removeRoom(id)));
    disableAllModes();
  };

  const canProceed = rooms.length >= 1 || freeRooms.length >= 1;

  // Does the sidebar's contextual section have anything to show? Mirrors the
  // conditions its children render under — see the note where it is used.
  const topSectionHasContent = drawMode
    || wallMode
    || (wallSegments || []).length > 0
    || selectedWallId != null
    || selectedAutoWallKey != null
    || !!selectedWallOpeningInfo;

  return (
    <div className="builder-shell">
      {/* ── BACK, AND NOTHING ELSE ───────────────────────────────────────────
          The layout step had no way out of it. Every step after this one has
          one — the floor plan carries "← Edit Layout", the 3D view goes back to
          the plan — but the first step is where you ARRIVE, from a project list
          you can otherwise only return to through the browser's own Back.

          A strip rather than a floating button: the canvas is a drag surface
          and the corners already hold undo/redo, the wall-height pill and the
          save chip, so anything laid over it is one more thing in the way. Kept
          to a single row — this bar says one thing, unlike the floor plan's,
          which also carries a step count and a title. */}
      <header className="builder-topbar">
        <button type="button" className="builder-back-btn" onClick={goBackOut}>
          <svg viewBox="0 0 24 24" fill="none" width="15" height="15"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t('back')}
        </button>
      </header>

      <div className="builder-page">
      {/* Toast: a wall commit was blocked because it would overlap an existing
          wall or a room's own boundary — the live dashed preview already
          shows red + "⊗ overlap" for this (see wallConflicts), but a click
          that silently does nothing otherwise reads as a bug, not a block. */}
      {wallBlockedMsg && (
        <div
          role="alert"
          style={{
            position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)',
            zIndex: 500, display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 10,
            background: 'rgba(80,10,10,0.92)', border: '1px solid rgba(255,110,110,0.4)',
            color: '#ff8080', fontSize: 13, fontWeight: 600,
            fontFamily: "'DM Mono', monospace", letterSpacing: '0.2px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)', pointerEvents: 'none',
          }}
        >
          ⊗ {wallBlockedMsg}
        </div>
      )}
      {/* Toast: the two picked rooms don't share a wall, so can't be merged.
          Sits low on the screen (not at the top like the wall toast) so it
          doesn't cover the toolbar the user is working in. */}
      {mergeMsg && (
        <div
          role="alert"
          style={{
            position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)',
            zIndex: 500, display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 10,
            background: 'rgba(80,10,10,0.92)', border: '1px solid rgba(255,110,110,0.4)',
            color: '#ff8080', fontSize: 13, fontWeight: 600,
            fontFamily: "'DM Mono', monospace", letterSpacing: '0.2px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)', pointerEvents: 'none',
          }}
        >
          ⊗ {mergeMsg}
        </div>
      )}
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="builder-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      {/* ── SIDEBAR ── */}
      <div className={`builder-sidebar${sidebarOpen ? ' sidebar--open' : ''}`}>

        {/* ── CONTEXTUAL TOOLS ──────────────────────────────────────────────
            Everything in here is conditional — a drawing hint, the wall list,
            a selected wall's panel, an opening's panel — so with nothing
            selected and no mode active it renders empty. Its padding and its
            bottom rule did not, leaving a blank ruled band at the top of the
            sidebar that looked like a component that had failed to load.

            Collapsed rather than unmounted: the section takes `flex: 1` while
            a wall is selected, and tearing it down and rebuilding it on every
            selection would throw away that scroll position. The flag lists the
            same conditions its children use — if a new panel is added in here,
            add it here too. */}
        <div
          className={`draw-section draw-section--top${topSectionHasContent ? '' : ' draw-section--empty'}`}
          style={(selectedWallId != null || selectedAutoWallKey != null) ? { flex: 1, minHeight: 0, overflowY: 'auto' } : {}}
        >
          {/* On mobile/tablet, a selected opening's own detail panel (rendered
              further down, inside this same wrapper) should be the only thing
              shown — hide the tools/walls/wall-openings clutter above it. */}
          <div className={selectedWallOpeningInfo ? 'draw-section--hide-compact' : undefined}>
          {/* The Draw Room / Add Room tool toggles now live in a floating bar at
              the canvas bottom-centre (.draw-tools-floating, rendered inside
              .builder-main below) — only the contextual hints/wall UI stay here. */}

          {drawMode && (
            <p className="draw-hint">{t('draw_room_hint')}</p>
          )}

          {wallMode && (
            <div className="wall-mode-ui">
              {wallChain.length === 0 ? (
                <p className="draw-hint">{t('wall_mode_hint')}</p>
              ) : (
                <>
                  <p className="wall-chain-status">
                    {wallChain.length} point{wallChain.length !== 1 ? 's' : ''} · {wallChain.length - 1} segment{wallChain.length - 1 !== 1 ? 's' : ''}
                  </p>
                  {/* Prev / Cancel / Done live in the floating bar at the canvas
                      bottom-centre now (see .draw-tools-floating). */}
                </>
              )}
            </div>
          )}

          {/* Wall segments list with individual delete — collapsible; hidden when one wall is being edited */}
          {(wallSegments || []).length > 0 && !selectedWallId && (
            <div className="wall-segments-list" data-wall-ui="1">
              <button
                className="wall-segments-toggle"
                onClick={() => setWallListOpen(o => !o)}
              >
                <svg
                  className={`wall-toggle-chevron${wallListOpen ? ' wall-toggle-chevron--open' : ''}`}
                  width="10" height="10" viewBox="0 0 10 10" fill="none"
                >
                  <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="wall-segments-count">{t('walls')}</span>
                <span className="wall-segments-badge">{(wallSegments || []).length}</span>
                <button
                  className="wall-clear-btn wall-clear-btn--inline"
                  onClick={e => { e.stopPropagation(); dispatch(clearWallSegments()); setSelectedWallId(null); }}
                >
                  {t('clear_all')}
                </button>
              </button>

              {wallListOpen && (
                <div className="wall-segments-body">
                  {(wallSegments || []).map((w, i) => {
                    const isH = w.y1 === w.y2;
                    const len = isH ? Math.abs(w.x2 - w.x1) : Math.abs(w.y2 - w.y1);
                    const isSelected = selectedWallId === w.id;
                    return (
                      <div
                        key={w.id}
                        className={`wall-segment-item${isSelected ? ' wall-segment-item--selected' : ''}`}
                        onClick={() => (isSelected ? setSelectedWallId(null) : selectWallSeg(w.id))}
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="wall-seg-icon">
                          {isH
                            ? <line x1="1" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            : <line x1="7" y1="1" x2="7" y2="13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>}
                        </svg>
                        <span className="wall-seg-label">Wall {i + 1} · {len}m {isH ? 'H' : 'V'}</span>
                        <button
                          className="wall-seg-delete"
                          title="Delete this wall"
                          onClick={e => { e.stopPropagation(); dispatch(removeWallSegment(w.id)); if (selectedWallId === w.id) setSelectedWallId(null); }}
                        >
                          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                            <path d="M1.5 1.5L9.5 9.5M9.5 1.5L1.5 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Wall editing floats on the right of the canvas (like the room
              panel), not cramped in the sidebar. On mobile it stays in the
              sheet flow (see .builder-wall-float CSS). */}
          {(selectedWallId != null || selectedAutoWallKey != null) && (
          <div className="builder-wall-float">
          {/* ── WALL OPENINGS TOOL ── always visible when any walls exist */}
          {(selectedWallId != null || selectedAutoWallKey != null) && (
            <div className="wall-openings-section" data-wall-ui="1">
              <span className="draw-section-label">{t('wall_openings')}</span>
              <div className="opening-btns" style={{ gap: 5 }}>
                <button
                  className={`opening-btn${wallOpeningTool === 'door' ? ' opening-btn--active' : ''}`}
                  onTouchEnd={e => { if (selectedWallId != null) { e.preventDefault(); e.stopPropagation(); handleQuickAddOpening('door'); } else if (selectedAutoWallKey) { e.preventDefault(); e.stopPropagation(); handleQuickAddOpeningAuto('door'); } }}
                  onClick={() => { if (selectedWallId != null) { handleQuickAddOpening('door'); return; } if (selectedAutoWallKey) { handleQuickAddOpeningAuto('door'); return; } if (wallMode && wallChain.length >= 2) handleFinishChain(); else disableAllModes(); setWallOpeningTool(wallOpeningTool === 'door' ? null : 'door'); setSidebarOpen(false); }}
                >
                  <DoorIcon size={13}/> <Tx k="door" />
                </button>
                <button
                  className={`opening-btn${wallOpeningTool === 'window' ? ' opening-btn--active' : ''}`}
                  onTouchEnd={e => { if (selectedWallId != null) { e.preventDefault(); e.stopPropagation(); handleQuickAddOpening('window'); } else if (selectedAutoWallKey) { e.preventDefault(); e.stopPropagation(); handleQuickAddOpeningAuto('window'); } }}
                  onClick={() => { if (selectedWallId != null) { handleQuickAddOpening('window'); return; } if (selectedAutoWallKey) { handleQuickAddOpeningAuto('window'); return; } if (wallMode && wallChain.length >= 2) handleFinishChain(); else disableAllModes(); setWallOpeningTool(wallOpeningTool === 'window' ? null : 'window'); setSidebarOpen(false); }}
                >
                  <WindowIcon size={13}/> <Tx k="window" />
                </button>
                <button
                  className={`opening-btn${wallOpeningTool === 'archway' ? ' opening-btn--active' : ''}`}
                  onTouchEnd={e => { if (selectedWallId != null) { e.preventDefault(); e.stopPropagation(); handleQuickAddOpening('archway'); } else if (selectedAutoWallKey) { e.preventDefault(); e.stopPropagation(); handleQuickAddOpeningAuto('archway'); } }}
                  onClick={() => { if (selectedWallId != null) { handleQuickAddOpening('archway'); return; } if (selectedAutoWallKey) { handleQuickAddOpeningAuto('archway'); return; } if (wallMode && wallChain.length >= 2) handleFinishChain(); else disableAllModes(); setWallOpeningTool(wallOpeningTool === 'archway' ? null : 'archway'); setSidebarOpen(false); }}
                >
                  <ArchIcon size={13}/> <Tx k="arch" />
                </button>
              </div>
              {wallOpeningTool ? (
                <>
                  <p className="opening-hint" style={{ marginBottom: 6 }}>
                    {t('place_on_wall_hint')}
                  </p>
                  <div className="opening-size-config">
                    <div className="opening-size-row">
                      <span className="opening-size-lbl">{t('room_width')}</span>
                      <button className="opening-size-btn"
                        onClick={() => setWallOpeningSize(p => ({ ...p, [wallOpeningTool]: { ...p[wallOpeningTool], width: +(Math.max(0.4, p[wallOpeningTool].width - 0.1)).toFixed(2) } }))}>−</button>
                      <span className="opening-size-val">{wallOpeningSize[wallOpeningTool]?.width?.toFixed(2)}m</span>
                      <button className="opening-size-btn"
                        onClick={() => setWallOpeningSize(p => ({ ...p, [wallOpeningTool]: { ...p[wallOpeningTool], width: +(Math.min(3.0, p[wallOpeningTool].width + 0.1)).toFixed(2) } }))}>+</button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Wall edit panel — shown when a wall is selected */}
          {(() => {
            const selWall = selectedWallId != null ? (wallSegments || []).find(w => w.id === selectedWallId) : null;
            if (!selWall) return null;
            const isH = selWall.y1 === selWall.y2;
            const segLen = isH ? Math.abs(selWall.x2 - selWall.x1) : Math.abs(selWall.y2 - selWall.y1);
            const segIdx = (wallSegments || []).findIndex(w => w.id === selectedWallId);
            return (
              <div className="wall-edit-panel" data-wall-ui="1">
                <div className="wall-edit-header">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="wall-edit-icon">
                    {isH
                      ? <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      : <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>}
                  </svg>
                  <span className="wall-edit-title">Wall {segIdx + 1}</span>
                  <span className="wall-edit-dim">{segLen}m · {isH ? 'horizontal' : 'vertical'}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {/* Move */}
                  <button
                    className={`wall-edit-action-btn${wallTouchMoving === selWall.id ? ' wall-edit-action-btn--active' : ''}`}
                    style={{ width: '100%', justifyContent: 'center', gap: 6 }}
                    onClick={() => {
                      if (wallTouchMoving === selWall.id) {
                        wallTouchMovingRef.current = null;
                        setWallTouchMoving(null);
                      } else {
                        wallTouchMovingRef.current = selWall.id;
                        setWallTouchMoving(selWall.id);
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 2L5.5 4.5M8 2L10.5 4.5M8 14L5.5 11.5M8 14L10.5 11.5M2 8L4.5 5.5M2 8L4.5 10.5M14 8L11.5 5.5M14 8L11.5 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    Move
                  </button>

                  {/* Rotate */}
                  <button
                    className="wall-edit-action-btn"
                    style={{ width: '100%', justifyContent: 'center', gap: 6 }}
                    onClick={() => handleRotateWall(selWall)}
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8C3 5.24 5.24 3 8 3a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M13 3v4h-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Rotate 90°
                  </button>

                  {/* Delete */}
                  <button
                    className="wall-delete-btn"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => { dispatch(removeWallSegment(selectedWallId)); setSelectedWallId(null); }}
                  >
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                      <path d="M1.5 1.5L9.5 9.5M9.5 1.5L1.5 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                    Delete
                  </button>
                </div>

                {/* Openings on this wall — tap to select & move */}
                {selWall.openings && selWall.openings.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--t3,#505468)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                      Openings
                    </div>
                    {selWall.openings.map(op => {
                      const meta = openingTypeMeta(op.type, t);
                      return (
                      <div key={op.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', cursor: 'pointer', borderBottom: '1px solid var(--border,rgba(0,0,0,0.07))', userSelect: 'none' }}
                        onTouchEnd={e => { e.preventDefault(); e.stopPropagation(); setSelectedWallOpeningInfo({ wallId: selWall.id, openingId: op.id }); setSelectedWallId(null); setSidebarOpen(false); }}
                        onClick={() => { setSelectedWallOpeningInfo({ wallId: selWall.id, openingId: op.id }); setSelectedWallId(null); setSidebarOpen(false); }}
                      >
                        <span style={{ fontSize: 14 }}>{meta.emoji}</span>
                        <span style={{ fontSize: 11, flex: 1 }}>
                          {meta.label} · {op.width.toFixed(2)}m
                        </span>
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path d="M2 1.5L5.5 4L2 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Auto-wall openings panel — click a room wall to see/delete its openings */}
          {(() => {
            if (!selectedAutoWallKey) return null;
            const selAW = autoWalls.find(w => w.key === selectedAutoWallKey);
            if (!selAW) return null;
            const isH = selAW.y1 === selAW.y2;
            const len = isH ? (selAW.x2 - selAW.x1) : (selAW.y2 - selAW.y1);
            const awOpenings = (autoWallOpenings || {})[selectedAutoWallKey] || [];
            // Also list room.openings on this wall (e.g. placed via the 3D room
            // view's "Add Opening" modal) — previously only cut a gap here, with
            // no way to select/move/delete them from the 2D Builder at all.
            const roomOpsOnWall = getRoomOpeningsOnWall(rooms, selAW, awOpenings);
            const allOpsOnWall = [
              ...awOpenings.map(op => ({ op, sel: { autoKey: selectedAutoWallKey, openingId: op.id } })),
              ...roomOpsOnWall.map(op => ({ op, sel: { roomId: op.roomId, openingId: op.id } })),
            ];
            return (
              <div className="wall-edit-panel" data-wall-ui="1">
                <div className="wall-edit-header">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="wall-edit-icon">
                    {isH
                      ? <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      : <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>}
                  </svg>
                  <span className="wall-edit-title">{t('room_wall')}</span>
                  <span className="wall-edit-dim">{len}m · {isH ? t('horizontal') : t('vertical')}</span>
                  <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: 14, lineHeight: 1, padding: 2 }}
                    onClick={() => setSelectedAutoWallKey(null)}>×</button>
                </div>
                {allOpsOnWall.length === 0
                  ? <p className="opening-hint">{t('no_openings_hint')}</p>
                  : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                      {allOpsOnWall.map(({ op, sel }) => {
                        const meta = openingTypeMeta(op.type, t);
                        return (
                        <div key={op.id} className="wall-segment-item"
                          style={{ cursor: 'pointer' }}
                          onTouchEnd={e => { e.preventDefault(); e.stopPropagation(); setSelectedWallOpeningInfo(sel); setSelectedAutoWallKey(null); setSidebarOpen(false); }}
                          onClick={() => { setSelectedWallOpeningInfo(sel); setSelectedAutoWallKey(null); setSidebarOpen(false); }}
                        >
                          <span className="wall-seg-label">
                            {meta.emoji} {meta.label} · {op.width.toFixed(2)}m
                          </span>
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M2 1.5L5.5 4L2 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        );
                      })}
                    </div>
                  )
                }
              </div>
            );
          })()}
          </div>
          )}
          </div>

          {/* ── OPENING STYLE PANEL ── shown when an opening is clicked */}
          {selectedWallOpeningInfo && (
          <div className="builder-wall-float">
          {selectedWallOpeningInfo && (() => {
            const isAutoWall = !!selectedWallOpeningInfo.autoKey;
            const isRoomOpening = !!selectedWallOpeningInfo.roomId;
            let opening = null;
            if (isAutoWall) {
              opening = ((autoWallOpenings || {})[selectedWallOpeningInfo.autoKey] || [])
                .find(o => o.id === selectedWallOpeningInfo.openingId);
            } else if (isRoomOpening) {
              const room = rooms.find(r => r.id === selectedWallOpeningInfo.roomId);
              opening = (room?.openings || []).find(o => o.id === selectedWallOpeningInfo.openingId);
            } else {
              const wall = selectedWallOpeningInfo.freeWallId != null
                ? (freeWalls || []).find(w => w.id === selectedWallOpeningInfo.freeWallId)
                : (wallSegments || []).find(w => w.id === selectedWallOpeningInfo.wallId);
              opening = (wall?.openings || []).find(o => o.id === selectedWallOpeningInfo.openingId);
            }
            if (!opening) return null;

            const upd = (changes) => {
              if (isAutoWall) return dispatch(updateAutoWallOpening({ key: selectedWallOpeningInfo.autoKey, openingId: opening.id, changes }));
              if (isRoomOpening) {
                return changes.color !== undefined
                  ? dispatch(recolorOpening({ roomId: selectedWallOpeningInfo.roomId, openingId: opening.id, color: changes.color }))
                  : dispatch(resizeOpening({ roomId: selectedWallOpeningInfo.roomId, openingId: opening.id, ...changes }));
              }
              if (selectedWallOpeningInfo.freeWallId != null) return dispatch(updateFreeWallOpening({ wallId: selectedWallOpeningInfo.freeWallId, openingId: opening.id, changes }));
              return dispatch(updateWallSegOpening({ wallId: selectedWallOpeningInfo.wallId, openingId: opening.id, changes }));
            };

            const del = () => {
              if (isAutoWall) dispatch(removeAutoWallOpening({ key: selectedWallOpeningInfo.autoKey, openingId: opening.id }));
              else if (isRoomOpening) dispatch(removeOpening({ roomId: selectedWallOpeningInfo.roomId, openingId: opening.id }));
              else if (selectedWallOpeningInfo.freeWallId != null) dispatch(removeFreeWallOpening({ wallId: selectedWallOpeningInfo.freeWallId, openingId: opening.id }));
              else dispatch(removeWallSegOpening({ wallId: selectedWallOpeningInfo.wallId, openingId: opening.id }));
              setSelectedWallOpeningInfo(null);
            };

            const { emoji: typeEmoji, label: typeLabel } = openingTypeMeta(opening.type, t);
            const COLORS = OPENING_TYPE_COLOR_PRESETS[opening.type] || OPENING_TYPE_COLOR_PRESETS.window;
            const hasSill = opening.type === 'window' || isWallDecorType(opening.type);

            const { width: defW, height: defH } = openingDefaultSize(opening.type);
            const w = opening.width || defW;
            const h = opening.height || defH;
            const sill = opening.sill ?? (WALL_DECOR_DEFAULT_SILL[opening.type] ?? 0.9);

            return (
              <div className="wall-edit-panel" data-wall-ui="1" style={{ marginTop: 8 }}>
                <div className="wall-edit-header">
                  <span style={{ fontSize: 15, lineHeight: 1 }}>{typeEmoji}</span>
                  <span className="wall-edit-title">{typeLabel}</span>
                  <button
                    style={{
                      marginLeft: 'auto', background: openingMoveArmed ? 'rgba(212,144,58,0.18)' : 'none',
                      border: openingMoveArmed ? '1px solid rgba(212,144,58,0.4)' : '1px solid transparent',
                      borderRadius: 6, cursor: 'pointer', color: openingMoveArmed ? 'var(--gold, #d4903a)' : 'inherit',
                      opacity: openingMoveArmed ? 1 : 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 24, height: 24, padding: 0,
                    }}
                    title={openingMoveArmed ? 'Drag on the canvas to move — click to cancel' : 'Click, then drag on the canvas to move'}
                    onClick={() => {
                      const next = !openingMoveArmed;
                      setOpeningMoveArmed(next);
                      // On mobile the sidebar is a bottom sheet that shares the drag
                      // overlay's z-index and sits on top of it — close it so the
                      // canvas (and the overlay covering it) is actually reachable.
                      if (next) setSidebarOpen(false);
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1v12M1 7h12M7 1L4.5 3.5M7 1l2.5 2.5M7 13l-2.5-2.5M7 13l2.5-2.5M1 7l2.5-2.5M1 7l2.5 2.5M13 7l-2.5-2.5M13 7l-2.5 2.5"
                        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    style={{
                      background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer',
                      opacity: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0,
                    }}
                    title="Copy (Ctrl+C)"
                    onClick={handleCopySelected}
                  >
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <rect x="4.5" y="4.5" width="8" height="8" rx="1.3" stroke="currentColor" strokeWidth="1.3"/>
                      <path d="M2.5 9.5V2.8A1.3 1.3 0 0 1 3.8 1.5h6.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: 14, lineHeight: 1, padding: 2 }}
                    onClick={() => setSelectedWallOpeningInfo(null)}>×</button>
                </div>

                <div className="opening-style-body">
                  {/* Width */}
                  <div className="opening-size-row">
                    <span className="opening-size-lbl">{t('room_width')}</span>
                    <button className="opening-size-btn" onClick={() => upd({ width: +(Math.max(0.4, w - 0.1)).toFixed(2) })}>−</button>
                    <span className="opening-size-val">{w.toFixed(2)}m</span>
                    <button className="opening-size-btn" onClick={() => upd({ width: +(Math.min(3.0, w + 0.1)).toFixed(2) })}>+</button>
                  </div>

                  {/* Height */}
                  <div className="opening-size-row">
                    <span className="opening-size-lbl">{t('room_height')}</span>
                    <button className="opening-size-btn" onClick={() => upd({ height: +(Math.max(0.5, h - 0.1)).toFixed(2) })}>−</button>
                    <span className="opening-size-val">{h.toFixed(2)}m</span>
                    <button className="opening-size-btn" onClick={() => upd({ height: +(Math.min(4.0, h + 0.1)).toFixed(2) })}>+</button>
                  </div>

                  {/* Sill — elevation from floor; windows + wall-decor items */}
                  {hasSill && (
                    <div className="opening-size-row">
                      <span className="opening-size-lbl">{t('sill')}</span>
                      <button className="opening-size-btn" onClick={() => upd({ sill: +(Math.max(0.0, sill - 0.1)).toFixed(2) })}>−</button>
                      <span className="opening-size-val">{sill.toFixed(2)}m</span>
                      <button className="opening-size-btn" onClick={() => upd({ sill: +(Math.min(2.8, sill + 0.1)).toFixed(2) })}>+</button>
                    </div>
                  )}

                  {/* Color */}
                  <div className="opening-color-row">
                    <span className="opening-size-lbl">{t('color_label')}</span>
                    <div className="opening-color-swatches">
                      {COLORS.map(col => (
                        <button key={col} title={col}
                          className={`opening-color-swatch${opening.color === col ? ' opening-color-swatch--active' : ''}`}
                          style={{ background: col }}
                          onClick={() => upd({ color: col })}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Remove */}
                  <button className="opening-btn opening-btn--danger" style={{ marginTop: 4, width: '100%' }} onClick={del}>
                    {t('remove')}
                  </button>
                </div>
              </div>
            );
          })()}
          </div>
          )}
        </div>

        {/* Openings block — always at the top (door/window/archway). Hidden
            while editing a wall / an opening's own detail panel. */}
        {!selectedWallId && (
          <div className={`builder-openings-top${selectedWallOpeningInfo ? ' draw-section--hide-compact' : ''}`}>
            <span className="sidebar-eyebrow">{t('openings_label')}</span>
            <div className="furniture-palette">
              {[
                { type: 'door',    label: t('door'),    icon: <DoorIcon size={16} /> },
                { type: 'window',  label: t('window'),  icon: <WindowIcon size={16} /> },
                { type: 'archway', label: t('archway'), icon: <ArchIcon size={16} /> },
              ].map(o => (
                <div
                  key={o.type}
                  className={`furniture-palette-item${wallOpeningTool === o.type ? ' furniture-palette-item--active' : ''}`}
                  onClick={() => {
                    disableAllModes();
                    setSelectedWallOpeningInfo(null);
                    setWallOpeningTool(wallOpeningTool === o.type ? null : o.type);
                    setSidebarOpen(false);
                  }}
                >
                  <span className="ftype-icon">{o.icon}</span>
                  <span className="ftype-label">{o.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs — hidden while a wall is being edited, or (mobile/tablet) while
            an opening's own detail panel is the thing being shown */}
        {!selectedWallId && (
        <select
          className={`sidebar-tabs-select${selectedWallOpeningInfo ? ' draw-section--hide-compact' : ''}`}
          value={sidebarTab}
          onChange={e => setSidebarTab(e.target.value)}
        >
          <option value="furniture">{t('furniture_tab')}</option>
          <option value="wallitems">{t('wall_items_label')}</option>
        </select>
        )}

        <div
          className={`builder-sidebar-top${selectedWallOpeningInfo ? ' draw-section--hide-compact' : ''}`}
          style={(selectedWallId != null || selectedAutoWallKey != null) ? { display: 'none' } : {}}
        >

          {/* ── FURNITURE PALETTE ── */}
          {sidebarTab === 'furniture' && !selectedWallId && (
            <>
              <span className="sidebar-eyebrow">{t('drag_hint_furniture')}</span>
              <div className="furniture-palette">
                {FURNITURE_TYPES.map(ft => (
                  <div
                    key={ft.type}
                    className="furniture-palette-item"
                    draggable
                    onDragStart={() => dispatch(setDragFurnitureType(ft.type))}
                    onDragEnd={() => dispatch(setDragFurnitureType(null))}
                    onClick={() => {
                      const x = Math.round((GRID * CELL - ft.w) / 2);
                      const y = Math.round((GRID * CELL - ft.h) / 2);
                      const newFurnId = nextFurnitureId;
                      dispatch(placeFurniture({ type: ft.type, x, y }));
                      setSelectedFurnitureId(newFurnId);
                      setSelectedRoomId(null);
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="ftype-icon"><FurniturePaletteIcon type={ft.type} size={16} /></span>
                    <span className="ftype-label">{t('furn_' + ft.type)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── WALL ITEMS PALETTE (painting/AC/clock/outlet/radiator — mount on
              the wall face, don't cut a void) ── */}
          {sidebarTab === 'wallitems' && !selectedWallId && (
            <>
              <span className="sidebar-eyebrow">{t('wall_items_hint')}</span>
              <div className="furniture-palette">
                {WALL_DECOR_TYPES.map(dt => ({
                  type: dt, label: t(dt), icon: React.createElement(WALL_DECOR_ICON_CMP[dt], { size: 16 }),
                })).map(o => (
                  <div
                    key={o.type}
                    className={`furniture-palette-item${wallOpeningTool === o.type ? ' furniture-palette-item--active' : ''}`}
                    onClick={() => {
                      disableAllModes();
                      setSelectedWallOpeningInfo(null);
                      setWallOpeningTool(wallOpeningTool === o.type ? null : o.type);
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="ftype-icon">{o.icon}</span>
                    <span className="ftype-label">{o.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}

        </div>

        <div className="builder-sidebar-bottom">
          <div className="builder-stats">
            <span>{rooms.length} {rooms.length !== 1 ? t('rooms_tab').toLowerCase() : t('room_custom').toLowerCase()} · {furniture.length} {t('furniture_tab').toLowerCase()}</span>
            {(rooms.length > 0 || furniture.length > 0 || wallSegments.length > 0) && (
              <button className="clear-btn" onClick={() => { dispatch(clearAllRooms()); handleDeselect(); }}>
                {t('clear_all')}
              </button>
            )}
          </div>
          {/* On mobile/tablet this same action lives in the floating card next
              to the save status, so it isn't duplicated down here. */}
          <button
            className={`proceed-btn bcc-desktop-only ${canProceed ? 'active' : 'disabled'}`}
            onClick={() => canProceed && confirmIfDirty(() => dispatch(goToPhase('plan')))}
            disabled={!canProceed}
          >
            <Tx k={canProceed ? 'view_floor_plan' : 'add_room_first'} />
          </button>
        </div>
      </div>

      {/* ── GRID ── */}
      {/* touch-action:none on just the tiny opening marker isn't reliably enough
          to stop this container's own native touch-scroll from taking over the
          gesture first — lock panning here too while a drag is in progress. */}
      <div
        className={`builder-main${openingMoveArmed ? ' builder-main--drag-lock' : ''}`}
        ref={mainRef}
        onClick={handleDeselect}
      >

        {/* Floating room tools — bottom-centre of the canvas.
            "Draw room" = the wall-chain tool (draw a room by its walls);
            "Add room"  = drag a rectangle. (Per the requested rename: the old
            "Draw walls" tool is now "Draw room", and the old rectangle "Draw
            room" tool is now "Add room".) */}
        {/* Selected free-form WALL panel: add an opening to that wall, or edit /
            remove the openings already on it. */}
        {selectedFreeEdge && (() => {
          const room = rooms.find(r => r.id === selectedFreeEdge.roomId);
          if (!room?.pts) return null;
          const edge = selectedFreeEdge.edge;
          const a = room.pts[edge], b = room.pts[(edge + 1) % room.pts.length];
          const lenM = a && b ? edgeLength(a, b) : 0;
          const ops = (room.openings || []).filter(o => o.edge === edge);
          const add = (type) => {
            const size = openingSize[type] || {};
            // Drop it in the first free spot along the wall.
            const used = ops.map(o => o.t).sort((x, y) => x - y);
            let t = 0.5;
            for (const cand of [0.5, 0.25, 0.75, 0.15, 0.85]) {
              if (!used.some(u => Math.abs(u - cand) < 0.12)) { t = cand; break; }
            }
            dispatch(addOpening({ roomId: room.id, type, wall: 'top', pos: t, edge, t, ...size }));
          };
          // Same icon buttons the straight interior wall uses, so every wall's
          // opening row reads identically.
          const btn = (type, icon, label) => (
            <button key={type} className="opening-btn" onClick={() => add(type)}>{icon} {label}</button>
          );
          // Same markup/classes as the normal room-wall panel, so a free-form
          // wall edits exactly like a straight one.
          return (
            <div className="wall-edit-panel" data-wall-ui="1"
              onClick={e => e.stopPropagation()}
              /* .wall-edit-panel is styled for the dark sidebar (near-transparent
                 background), so floating it over the light canvas needs its own
                 solid surface or it renders invisible. */
              style={{
                position: 'fixed', top: 'calc(var(--header-h, 56px) + 56px)', right: 24,
                zIndex: 600, width: 250,
                margin: 0, background: 'rgba(26,28,38,0.97)', color: '#fff',
                borderRadius: 12, padding: 12,
                boxShadow: '0 10px 34px rgba(0,0,0,0.5)',
              }}>
              <div className="wall-edit-header">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="wall-edit-icon">
                  {a?.bulge
                    ? <path d="M0 9 Q6 0 12 9" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                    : <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>}
                </svg>
                <span className="wall-edit-title">{t('room_wall')}</span>
                <span className="wall-edit-dim">{lenM.toFixed(2)}m · {a?.bulge ? t('free_wall') : `#${edge + 1}`}</span>
                <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: 14, lineHeight: 1, padding: 2 }}
                  onClick={() => setSelectedFreeEdge(null)}>×</button>
              </div>
              {/* Shape: bow this wall, or straighten it back. Same move-to-bow /
                  click-to-place step the free-wall tool uses, so a room's wall
                  curves exactly like a wall being drawn.
                  Only for a FREE wall — one already bowed, or drawn at an angle.
                  A plain axis-aligned wall is an ordinary straight wall and gets
                  no shape controls (the header labels it "#n", not "free wall"). */}
              {(!!a?.bulge || (a && b && Math.abs(a.x - b.x) > 0.01 && Math.abs(a.y - b.y) > 0.01)) && (() => {
                const curving = freeWallDraft?.editRoom?.roomId === room.id && freeWallDraft?.editRoom?.edge === edge;
                const shapeBtn = (label, icon, on, active) => (
                  <button onClick={on} style={{
                    flex: 1, padding: '7px 4px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    background: active ? '#3f7fc4' : 'rgba(255,255,255,0.08)', color: '#fff',
                    border: `1px solid ${active ? '#5096dc' : 'rgba(255,255,255,0.16)'}`,
                  }}>{icon}{label}</button>
                );
                return (
                  <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                    {curving
                      ? shapeBtn(t('cancel'),
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
                          () => { setFreeWallDraft(null); setFreeWallCursor(null); })
                      : shapeBtn(t('curve_btn'),
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M0.5 9.5 Q6 0.5 11.5 9.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>,
                          () => {
                            setFreeWallDraft({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, bulge: a.bulge || 0, stage: 'bulge', editRoom: { roomId: room.id, edge } });
                            setFreeWallCursor(null);
                          }, true)}
                    {shapeBtn(t('straight_btn'),
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><line x1="0.5" y1="6" x2="11.5" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
                      () => {
                        dispatch(setRoomEdgeBulge({ roomId: room.id, edge, bulge: 0 }));
                        setFreeWallDraft(null); setFreeWallCursor(null);
                      })}
                  </div>
                );
              })()}
              {/* Quick-add, mirroring the sidebar's Wall Openings row */}
              <span className="draw-section-label" style={{ marginTop: 10, display: 'block' }}>{t('wall_openings')}</span>
              <div className="opening-btns" style={{ gap: 5 }}>
                {btn('door', <DoorIcon size={13} />, t('door'))}
                {btn('window', <WindowIcon size={13} />, t('window'))}
                {btn('archway', <ArchIcon size={13} />, t('arch'))}
              </div>
              {ops.length === 0
                ? <p className="opening-hint">{t('no_openings_hint')}</p>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                    {ops.map(o => {
                      const meta = openingTypeMeta(o.type, t);
                      return (
                        <div key={o.id} className="wall-segment-item" style={{ cursor: 'pointer' }}
                          onClick={() => { setSelectedOpeningId(o.id); setSelectedRoomId(room.id); setSelectedFreeEdge(null); setSidebarOpen(false); }}>
                          <span className="wall-seg-label">
                            {meta.emoji} {meta.label} · {(o.width || 0.88).toFixed(2)}m
                          </span>
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M2 1.5L5.5 4L2 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      );
                    })}
                  </div>
                )
              }
            </div>
          );
        })()}

        {/* Free-wall edit panel — shown when a placed free wall is selected.
            "Curve" re-arms the same bowing step the tool uses while drawing, so
            a wall can be bent (or straightened) after the fact instead of having
            to be deleted and redrawn. */}
        {selectedFreeWallId != null && (() => {
          const w = freeWalls.find(f => f.id === selectedFreeWallId);
          if (!w) return null;
          const curving = freeWallDraft?.editId === w.id;
          const lenM = edgeLength({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 });
          const act = (label, on, tone) => (
            <button onClick={on} style={{
              flex: 1, padding: '7px 4px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: tone === 'on' ? '#3f7fc4' : 'rgba(255,255,255,0.08)',
              color: '#fff', border: `1px solid ${tone === 'on' ? '#5096dc' : 'rgba(255,255,255,0.16)'}`,
            }}>{label}</button>
          );
          return (
            <div onClick={e => e.stopPropagation()} style={{
              position: 'fixed', top: 'calc(var(--header-h, 56px) + 56px)', right: 24,
              zIndex: 599, width: 208,
              background: 'rgba(28,30,40,0.96)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 12, padding: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
            }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Free wall</span>
                <button onClick={() => { dispatch(removeFreeWall(w.id)); setSelectedFreeWallId(null); setFreeWallDraft(null); }}
                  style={{ background: 'none', border: 'none', color: '#ff7676', cursor: 'pointer', fontSize: 12, padding: 0 }}>Delete</button>
              </div>
              <div style={{ color: '#9aa3b2', fontSize: 11, marginBottom: 8 }}>
                {lenM.toFixed(2)}m{w.bulge ? ` · curve ${Math.abs(w.bulge).toFixed(2)}m` : ''}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {curving
                  ? act('Cancel', () => { setFreeWallDraft(null); setFreeWallCursor(null); })
                  : act('Curve', () => {
                      // Same 'bulge' step as drawing: move to bow it, click to place.
                      setFreeWallDraft({ x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2, bulge: w.bulge || 0, stage: 'bulge', editId: w.id });
                      setFreeWallCursor(null);
                    }, 'on')}
                {act('Straight', () => {
                  dispatch(updateFreeWall({ id: w.id, bulge: 0 }));
                  setFreeWallDraft(null); setFreeWallCursor(null);
                })}
              </div>
              {curving && (
                <div style={{ color: '#9aa3b2', fontSize: 11, marginTop: 8 }}>
                  Move to bow the wall, click to place.
                </div>
              )}

              {/* Openings — same quick-add + list a straight interior wall has,
                  so a free wall takes doors and windows the same way. Position is
                  `t` along the wall, dropped in the first free spot. */}
              {(() => {
                const ops = w.openings || [];
                const add = (type) => {
                  const size = wallOpeningSize[type] || {};
                  const used = ops.map(o => o.t);
                  let t = 0.5;
                  for (const cand of [0.5, 0.25, 0.75, 0.15, 0.85]) {
                    if (!used.some(u => Math.abs(u - cand) < 0.12)) { t = cand; break; }
                  }
                  dispatch(addFreeWallOpening({ wallId: w.id, type, t, ...size }));
                };
                // Same markup/classes as the straight interior wall's quick-add
                // row, so the two panels read identically.
                const oBtn = (type, icon, label) => (
                  <button key={type} className="opening-btn" onClick={() => add(type)}>
                    {icon} {label}
                  </button>
                );
                return (
                  <>
                    <span className="draw-section-label" style={{ marginTop: 10, display: 'block' }}>{t('wall_openings')}</span>
                    <div className="opening-btns" style={{ gap: 5 }}>
                      {oBtn('door', <DoorIcon size={13} />, t('door'))}
                      {oBtn('window', <WindowIcon size={13} />, t('window'))}
                      {oBtn('archway', <ArchIcon size={13} />, t('arch'))}
                    </div>
                    {ops.length === 0
                      ? <p className="opening-hint">{t('no_openings_hint')}</p>
                      : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                          {ops.map(o => {
                            const meta = openingTypeMeta(o.type, t);
                            return (
                              <div key={o.id} className="wall-segment-item" style={{ cursor: 'pointer' }}
                                onClick={() => { setSelectedWallOpeningInfo({ freeWallId: w.id, openingId: o.id }); setSelectedFreeWallId(null); setSidebarOpen(false); }}>
                                <span className="wall-seg-label">
                                  {meta.emoji} {meta.label} · {(o.width || 0.88).toFixed(2)}m
                                </span>
                                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                  <path d="M2 1.5L5.5 4L2 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            );
                          })}
                        </div>
                      )
                    }
                  </>
                );
              })()}
            </div>
          );
        })()}

        {/* Free-room material picker (Phase 1: design). Shown when a free-form
            room is selected. */}
        {selectedFreeRoomId != null && (() => {
          const r = freeRooms.find(f => f.id === selectedFreeRoomId);
          if (!r) return null;
          const sw = (m, bg, active, on) => (
            <button key={m.id} title={m.label} onClick={on}
              style={{ width: 22, height: 22, borderRadius: 5, background: bg, border: active ? '2px solid #5096dc' : '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', padding: 0 }} />
          );
          return (
            <div onClick={e => e.stopPropagation()} style={{
              position: 'fixed', top: 'calc(var(--header-h, 56px) + 56px)', right: 24,
              zIndex: 599, width: 208,
              background: 'rgba(28,30,40,0.96)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 12, padding: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
            }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Free room</span>
                <button onClick={() => { dispatch(removeFreeRoom(r.id)); setSelectedFreeRoomId(null); }}
                  style={{ background: 'none', border: 'none', color: '#ff7676', cursor: 'pointer', fontSize: 12, padding: 0 }}>Delete</button>
              </div>
              <div style={{ color: '#9aa3b2', fontSize: 11, marginBottom: 5 }}>Floor</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                {FLOOR_MATERIALS.slice(0, 14).map(m => sw(m, m.baseColor, r.floor === m.id, () => dispatch(setFreeRoomDesign({ id: r.id, floor: m.id }))))}
              </div>
              <div style={{ color: '#9aa3b2', fontSize: 11, marginBottom: 5 }}>Walls</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {WALL_MATERIALS.slice(0, 14).map(m => sw(m, m.color, r.wall === m.id, () => dispatch(setFreeRoomDesign({ id: r.id, wall: m.id }))))}
              </div>
            </div>
          );
        })()}

        <div className="draw-tools-floating" onClick={e => e.stopPropagation()}>
          {wallMode ? (
            /* Drawing a room (wall chain) — Prev / Cancel / Done controls. */
            <>
              <button
                className="dtf-action"
                onClick={handleWallPrev}
                disabled={wallChain.length === 0}
                title="Undo the last point"
              >
                <Tx k="prev" />
              </button>
              <button
                className="dtf-action dtf-action--cancel"
                onClick={disableAllModes}
                title="Discard and stop drawing"
              >
                <Tx k="cancel" />
              </button>
              <button
                className="dtf-action dtf-action--done"
                onClick={handleFinishChain}
                disabled={wallChain.length < 2}
                title="Finish the room"
              >
                <Tx k="done" />
              </button>
            </>
          ) : freeWallMode ? (
            /* Drawing free walls — same Prev / Cancel / Done shape as the other
               tools. Done turns a closed outline into a room, which is what the
               old "Make Room" button did. */
            <>
              <button
                className="dtf-action"
                onClick={handleFreeWallPrev}
                disabled={!freeWallDraft && freeWallSessionIds.length === 0}
                title="Undo the last wall"
              >
                <Tx k="prev" />
              </button>
              <button
                className="dtf-action dtf-action--cancel"
                onClick={handleFreeWallCancel}
                title="Discard and stop drawing"
              >
                <Tx k="cancel" />
              </button>
              <button
                className="dtf-action dtf-action--done"
                onClick={handleFreeWallDone}
                title="Finish — a closed outline becomes a room"
              >
                <Tx k="done" />
              </button>
            </>
          ) : drawMode ? (
            /* Adding rooms (rectangle drag) — each drag commits immediately, so
               Prev undoes the last one placed and Cancel undoes all of them. */
            <>
              <button
                className="dtf-action"
                onClick={handleDrawPrev}
                disabled={drawSessionRoomIds.length === 0}
                title="Undo the last room"
              >
                <Tx k="prev" />
              </button>
              <button
                className="dtf-action dtf-action--cancel"
                onClick={handleDrawCancel}
                title="Discard and stop drawing"
              >
                <Tx k="cancel" />
              </button>
              <button
                className="dtf-action dtf-action--done"
                onClick={disableAllModes}
                title="Finish adding rooms"
              >
                <Tx k="done" />
              </button>
            </>
          ) : (
            <>
              <button
                className={`draw-tool-btn${wallMode ? ' draw-tool-btn--active' : ''}`}
                onClick={() => { enableWallMode(); setSidebarOpen(false); }}
                title="Draw a room by placing its walls one segment at a time"
              >
                <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                  <path d="M1.5 11.5L11.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="1.5" cy="11.5" r="1.5" fill="currentColor"/>
                  <circle cx="11.5" cy="1.5" r="1.5" fill="currentColor"/>
                </svg>
                <Tx k="draw_room" />
              </button>
              <button
                className={`draw-tool-btn${drawMode ? ' draw-tool-btn--active' : ''}`}
                onClick={() => { drawMode ? disableAllModes() : enableDrawMode(); setSidebarOpen(false); }}
                title="Add a room by dragging a rectangle on the grid"
              >
                <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                  <rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <Tx k="add_room" />
              </button>
              <button
                className={`draw-tool-btn${freeWallMode ? ' draw-tool-btn--active' : ''}`}
                onClick={() => { freeWallMode ? disableAllModes() : enableFreeWallMode(); setSidebarOpen(false); }}
                title="Draw a wall at any angle — click start, click end, then place it or switch to curving it (Shift = free angle)"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1.5 12.5C4 5 9 2 12.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="1.5" cy="12.5" r="1.5" fill="currentColor"/>
                  <circle cx="12.5" cy="1.5" r="1.5" fill="currentColor"/>
                </svg>
                <Tx k="free_wall" />
              </button>
            </>
          )}
        </div>

        {/* Floating History Bar */}
        <div className="style-history-bar" onClick={e => e.stopPropagation()}>
          <button
            className="shb-arrow"
            onClick={() => dispatch(builderHistoryBack())}
            disabled={builderHistoryIndex === 0}
            title="Undo layout change"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2.5L4.5 7L9 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span className="shb-label">
            {builderHistory.length > 1 && (
              <span className="shb-count">{builderHistoryIndex}/{builderHistory.length - 1}</span>
            )}
          </span>
          <button
            className="shb-arrow"
            onClick={() => dispatch(builderHistoryNext())}
            disabled={builderHistoryIndex >= builderHistory.length - 1}
            title="Redo layout change"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Merge-rooms tool — pick adjacent rooms to fuse into one */}
        <div className="builder-merge-bar" onClick={e => e.stopPropagation()}>
          <button
            className={`builder-merge-btn${mergeMode ? ' builder-merge-btn--active' : ''}`}
            onClick={toggleMergeMode}
            title={t('merge_tooltip')}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <rect x="1.5" y="1.5" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4"/>
              <rect x="8.5" y="8.5" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M7.5 4.5H11A1.5 1.5 0 0 1 12.5 6v2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            <Tx k="merge_btn" />
          </button>
          {mergeMode && <span className="builder-merge-hint"><Tx k="merge_hint" /></span>}
        </div>

        {/* Mobile bottom-left bar: Rooms toggle + Finish button — hidden on desktop */}
        <div className="builder-mobile-bar" onClick={e => e.stopPropagation()}>
          <button
            className="builder-mobile-toggle"
            onClick={(e) => { e.stopPropagation(); setSidebarOpen(o => !o); }}
            title="Toggle panel"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="3" width="14" height="2" rx="1" fill="currentColor"/>
              <rect x="1" y="7" width="14" height="2" rx="1" fill="currentColor"/>
              <rect x="1" y="11" width="14" height="2" rx="1" fill="currentColor"/>
            </svg>
            <Tx k="rooms_tab" />
          </button>
        </div>

        {/* Bottom-right column: wall height (mobile only) stacked above zoom */}
        <div className="builder-bottom-right" onClick={e => e.stopPropagation()}>
          <div className="bwh-mobile" data-wh-ui="1" onClick={e => e.stopPropagation()}>
            {/* Upward panel */}
            <div className={`bwh-mobile-panel${wallHeightOpen ? ' bwh-mobile-panel--open' : ''}`}>
              <div className="bwh-mobile-row">
                <button className="bwh-mobile-btn" onClick={() => dispatch(setWallHeight(Math.max(1.8, +((wallHeight ?? 2.8) - 0.1).toFixed(1))))}>−</button>
                <input
                  className="bwh-mobile-slider"
                  type="range"
                  min="1.8" max="10.0" step="0.1"
                  value={wallHeight ?? 2.8}
                  onChange={e => dispatch(setWallHeight(parseFloat(e.target.value)))}
                />
                <button className="bwh-mobile-btn" onClick={() => dispatch(setWallHeight(Math.min(10.0, +((wallHeight ?? 2.8) + 0.1).toFixed(1))))}>+</button>
              </div>
              {wallHeightEditing ? (
                <input
                  autoFocus
                  type="text"
                  inputMode="decimal"
                  defaultValue={(wallHeight ?? 2.8).toFixed(1)}
                  onChange={e => setWallHeightDraft(e.target.value)}
                  onFocus={e => e.target.select()}
                  onBlur={() => {
                    const v = parseFloat(wallHeightDraft.replace(',', '.'));
                    if (!isNaN(v)) dispatch(setWallHeight(v));
                    setWallHeightEditing(false);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setWallHeightEditing(false);
                  }}
                  className="bwh-mobile-val bwh-mobile-val--input"
                />
              ) : (
                <div
                  className="bwh-mobile-val"
                  title="Click to type a value"
                  onClick={() => { setWallHeightDraft((wallHeight ?? 2.8).toFixed(1)); setWallHeightEditing(true); }}
                >
                  {(wallHeight ?? 2.8).toFixed(1)} m
                </div>
              )}
            </div>
            {/* Trigger pill */}
            <button
              className={`bwh-mobile-trigger${wallHeightOpen ? ' bwh-mobile-trigger--open' : ''}`}
              onClick={() => setWallHeightOpen(o => !o)}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}>
                <rect x="1" y="1" width="2" height="10" rx="1" fill="currentColor"/>
                <rect x="9" y="1" width="2" height="10" rx="1" fill="currentColor"/>
                <path d="M3 6H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              {/* Two labels, swapped by CSS at 500px. Rendering both and
                  hiding one beats a resize listener: no JS runs on resize, and
                  the label is correct on the very first paint. */}
              <span className="bwh-mobile-label"><Tx k="wall_height_label" /></span>
              <span className="bwh-mobile-label bwh-mobile-label--short"><Tx k="wall_label_short" /></span>
              <span>{(wallHeight ?? 2.8).toFixed(1)} m</span>
              <svg className="bwh-mobile-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 6.5L5 3.5L8 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="builder-zoom-overlay">
            <button className="bzo-btn" onClick={() => setZoom(z => Math.min(2.5, +(z + 0.1).toFixed(2)))} title="Zoom in">+</button>
            <button className="bzo-val" onClick={() => setZoom(1)} title="Reset zoom">{Math.round(zoom * 100)}%</button>
            <button className="bzo-btn" onClick={() => setZoom(z => Math.max(0.2, +(z - 0.1).toFixed(2)))} title="Zoom out">−</button>
            {/* The screenshot button was here. Removed from the BUILDER only —
                a picture of the bare grid layout is of little use, and it was
                crowding the zoom row on a phone. The floor plan and the 3D
                views keep theirs, where a screenshot is of something worth
                sharing. The helper that drew it went with it. */}
          </div>
        </div>

        {/* Right-column cards: wall height + save/upload */}
        <div className="builder-right-cards" onClick={e => e.stopPropagation()}>
          {/* Save card — desktop only; mobile/tablet shows "View Floor Plan" here instead */}
          <div className="builder-controls-card bcc-desktop-only">
          {isGuest ? (
            <GuestSaveButton />
          ) : (
            <div
              className={`bcc-websave-btn bcc-websave-btn--status${!projectId ? ' bcc-websave-btn--disabled' : ''}${(webSaving || isDirty) ? ' bcc-websave-btn--saving' : ''}`}
              title={projectId ? 'Autosaves to the cloud' : 'Open from Workspace to enable autosave'}
            >
              {(webSaving || isDirty) ? (
                <span className="bcc-websave-spinner" />
              ) : (
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                  <rect x="3.5" y="1.5" width="5" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
                  <rect x="3" y="7" width="8" height="4.5" rx="0.8" stroke="currentColor" strokeWidth="1.2"/>
                  <line x1="8.5" y1="2.5" x2="8.5" y2="4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              )}
              {(webSaving || isDirty) ? t('saving') : t('saved')}
            </div>
          )}
          {saveMsg && (
            <div className={`bcc-flash${saveMsg.ok ? '' : ' bcc-flash--err'}`}>{saveMsg.text}</div>
          )}
          </div>
          {/* Mobile/tablet only — same action as the sidebar's "View Floor Plan"
              button (hidden there on these breakpoints so it isn't duplicated) */}
          <div className="builder-controls-card bcc-mobile-only">
            <button
              className={`bcc-websave-btn bcc-viewplan-btn${!canProceed ? ' bcc-websave-btn--disabled' : ''}`}
              onClick={() => canProceed && confirmIfDirty(() => dispatch(goToPhase('plan')))}
              disabled={!canProceed}
            >
              <Tx k={canProceed ? 'view_floor_plan' : 'add_room_first'} />
            </button>
          </div>
          {/* Wall height card */}
          <div className="builder-controls-card builder-wh-card" data-wh-ui="1">
            <button
              className={`bcc-wh-toggle${wallHeightOpen ? ' bcc-wh-toggle--open' : ''}`}
              onClick={() => setWallHeightOpen(o => !o)}
            >
              <span className="bcc-wh-toggle-left">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}>
                  <rect x="1" y="1" width="2" height="10" rx="1" fill="currentColor"/>
                  <rect x="9" y="1" width="2" height="10" rx="1" fill="currentColor"/>
                  <path d="M3 6H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                {t('wall_height_label')}
              </span>
              <svg className="bcc-wh-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className={`bcc-wh-body${wallHeightOpen ? ' bcc-wh-body--open' : ''}`}>
              <div className="bcc-wh-row">
                <button className="bcc-btn bcc-btn--sm" onClick={() => dispatch(setWallHeight((wallHeight ?? 2.8) - 0.1))}>−</button>
                <input
                  className="bcc-slider"
                  type="range"
                  min="2.2" max="10.0" step="0.1"
                  value={wallHeight ?? 2.8}
                  onChange={e => dispatch(setWallHeight(e.target.value))}
                />
                <button className="bcc-btn bcc-btn--sm" onClick={() => dispatch(setWallHeight((wallHeight ?? 2.8) + 0.1))}>+</button>
              </div>
              <div className="bcc-wh-val">{(wallHeight ?? 2.8).toFixed(1)} m</div>
            </div>
          </div>
        </div>{/* /builder-right-cards */}

        {/* ── FLOATING EDIT PANEL ── (room / furniture / room-opening). Hidden
            while a wall or wall-opening is selected, so it never overlaps the
            wall-edit float which sits at the same spot. */}
        {(selectedOpeningId || panelRoom || selectedFurniture) && selectedWallId == null && selectedAutoWallKey == null && !selectedWallOpeningInfo && (
          <div className="floating-edit-panel" onClick={e => e.stopPropagation()}>

            {/* Opening resize panel */}
            {selectedOpeningId && selectedRoom && (() => {
              const op = (selectedRoom.openings || []).find(o => o.id === selectedOpeningId);
              if (!op) return null;
              const isD = op.type === 'door';
              const isA = op.type === 'archway';
              const isDecor = isWallDecorType(op.type);
              const { width: defW, height: defH } = openingDefaultSize(op.type);
              const showSill = op.type === 'window' || isDecor;
              const w    = +(op.width  || defW).toFixed(2);
              const h    = +(op.height || defH).toFixed(2);
              const sill = +(op.sill ?? (WALL_DECOR_DEFAULT_SILL[op.type] ?? 0.9)).toFixed(2);
              const rid = selectedRoom.id;
              const oid = op.id;
              const { emoji: decorEmoji, label: typeLabel } = openingTypeMeta(op.type, t);
              const minH = 0.1;
              const minW = 0.1;
              return (
                <>
                  <div className="fp-panel-header">
                    <span className="fp-panel-title">
                      {isD ? <DoorIcon size={15} /> : isA ? <ArchIcon size={15} /> : isDecor ? <span>{decorEmoji}</span> : <WindowIcon size={15} />}
                    {typeLabel} {t('size')}
                    </span>
                    <button
                      className="resize-panel-close" title="Copy (Ctrl+C)"
                      onClick={handleCopySelected}
                    >⧉</button>
                    <button className="resize-panel-close" onClick={() => setSelectedOpeningId(null)}>×</button>
                  </div>
                  <div className="resize-row">
                    <span className="resize-label">{t('room_width')}</span>
                    <div className="resize-controls">
                      <button className="resize-btn" onClick={() => dispatch(resizeOpening({ roomId: rid, openingId: oid, width: Math.max(minW, +(w - 0.1).toFixed(2)) }))}>−</button>
                      <input className="resize-input" type="number" min={minW} max={3.0} step={0.1}
                        value={w}
                        onChange={(e) => dispatch(resizeOpening({ roomId: rid, openingId: oid, width: parseFloat(e.target.value) || defW }))} />
                      <button className="resize-btn" onClick={() => dispatch(resizeOpening({ roomId: rid, openingId: oid, width: Math.min(3.0, +(w + 0.1).toFixed(2)) }))}>+</button>
                    </div>
                    <span className="resize-meters">{w.toFixed(1)}m</span>
                  </div>
                  <div className="resize-row">
                    <span className="resize-label">{t('room_height')}</span>
                    <div className="resize-controls">
                      <button className="resize-btn" onClick={() => dispatch(resizeOpening({ roomId: rid, openingId: oid, height: Math.max(minH, +(h - 0.1).toFixed(2)) }))}>−</button>
                      <input className="resize-input" type="number" min={minH} max={3.5} step={0.1}
                        value={h}
                        onChange={(e) => dispatch(resizeOpening({ roomId: rid, openingId: oid, height: parseFloat(e.target.value) || defH }))} />
                      <button className="resize-btn" onClick={() => dispatch(resizeOpening({ roomId: rid, openingId: oid, height: Math.min(3.5, +(h + 0.1).toFixed(2)) }))}>+</button>
                    </div>
                    <span className="resize-meters">{h.toFixed(1)}m</span>
                  </div>
                  {showSill && (
                    <div className="resize-row">
                      <span className="resize-label">{t('sill')}</span>
                      <div className="resize-controls">
                        <button className="resize-btn" onClick={() => dispatch(resizeOpening({ roomId: rid, openingId: oid, sill: Math.max(0.0, +(sill - 0.1).toFixed(2)) }))}>−</button>
                        <input className="resize-input" type="number" min={0.0} max={2.8} step={0.1}
                          value={sill}
                          onChange={(e) => dispatch(resizeOpening({ roomId: rid, openingId: oid, sill: parseFloat(e.target.value) || 0.9 }))} />
                        <button className="resize-btn" onClick={() => dispatch(resizeOpening({ roomId: rid, openingId: oid, sill: Math.min(2.8, +(sill + 0.1).toFixed(2)) }))}>+</button>
                      </div>
                      <span className="resize-meters">{sill.toFixed(1)}m</span>
                    </div>
                  )}
                  <div className="resize-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                    <span className="resize-label">{t('color_label')}</span>
                    <ColorPalette
                      colors={isD ? DOOR_COLORS : isA ? ARCH_COLORS : isDecor ? (WALL_DECOR_COLORS[op.type] || WINDOW_COLORS) : WINDOW_COLORS}
                      current={op.color}
                      onChange={c => dispatch(recolorOpening({ roomId: rid, openingId: oid, color: c }))}
                    />
                  </div>
                  <button className="opening-btn opening-btn--danger" style={{ marginTop: 4 }}
                    onClick={() => {
                      dispatch(removeOpening({ roomId: rid, openingId: oid }));
                      setSelectedOpeningId(null);
                    }}>
                    {t('remove')}
                  </button>
                </>
              );
            })()}

            {/* Room edit panel */}
            {panelRoom && !selectedOpeningId && (() => {
              const maxW = GRID - panelRoom.x;
              const maxH = GRID - panelRoom.y;
              const isSelected = panelRoom.id === selectedRoomId;
              return (
                <>
                  <div className="fp-panel-header">
                    <span className="fp-panel-title">
                      <RoomIcon type={panelRoom.type} size={16} /> {panelRoom.label}
                      {!isSelected && <span className="resize-panel-hint"> · click to edit</span>}
                    </span>
                    {isSelected && <button className="resize-panel-close" onClick={handleDeselect}>×</button>}
                  </div>

                  {isSelected && <>
                    <div className="resize-row">
                      <span className="resize-label">{t('room_name')}</span>
                      <input
                        type="text"
                        className="resize-input room-name-input"
                        value={panelRoom.label}
                        onChange={e => dispatch(renameRoom({ id: panelRoom.id, label: e.target.value }))}
                        placeholder={t('room_name')}
                      />
                    </div>
                    <div className="resize-row">
                      <span className="resize-label">{t('room_width')}</span>
                      <div className="resize-controls">
                        <button className="resize-btn" onClick={() => handleResize(-1, 0)} disabled={panelRoom.w <= 1}>−</button>
                        <input className="resize-input" type="number" min={1} max={maxW}
                          value={panelRoom.w}
                          onChange={(e) => {
                            const v = Math.max(1, Math.min(maxW, parseInt(e.target.value) || 1));
                            dispatch(resizeRoom({ id: panelRoom.id, w: v, h: panelRoom.h }));
                          }}
                        />
                        <button className="resize-btn" onClick={() => handleResize(1, 0)} disabled={panelRoom.w >= maxW}>+</button>
                      </div>
                      <span className="resize-meters">{panelRoom.w}m</span>
                    </div>
                    <div className="resize-range-hint">min 1 · max {maxW} cells</div>

                    <div className="resize-row">
                      <span className="resize-label">{t('room_height')}</span>
                      <div className="resize-controls">
                        <button className="resize-btn" onClick={() => handleResize(0, -1)} disabled={panelRoom.h <= 1}>−</button>
                        <input className="resize-input" type="number" min={1} max={maxH}
                          value={panelRoom.h}
                          onChange={(e) => {
                            const v = Math.max(1, Math.min(maxH, parseInt(e.target.value) || 1));
                            dispatch(resizeRoom({ id: panelRoom.id, w: panelRoom.w, h: v }));
                          }}
                        />
                        <button className="resize-btn" onClick={() => handleResize(0, 1)} disabled={panelRoom.h >= maxH}>+</button>
                      </div>
                      <span className="resize-meters">{panelRoom.h}m</span>
                    </div>
                    <div className="resize-range-hint">min 1 · max {maxH} cells</div>

                    {(panelRoom.openings || []).length > 0 && (
                      <p className="opening-hint" style={{ marginTop: 8 }}>
                        {(panelRoom.openings || []).length} opening{(panelRoom.openings || []).length !== 1 ? 's' : ''} on this room
                      </p>
                    )}

                    {/* ── MOVE THE ROOM ──────────────────────────────────────
                        TOUCH ONLY (see .room-move-btn in Builder.css): a mouse
                        drags a room directly, with no mode to enter, so on a
                        desktop this button would offer nothing.

                        Immediately BEFORE delete, and styled as an ordinary
                        action rather than a danger one, so the destructive
                        button stays last in the panel where it is hardest to
                        hit by accident.

                        Selecting it closes this panel — see `movingThisRoom` —
                        because the card sits over the room you are about to
                        drag. The room glows and shows a badge meanwhile, and
                        the button on the room itself ends the move. */}
                    <button
                      type="button"
                      className="room-move-btn"
                      onClick={() => { setConfirmDelete(false); setMoveRoomId(panelRoom.id); }}
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M8 1v14M1 8h14M8 1L5 4M8 1l3 3M8 15l-3-3M8 15l3-3M1 8l3-3M1 8l3 3M15 8l-3-3M15 8l-3 3"
                          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {t('builder_move')}
                    </button>

                    {/* Delete room */}
                    {!confirmDelete ? (
                      <button
                        className="room-delete-btn"
                        onClick={() => setConfirmDelete(true)}
                      >
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                          <path d="M2 4h10M5 4V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5V4M6 7v3.5M8 7v3.5M3 4l.8 7.2A1 1 0 004.8 12h4.4a1 1 0 001-.8L11 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('delete_room')}
                      </button>
                    ) : (
                      <div className="room-delete-confirm">
                        <span className="room-delete-confirm-text">{t('delete_room_confirm')}</span>
                        <div className="room-delete-confirm-btns">
                          <button
                            className="room-delete-confirm-btn room-delete-confirm-btn--yes"
                            onClick={() => {
                              dispatch(removeRoom(panelRoom.id));
                              setConfirmDelete(false);
                              handleDeselect();
                            }}
                          >{t('yes')}</button>
                          <button
                            className="room-delete-confirm-btn room-delete-confirm-btn--no"
                            onClick={() => setConfirmDelete(false)}
                          >{t('no')}</button>
                        </div>
                      </div>
                    )}
                  </>}

                  {!isSelected && (
                    <div className="resize-panel-stats">
                      <span>{panelRoom.w} × {panelRoom.h} m</span>
                      {(panelRoom.openings || []).length > 0 && (
                        <span>{(panelRoom.openings || []).length} opening{(panelRoom.openings || []).length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Furniture edit panel */}
            {selectedFurniture && !panelRoom && !selectedOpeningId && (() => {
              const ft = FURNITURE_TYPES.find(f => f.type === selectedFurniture.type);
              return (
                <>
                  <div className="fp-panel-header">
                    <span className="fp-panel-title"><FurniturePaletteIcon type={ft?.type} size={16} /> {ft?.label}</span>
                    <button className="resize-panel-close" onClick={handleDeselect}>×</button>
                  </div>
                  {!confirmDeleteFurniture ? (
                    <div className="furniture-panel-actions">
                      <button className="furniture-action-btn"
                        onClick={() => dispatch(rotateFurniture(selectedFurniture.id))}>{t('rotate')}</button>
                      <button className="furniture-action-btn" title="Copy (Ctrl+C)"
                        onClick={handleCopySelected}>⧉ Copy</button>
                      <button className="furniture-action-btn furniture-action-btn--danger"
                        onClick={() => setConfirmDeleteFurniture(true)}>× {t('remove')}</button>
                    </div>
                  ) : (
                    <div className="furniture-delete-confirm room-delete-confirm">
                      <span className="room-delete-confirm-text">{t('delete_confirm')}</span>
                      <div className="room-delete-confirm-btns">
                        <button className="room-delete-confirm-btn room-delete-confirm-btn--yes"
                          onClick={() => { dispatch(removeFurniture(selectedFurniture.id)); handleDeselect(); }}>
                          {t('yes')}
                        </button>
                        <button className="room-delete-confirm-btn room-delete-confirm-btn--no"
                          onClick={() => setConfirmDeleteFurniture(false)}>
                          {t('no')}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="opening-tools-label" style={{ marginBottom: 6 }}>Colour</div>
                  <ColorPalette
                    colors={FURN_COLORS}
                    current={selectedFurniture.color}
                    onChange={c => dispatch(recolorFurniture({ id: selectedFurniture.id, color: c }))}
                  />

                  {/* Size — dims are stored pre-rotation, so "display" W/H (what the
                      user sees as width/height on screen) swaps with the stored w/h
                      once rotated 90/270°, same mapping the drag-corner resize handle
                      uses (handleResizeMouseDown above). */}
                  {(() => {
                    const rotated = selectedFurniture.rotation % 180 !== 0;
                    const dispWm = Math.round(((rotated ? selectedFurniture.h : selectedFurniture.w) / CELL) * 10) / 10;
                    const dispHm = Math.round(((rotated ? selectedFurniture.w : selectedFurniture.h) / CELL) * 10) / 10;
                    const applyResize = (newWm, newHm) => {
                      const newDispW = Math.max(0.1, newWm) * CELL;
                      const newDispH = Math.max(0.1, newHm) * CELL;
                      const newW = rotated ? newDispH : newDispW;
                      const newH = rotated ? newDispW : newDispH;
                      dispatch(resizeFurniture({ id: selectedFurniture.id, w: newW, h: newH }));
                      dispatch(commitBuilderHistory());
                    };
                    return (
                      <>
                        <div className="opening-tools-label" style={{ marginBottom: 8, marginTop: 12 }}>{t('size')}</div>
                        <div className="resize-row">
                          <span className="resize-label">W</span>
                          <div className="resize-controls">
                            <button className="resize-btn" onClick={() => applyResize(Math.round((dispWm - 0.1) * 10) / 10, dispHm)} disabled={dispWm <= 0.1}>−</button>
                            <input className="resize-input" type="number" min={0.1} step={0.1}
                              value={dispWm}
                              onChange={(e) => applyResize(parseFloat(e.target.value) || 0.1, dispHm)}
                            />
                            <button className="resize-btn" onClick={() => applyResize(Math.round((dispWm + 0.1) * 10) / 10, dispHm)}>+</button>
                          </div>
                          <span className="resize-meters">{dispWm.toFixed(1)}m</span>
                        </div>
                        <div className="resize-row">
                          <span className="resize-label">H</span>
                          <div className="resize-controls">
                            <button className="resize-btn" onClick={() => applyResize(dispWm, Math.round((dispHm - 0.1) * 10) / 10)} disabled={dispHm <= 0.1}>−</button>
                            <input className="resize-input" type="number" min={0.1} step={0.1}
                              value={dispHm}
                              onChange={(e) => applyResize(dispWm, parseFloat(e.target.value) || 0.1)}
                            />
                            <button className="resize-btn" onClick={() => applyResize(dispWm, Math.round((dispHm + 0.1) * 10) / 10)}>+</button>
                          </div>
                          <span className="resize-meters">{dispHm.toFixed(1)}m</span>
                        </div>
                      </>
                    );
                  })()}

                  <div className="opening-tools-label" style={{ marginBottom: 8, marginTop: 12 }}>Change type</div>
                  <div className="furniture-type-grid">
                    {FURNITURE_TYPES.map(t => (
                      <button
                        key={t.type}
                        className={`furniture-type-btn${selectedFurniture.type === t.type ? ' furniture-type-btn--active' : ''}`}
                        onClick={() => dispatch(changeFurnitureType({ id: selectedFurniture.id, type: t.type }))}
                      >
                        <span className="ftype-icon"><FurniturePaletteIcon type={t.type} size={14} /></span>
                        <span className="ftype-label">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}

          </div>
        )}

        {/* Outer div sized to scaled dimensions so scroll area updates with zoom */}
        <div style={{ width: GRID * CELL * zoom, height: GRID * CELL * zoom, flexShrink: 0, margin: 'auto' }}>
        <div
          className="grid-wrapper"
          ref={gridRef}
          style={{ width: GRID * CELL, height: GRID * CELL, transform: `scale(${zoom})`, transformOrigin: '0 0' }}
          onDragLeave={handleGridDragLeave}
          onClick={handleDeselect}
        >
          {/* Grid lines */}
          <svg className="grid-lines" width={GRID * CELL} height={GRID * CELL}>
            {Array.from({ length: GRID + 1 }, (_, i) => (
              <React.Fragment key={i}>
                <line x1={i * CELL} y1={0} x2={i * CELL} y2={GRID * CELL} stroke="#e2e5ed" strokeWidth="0.5" />
                <line x1={0} y1={i * CELL} x2={GRID * CELL} y2={i * CELL} stroke="#e2e5ed" strokeWidth="0.5" />
              </React.Fragment>
            ))}
          </svg>

          {/* Room ghost */}
          {dragRoomType && hoverCell && <GhostPreview type={dragRoomType} hoverCell={hoverCell} />}

          {/* Placed rooms */}
          {rooms.map(room => (
            <PlacedRoom key={room.id} room={room}
              isSelected={mergeMode ? mergeIds.includes(room.id) : room.id === selectedRoomId}
              onSelect={() => mergeMode ? handleMergeClick(room.id) : handleSelectRoom(room.id)}
              onHover={(id) => setHoveredRoomId(id)}
              openingTool={openingTool}
              zoom={zoom}
              moveMode={room.id === moveRoomId}
              onMoveMode={(on) => setMoveRoomId(on ? room.id : null)}
              selectedOpeningId={selectedOpeningId}
              onSelectOpening={(id) => { setSelectedOpeningId(id); setSelectedRoomId(room.id); setSelectedFurnitureId(null); setSidebarTab('furniture'); }}
              onFreeOpeningClick={room.pts ? ((e) => handlePtsRoomOpeningClick(e, room)) : undefined}
              selectedFreeEdge={selectedFreeEdge?.roomId === room.id ? selectedFreeEdge.edge : null}
              /* Selecting a WALL must not also select the room, or the room's
                 edit panel would open on top of the wall panel. */
              onSelectFreeEdge={(edge) => {
                selectFreeEdge({ roomId: room.id, edge });
                setSelectedRoomId(null);
                setSelectedOpeningId(null);
                setSelectedFurnitureId(null);
              }}
              onFreeOpeningDrag={room.pts ? ((e, op) => handleFreeOpeningDrag(e, room, op)) : undefined}
              freeOpeningDrag={freeOpeningDrag?.roomId === room.id ? freeOpeningDrag : null}
              onPlaceOpening={(wall, pos, absX, absY) => {
                const sz = openingSize[openingTool] || {};
                const newId = nextOpeningId; // captured before dispatch increments it
                dispatch(addOpening({
                  roomId: room.id, type: openingTool, wall, pos, ...sz,
                  ...(absX !== undefined ? { absX, absY } : {}),
                }));
                setOpeningTool(null);
                setSelectedOpeningId(newId); // auto-open resize panel for the new opening
                setSelectedRoomId(room.id);
                setSidebarTab('furniture');
              }}
            />
          ))}

          {/* Placed furniture */}
          {furniture.map(item => (
            <FurniturePiece key={item.id} item={item}
              isSelected={item.id === selectedFurnitureId}
              onSelect={() => handleSelectFurniture(item.id)}
              zoom={zoom}
              GRID={GRID}
              onDragReport={reportFurnitureDrag}
            />
          ))}

          {/* Room drag-capture overlay — covers placed rooms so drop always works */}
          {dragRoomType && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 100 }}
              onDragOver={handleRoomDragOver}
              onDrop={handleRoomDrop}
              onDragLeave={() => dispatch(setHoverCell(null))}
            />
          )}

          {/* Furniture drag-capture overlay */}
          {dragFurnitureType && (
            <div className="furniture-drag-capture"
              onDragOver={handleFurnitureDragOver}
              onDrop={handleFurnitureDrop}
              onDragLeave={() => setFurnitureHover(null)}
            >
              {furnitureHover && (() => {
                const ft = FURNITURE_TYPES.find(f => f.type === dragFurnitureType);
                return ft ? (
                  <div className="furniture-ghost" style={{ left: furnitureHover.x, top: furnitureHover.y, width: ft.w, height: ft.h }}>
                    <FurnitureSymbol type={dragFurnitureType} w={ft.w} h={ft.h} color={ft.color} />
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* ── WALL SEGMENTS SVG OVERLAY ── */}
          <svg
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 70, overflow: 'visible' }}
            width={GRID * CELL} height={GRID * CELL}
          >
            {/* Auto-walls: room perimeters (interactive — select or place openings) */}
            {autoWalls.map((w, i) => {
              const isSelected = selectedAutoWallKey === w.key;
              const awOpenings = (autoWallOpenings || {})[w.key] || [];
              const isH = w.y1 === w.y2;
              const px1 = w.x1*CELL, py1 = w.y1*CELL, px2 = w.x2*CELL, py2 = w.y2*CELL;
              const totalLen = isH ? (px2-px1) : (py2-py1);
              // room.openings that fall on this auto-wall (e.g. placed via the 3D room
              // view's "Add Opening" modal) — shown and made interactive alongside
              // autoWallOpenings, not just cut into the wall line as a gap.
              const roomOpsOnWall = getRoomOpeningsOnWall(rooms, w, awOpenings);
              const openings = [...awOpenings, ...roomOpsOnWall];
              // Wall-decor items (painting/AC/clock/outlet/radiator) mount on the
              // wall face but don't cut a void — exclude them from the gap-cutting
              // openings list passed to WallLines; they still render their own
              // marker via the .map() calls below.
              const gapOpenings = openings.filter(o => !isWallDecorType(o.type));
              return (
                <g key={`aw-${i}`} style={{ pointerEvents: 'all' }} data-wall-ui="1" onClick={e => e.stopPropagation()}>
                  {/* Hit area */}
                  <line x1={px1} y1={py1} x2={px2} y2={py2}
                    stroke="transparent" strokeWidth={20} strokeLinecap="square"
                    style={{ cursor: wallOpeningTool ? 'crosshair' : 'pointer' }}
                    onMouseMove={e => {
                      if (!wallOpeningTool && !isSelected) return;
                      const r = gridRef.current?.getBoundingClientRect();
                      if (!r) return;
                      const t = isH
                        ? Math.max(0.02, Math.min(0.98, ((e.clientX - r.left) / (CELL * zoomRef.current) - w.x1) / (w.x2 - w.x1)))
                        : Math.max(0.02, Math.min(0.98, ((e.clientY - r.top)  / (CELL * zoomRef.current) - w.y1) / (w.y2 - w.y1)));
                      setWallHoverInfo({ autoKey: w.key, t });
                    }}
                    onMouseLeave={() => setWallHoverInfo(null)}
                    onMouseDown={e => {
                      if (e.button !== 0) return;
                      e.stopPropagation();
                      e.preventDefault();
                      const r = gridRef.current?.getBoundingClientRect();
                      if (!r) return;
                      const t = isH
                        ? Math.max(0.02, Math.min(0.98, ((e.clientX - r.left) / (CELL * zoomRef.current) - w.x1) / (w.x2 - w.x1)))
                        : Math.max(0.02, Math.min(0.98, ((e.clientY - r.top)  / (CELL * zoomRef.current) - w.y1) / (w.y2 - w.y1)));
                      if (wallOpeningTool) {
                        const sz = wallOpeningSize[wallOpeningTool];
                        dispatch(addAutoWallOpening({ key: w.key, type: wallOpeningTool, t, ...sz }));
                        setWallOpeningTool(null);
                      }
                      selectAutoWall(w.key);
                    }}
                    onTouchStart={e => {
                      e.stopPropagation();
                      const touch = e.touches[0];
                      if (wallOpeningTool && touch && gridRef.current) {
                        e.preventDefault();
                        const r = gridRef.current.getBoundingClientRect();
                        const t = isH
                          ? Math.max(0.02, Math.min(0.98, ((touch.clientX - r.left) / (CELL * zoomRef.current) - w.x1) / (w.x2 - w.x1)))
                          : Math.max(0.02, Math.min(0.98, ((touch.clientY - r.top)  / (CELL * zoomRef.current) - w.y1) / (w.y2 - w.y1)));
                        const sz = wallOpeningSize[wallOpeningTool];
                        dispatch(addAutoWallOpening({ key: w.key, type: wallOpeningTool, t, ...sz }));
                        setWallOpeningTool(null);
                        return;
                      }
                      selectAutoWall(w.key);
                      setWallOpeningTool(null);
                    }}
                    onTouchEnd={e => {
                      e.stopPropagation();
                      e.preventDefault(); // suppress synthetic click that would hit the backdrop
                      setSidebarOpen(true);
                    }}
                  />
                  {isSelected && <line x1={px1} y1={py1} x2={px2} y2={py2}
                    stroke="#e05800" strokeWidth={14} strokeLinecap="square" opacity={0.35} style={{ pointerEvents: 'none' }}/>}
                  <WallLines x1={px1} y1={py1} x2={px2} y2={py2} openings={gapOpenings}
                    strokeOuter={isSelected ? '#c04000' : '#26211c'}
                    strokeInner={isSelected ? '#f0a060' : '#e2ddd7'}
                  />
                  {awOpenings.map(op => {
                    const displayT = (openingDragPreview?.autoKey === w.key && openingDragPreview?.openingId === op.id)
                      ? openingDragPreview.t : op.t;
                    const cPx = displayT * totalLen;
                    const cx = isH ? px1 + cPx : px1;
                    const cy = isH ? py1 : py1 + cPx;
                    const isArmed = openingMoveArmed && selectedWallOpeningInfo?.openingId === op.id && selectedWallOpeningInfo?.autoKey === w.key;
                    return <WallOpeningSymbol key={op.id}
                      type={op.type} cx={cx} cy={cy} isH={isH}
                      width={op.width} color={op.color}
                      isSelected={selectedWallOpeningInfo?.openingId === op.id && selectedWallOpeningInfo?.autoKey === w.key}
                      armed={isArmed}
                      onClick={e => { e.stopPropagation(); selectAutoWall(w.key); setSelectedWallOpeningInfo({ autoKey: w.key, openingId: op.id }); }}
                      onPointerDown={isArmed ? (e) => startOpeningDrag(e, { autoKey: w.key, openingId: op.id, isH, totalLen, startT: op.t, width: op.width }) : undefined}
                    />;
                  })}
                  {roomOpsOnWall.map(op => {
                    const displayT = (openingDragPreview?.roomId === op.roomId && openingDragPreview?.openingId === op.id)
                      ? openingDragPreview.t : op.t;
                    const cPx = displayT * totalLen;
                    const cx = isH ? px1 + cPx : px1;
                    const cy = isH ? py1 : py1 + cPx;
                    const isThisSelected = selectedWallOpeningInfo?.openingId === op.id && selectedWallOpeningInfo?.roomId === op.roomId;
                    const isArmed = openingMoveArmed && isThisSelected;
                    const opRoom = rooms.find(r => r.id === op.roomId);
                    return <WallOpeningSymbol key={`room-${op.id}`}
                      type={op.type} cx={cx} cy={cy} isH={isH}
                      width={op.width} color={op.color}
                      isSelected={isThisSelected}
                      armed={isArmed}
                      onClick={e => { e.stopPropagation(); selectAutoWall(w.key); setSelectedWallOpeningInfo({ roomId: op.roomId, openingId: op.id }); }}
                      onPointerDown={isArmed && opRoom ? (e) => startOpeningDrag(e, {
                        roomId: op.roomId, openingId: op.id, isH, totalLen, startT: op.t, width: op.width,
                        wallSide: op.wall,
                        wallStartGrid: isH ? w.x1 : w.y1, wallEndGrid: isH ? w.x2 : w.y2, wallLineGrid: isH ? w.y1 : w.x1,
                        roomX: opRoom.x, roomY: opRoom.y, roomW: opRoom.w, roomH: opRoom.h,
                        isCompound: !!(opRoom.rects && opRoom.rects.length > 1),
                      }) : undefined}
                    />;
                  })}
                  {wallOpeningTool && wallHoverInfo?.autoKey === w.key && (() => {
                    const cPx = wallHoverInfo.t * totalLen;
                    const cx = isH ? px1 + cPx : px1;
                    const cy = isH ? py1 : py1 + cPx;
                    return <WallOpeningSymbol type={wallOpeningTool} cx={cx} cy={cy} isH={isH}
                      width={wallOpeningSize[wallOpeningTool]?.width} opacity={0.55}/>;
                  })()}
                  {/* Length label */}
                  {(() => {
                    const mx = (px1 + px2) / 2;
                    const my = (py1 + py2) / 2;
                    const lenM = isH ? (w.x2 - w.x1) : (w.y2 - w.y1);
                    const label = `${lenM}m`;
                    const offset = 14;
                    return (
                      <g style={{ pointerEvents: 'none' }}>
                        <text
                          x={isH ? mx : mx - offset}
                          y={isH ? my - offset : my}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={11}
                          fontFamily="system-ui, sans-serif"
                          fontWeight="600"
                          fill={isSelected ? '#e05800' : '#26211c'}
                          stroke="rgba(255,255,255,0.85)"
                          strokeWidth={3}
                          paintOrder="stroke"
                          transform={isH ? undefined : `rotate(-90, ${mx - offset}, ${my})`}
                        >{label}</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}

            {/* Placed wall segments — double-line architectural style */}
            {(wallSegments || []).map(w => {
              const isSelected = selectedWallId === w.id;
              const isDraggingThis = wallDrag?.id === w.id || wallTouchMoving === w.id;
              const c = (isDraggingThis && dragWallPreview) ? dragWallPreview : w;
              const isH = c.y1 === c.y2;
              const epCursor = isH ? 'ew-resize' : 'ns-resize';
              const openings = w.openings || [];
              const gapOpenings = openings.filter(o => !isWallDecorType(o.type));
              const px1 = c.x1*CELL, py1 = c.y1*CELL, px2 = c.x2*CELL, py2 = c.y2*CELL;
              const totalLen = isH ? (px2-px1) : (py2-py1);
              return (
                <g key={w.id} style={{ pointerEvents: 'all' }} data-wall-ui="1" onClick={e => e.stopPropagation()}>
                  {/* Hit area */}
                  <line x1={px1} y1={py1} x2={px2} y2={py2}
                    stroke="transparent" strokeWidth={20} strokeLinecap="square"
                    style={{ cursor: wallOpeningTool ? 'crosshair' : isSelected ? 'grab' : 'pointer' }}
                    onMouseMove={e => {
                      if (!wallOpeningTool) return;
                      setWallHoverInfo({ segId: w.id, t: getWallT(e, c) });
                    }}
                    onMouseLeave={() => setWallHoverInfo(null)}
                    onMouseDown={e => handleWallPointerDown(e, w, 'move')}
                    onTouchStart={e => {
                      e.stopPropagation();
                      const touch = e.touches[0];
                      if (!touch) return;

                      // Priority 1: wall move mode
                      if (wallTouchMovingRef.current === w.id) {
                        e.preventDefault();
                        wallTouchMoveStartRef.current = {
                          touchX: touch.clientX, touchY: touch.clientY,
                          origX1: w.x1, origY1: w.y1, origX2: w.x2, origY2: w.y2,
                        };
                        return;
                      }

                      // Priority 2: opening tool active — place the opening here
                      if (wallOpeningTool) {
                        e.preventDefault();
                        const t = getWallT(touch, w);
                        const sz = wallOpeningSize[wallOpeningTool];
                        dispatch(addWallSegOpening({ wallId: w.id, type: wallOpeningTool, t, ...sz }));
                        selectWallSeg(w.id);
                        setWallOpeningTool(null);
                        return;
                      }

                      // Priority 3: normal tap — select wall
                      selectWallSeg(w.id);
                      setWallOpeningTool(null);
                    }}
                    onTouchMove={e => {
                      // Wall move drag
                      if (wallTouchMovingRef.current !== w.id || !wallTouchMoveStartRef.current) return;
                      e.stopPropagation();
                      const touch = e.touches[0];
                      if (!touch || !gridRef.current) return;
                      const { touchX, touchY, origX1, origY1, origX2, origY2 } = wallTouchMoveStartRef.current;
                      const rect = gridRef.current.getBoundingClientRect();
                      const z = zoomRef.current;
                      const startGx = Math.round((touchX - rect.left) / (CELL * z));
                      const startGy = Math.round((touchY - rect.top)  / (CELL * z));
                      const gx = Math.round((touch.clientX - rect.left) / (CELL * z));
                      const gy = Math.round((touch.clientY - rect.top)  / (CELL * z));
                      const dx = gx - startGx, dy = gy - startGy;
                      const nc = {
                        x1: Math.max(0, Math.min(GRID, origX1 + dx)),
                        y1: Math.max(0, Math.min(GRID, origY1 + dy)),
                        x2: Math.max(0, Math.min(GRID, origX2 + dx)),
                        y2: Math.max(0, Math.min(GRID, origY2 + dy)),
                      };
                      dragPreviewRef.current = nc;
                      setDragWallPreview({ ...nc });
                    }}
                    onTouchEnd={e => {
                      // Wall move commit
                      if (wallTouchMovingRef.current === w.id && wallTouchMoveStartRef.current) {
                        wallTouchMovingRef.current = null;
                        setWallTouchMoving(null);
                        wallTouchMoveStartRef.current = null;
                        const final = dragPreviewRef.current;
                        if (final) {
                          dispatch(updateWallSegment({ id: w.id, ...final }));
                          dragPreviewRef.current = null;
                          setDragWallPreview(null);
                        }
                        return;
                      }
                      // Normal tap: useEffect watches selectedWallId and opens sidebar with 50ms delay
                    }}
                  />
                  {isSelected && <line x1={px1} y1={py1} x2={px2} y2={py2}
                    stroke="#e05800" strokeWidth={14} strokeLinecap="square" opacity={0.35} style={{ pointerEvents: 'none' }}/>}
                  <WallLines x1={px1} y1={py1} x2={px2} y2={py2} openings={gapOpenings}
                    strokeOuter={isSelected ? '#c04000' : '#26211c'}
                    strokeInner={isSelected ? '#f0a060' : '#e2ddd7'}
                  />
                  {openings.map(op => {
                    const isOpSelected = selectedWallOpeningInfo?.openingId === op.id && selectedWallOpeningInfo?.wallId === w.id;
                    const displayT = (openingDragPreview?.wallId === w.id && openingDragPreview?.openingId === op.id)
                      ? openingDragPreview.t : op.t;
                    const cPx = displayT * totalLen;
                    const cx = isH ? px1 + cPx : px1;
                    const cy = isH ? py1 : py1 + cPx;
                    const isArmed = openingMoveArmed && isOpSelected;
                    return <WallOpeningSymbol key={op.id}
                      type={op.type} cx={cx} cy={cy} isH={isH}
                      width={op.width} color={op.color}
                      isSelected={isOpSelected}
                      armed={isArmed}
                      onClick={e => { e.stopPropagation(); selectWallSeg(w.id); setSelectedWallOpeningInfo({ wallId: w.id, openingId: op.id }); }}
                      onPointerDown={isArmed ? (e) => startOpeningDrag(e, { wallId: w.id, openingId: op.id, isH, totalLen, startT: op.t, width: op.width }) : undefined}
                    />;
                  })}
                  {wallOpeningTool && wallHoverInfo?.segId === w.id && (() => {
                    const cPx = wallHoverInfo.t * totalLen;
                    const cx = isH ? px1 + cPx : px1;
                    const cy = isH ? py1 : py1 + cPx;
                    return <WallOpeningSymbol type={wallOpeningTool} cx={cx} cy={cy} isH={isH}
                      width={wallOpeningSize[wallOpeningTool]?.width} opacity={0.55}/>;
                  })()}
                  {isSelected && !wallOpeningTool && (
                    <>
                      <circle cx={px1} cy={py1} r={7}
                        fill={isDraggingThis && wallDrag?.dragType === 'ep1' ? '#e05800' : '#5096dc'}
                        stroke="white" strokeWidth="2" style={{ cursor: epCursor }}
                        onMouseDown={e => handleWallPointerDown(e, w, 'ep1')}/>
                      <circle cx={px2} cy={py2} r={7}
                        fill={isDraggingThis && wallDrag?.dragType === 'ep2' ? '#e05800' : '#5096dc'}
                        stroke="white" strokeWidth="2" style={{ cursor: epCursor }}
                        onMouseDown={e => handleWallPointerDown(e, w, 'ep2')}/>
                    </>
                  )}
                  {/* Length label */}
                  {(() => {
                    const mx = (px1 + px2) / 2;
                    const my = (py1 + py2) / 2;
                    const lenM = isH ? Math.abs(c.x2 - c.x1) : Math.abs(c.y2 - c.y1);
                    const label = `${lenM}m`;
                    const offset = 14;
                    return (
                      <g style={{ pointerEvents: 'none' }}>
                        <text
                          x={isH ? mx : mx - offset}
                          y={isH ? my - offset : my}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={11}
                          fontFamily="system-ui, sans-serif"
                          fontWeight="600"
                          fill={isSelected ? '#e05800' : '#26211c'}
                          stroke="rgba(255,255,255,0.85)"
                          strokeWidth={3}
                          paintOrder="stroke"
                          transform={isH ? undefined : `rotate(-90, ${mx - offset}, ${my})`}
                        >{label}</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}
            {/* In-progress chain — same style, dimmed to show it's not yet committed */}
            {wallChain.length > 1 && wallChain.slice(0, -1).map((pt, i) => {
              const nxt = wallChain[i + 1];
              const isH = pt.y === nxt.y;
              const lenM = isH ? Math.abs(nxt.x - pt.x) : Math.abs(nxt.y - pt.y);
              const mx = (pt.x + nxt.x) / 2 * CELL, my = (pt.y + nxt.y) / 2 * CELL;
              const off = 14;
              return (
                <g key={`cs-${i}`}>
                  <g opacity={0.55}>
                    <line x1={pt.x * CELL} y1={pt.y * CELL} x2={nxt.x * CELL} y2={nxt.y * CELL}
                      stroke="#26211c" strokeWidth={10} strokeLinecap="square" />
                    <line x1={pt.x * CELL} y1={pt.y * CELL} x2={nxt.x * CELL} y2={nxt.y * CELL}
                      stroke="#e2ddd7" strokeWidth={5} strokeLinecap="square" />
                  </g>
                  {lenM > 0 && (
                    <text x={isH ? mx : mx - off} y={isH ? my - off : my}
                      textAnchor="middle" dominantBaseline="middle"
                      fontSize={11} fontFamily="system-ui, sans-serif" fontWeight="600"
                      fill="#26211c" stroke="rgba(255,255,255,0.85)" strokeWidth={3} paintOrder="stroke"
                      transform={isH ? undefined : `rotate(-90, ${mx - off}, ${my})`}
                      style={{ pointerEvents: 'none' }}>{lenM}m</text>
                  )}
                </g>
              );
            })}
            {/* Preview: last chain point → snapped mouse position */}
            {wallMode && wallChain.length > 0 && wallMousePos && (() => {
              const last = wallChain[wallChain.length - 1];
              const x1 = last.x * CELL, y1 = last.y * CELL;
              const x2 = wallMousePos.x * CELL, y2 = wallMousePos.y * CELL;
              const dx = wallMousePos.x - last.x, dy = wallMousePos.y - last.y;
              const lenM = Math.round(Math.sqrt(dx * dx + dy * dy) * 10) / 10;
              const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
              const previewSeg = { x1: last.x, y1: last.y, x2: wallMousePos.x, y2: wallMousePos.y };
              const conflict = lenM > 0 && segConflicts(previewSeg);
              // Perpendicular gap guides: from the moving endpoint, measure the
              // clear distance to the nearest wall on each side across the wall's
              // travel axis (up/down for a horizontal wall, left/right for a
              // vertical one) — so you can align/space against surrounding walls.
              // Includes this chain's own already-placed (but not yet committed
              // to Redux) legs, so e.g. dragging a 3rd leg back toward the 1st
              // still measures against it, not just fully-finished walls.
              const chainSegs = wallChain.slice(0, -1).map((pt, i) => ({
                x1: pt.x, y1: pt.y, x2: wallChain[i + 1].x, y2: wallChain[i + 1].y,
              }));
              const allWalls = [...autoWalls, ...(wallSegments || []), ...chainSegs];
              const perpDirs = (dx !== 0 && dy === 0) ? ['up', 'down']
                : (dy !== 0 && dx === 0) ? ['left', 'right'] : [];
              const guides = lenM > 0 ? perpDirs.map(dir => {
                const coord = nearestWallInDir(wallMousePos, dir, allWalls);
                if (coord === null) return null;
                const ex = x2, ey = y2;
                let gx = ex, gy = ey, distCells = 0;
                if (dir === 'down')  { gy = coord * CELL; distCells = coord - wallMousePos.y; }
                if (dir === 'up')    { gy = coord * CELL; distCells = wallMousePos.y - coord; }
                if (dir === 'right') { gx = coord * CELL; distCells = coord - wallMousePos.x; }
                if (dir === 'left')  { gx = coord * CELL; distCells = wallMousePos.x - coord; }
                return { dir, ex, ey, gx, gy, distCells };
              }).filter(Boolean) : [];
              return (
                <>
                  {guides.map(g => {
                    const gmx = (g.ex + g.gx) / 2, gmy = (g.ey + g.gy) / 2;
                    return (
                      <g key={g.dir} style={{ pointerEvents: 'none' }}>
                        <line x1={g.ex} y1={g.ey} x2={g.gx} y2={g.gy}
                          stroke={DIST_GUIDE_COLOR} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.85" />
                        <g transform={`translate(${gmx},${gmy})`}>
                          <rect x="-20" y="-9" width="40" height="18" rx="4" fill="rgba(16,32,22,0.85)" />
                          <text x="0" y="0" textAnchor="middle" dominantBaseline="central"
                            fontSize="11" fontFamily="DM Mono, monospace" fontWeight="600"
                            fill={DIST_GUIDE_LABEL_COLOR} letterSpacing="0.3">
                            {Math.round(g.distCells * 10) / 10}m
                          </text>
                        </g>
                      </g>
                    );
                  })}
                  <line x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={conflict ? '#e05050' : WALL_PREVIEW_COLOR} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="6 4" opacity="0.8"
                  />
                  {lenM > 0 && (
                    <g transform={`translate(${mx},${my})`}>
                      <rect x={conflict ? '-30' : '-22'} y="-9" width={conflict ? '60' : '44'} height="18" rx="4"
                        fill={conflict ? 'rgba(80,10,10,0.88)' : 'rgba(20,30,55,0.82)'} />
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="central"
                        fontSize="11" fontFamily="DM Mono, monospace" fontWeight="600"
                        fill={conflict ? '#ff8080' : WALL_PREVIEW_LABEL_COLOR} letterSpacing="0.3">
                        {conflict ? '⊗ overlap' : `${lenM}m`}
                      </text>
                    </g>
                  )}
                </>
              );
            })()}
            {/* Chain vertex dots */}
            {wallChain.map((pt, i) => {
              const isStart = i === 0;
              // Pulse the start dot orange when the cursor is hovering over it
              const nearClose = isStart && wallChain.length >= 3 && wallMousePos &&
                wallMousePos.x === pt.x && wallMousePos.y === pt.y;
              return (
                <circle key={`cdot-${i}`}
                  cx={pt.x * CELL} cy={pt.y * CELL}
                  r={nearClose ? 9 : (isStart ? 6 : 4)}
                  fill={isStart ? '#f0a030' : '#5096dc'}
                  stroke={nearClose ? '#e05800' : 'white'}
                  strokeWidth={nearClose ? 2.5 : 1.5}
                  opacity={nearClose ? 1 : 0.9}
                />
              );
            })}
            {/* Snap dot at mouse position when wall mode is active */}
            {wallMode && wallMousePos && wallChain.length === 0 && (
              <circle cx={wallMousePos.x * CELL} cy={wallMousePos.y * CELL} r={4}
                fill="#5096dc" stroke="white" strokeWidth="1.5" opacity="0.7" />
            )}

            {/* ── FREE-FORM ROOMS (closed loops → floor + wall outline) ── */}
            {freeRooms.map(r => {
              const pts = freeRoomPts(r);              // live edit applied
              const d = freeRoomFloorPath(pts);
              const off = freeRoomDragOffset?.id === r.id ? freeRoomDragOffset : null;
              const sel = selectedFreeRoomId === r.id;
              const interactive = !(freeWallMode || wallMode || drawMode || mergeMode);
              const fm = FLOOR_MATERIALS.find(m => m.id === r.floor);
              const wm = WALL_MATERIALS.find(m => m.id === r.wall);
              const floorFill = fm ? fm.baseColor : '#c4b8a0';
              const innerStroke = sel ? '#f0a060' : (wm ? wm.color : '#e2ddd7');
              return (
                <g key={`fr-${r.id}`} transform={off ? `translate(${off.dx * CELL},${off.dy * CELL})` : undefined}>
                  <path d={d} fill={floorFill} fillOpacity={0.9} stroke="none"
                    style={{ cursor: wallOpeningTool ? 'crosshair' : (interactive ? 'move' : 'default'), pointerEvents: (interactive || wallOpeningTool) ? 'fill' : 'none' }}
                    onPointerDown={(e) => { if (wallOpeningTool) { e.stopPropagation(); return; } handleFreeRoomPointerDown(e, r); }}
                    onClick={(e) => { if (handleFreeRoomOpeningClick(e, r)) e.stopPropagation(); }} />
                  {/* Wall hit-band: lets an opening be placed by clicking ON the wall */}
                  <path d={d} fill="none" stroke="transparent" strokeWidth={16}
                    style={{ cursor: wallOpeningTool ? 'crosshair' : 'default', pointerEvents: wallOpeningTool ? 'stroke' : 'none' }}
                    onClick={(e) => { if (handleFreeRoomOpeningClick(e, r)) e.stopPropagation(); }} />
                  <path d={d} fill="none" stroke={sel ? '#c04000' : '#26211c'} strokeWidth={10} strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
                  <path d={d} fill="none" stroke={innerStroke} strokeWidth={5} strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
                  {/* Openings placed on this room's walls */}
                  {(r.openings || []).map(op => {
                    const a = pts[op.edge], b = pts[(op.edge + 1) % pts.length];
                    if (!a || !b) return null;
                    const q = edgePointAt(a, b, op.t);
                    const wPx = (op.width || 0.9) * PX_PER_M;
                    const deg = q.angle * 180 / Math.PI;
                    const col = op.type === 'door' ? '#c4a87a' : op.type === 'window' ? '#8fd0ff' : op.type === 'archway' ? '#e0d8c8' : (op.color || '#bbb');
                    return (
                      <g key={`fro-${op.id}`} transform={`translate(${q.x * CELL},${q.y * CELL}) rotate(${deg})`}
                         style={{ cursor: 'pointer' }}
                         onClick={(e) => { e.stopPropagation(); dispatch(removeFreeRoomOpening({ roomId: r.id, openingId: op.id })); }}>
                        <rect x={-wPx / 2} y={-6} width={wPx} height={12} fill="#fff" stroke="none" />
                        <rect x={-wPx / 2} y={-5} width={wPx} height={10} fill={col} stroke="#26211c" strokeWidth={1.2} />
                      </g>
                    );
                  })}
                  {/* RESIZE handles — corner vertices (square) + per-edge curve
                      handles (round), shown while this room is selected. */}
                  {sel && interactive && pts.map((p, i) => {
                    const b = pts[(i + 1) % pts.length];
                    const { cx, cy } = edgeControlPoint(p.x, p.y, b.x, b.y, (p.bulge || 0) / 2);
                    return (
                      <g key={`h-${i}`}>
                        <rect x={p.x * CELL - 5} y={p.y * CELL - 5} width={10} height={10} rx={2}
                          fill="#fff" stroke="#c04000" strokeWidth={2}
                          style={{ cursor: 'nwse-resize', pointerEvents: 'all' }}
                          onPointerDown={(e) => handleFreeRoomHandleDown(e, r, 'vertex', i)} />
                        <circle cx={cx * CELL} cy={cy * CELL} r={4.5}
                          fill="#5096dc" stroke="#fff" strokeWidth={1.5}
                          style={{ cursor: 'grab', pointerEvents: 'all' }}
                          onPointerDown={(e) => handleFreeRoomHandleDown(e, r, 'bulge', i)} />
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* ── FREE-FORM WALLS (any angle + curve) ──
                Rendered in the SAME double-stroke style as the grid walls
                (dark outer + light inner) so they clearly read as walls. */}
            {freeWalls.map(w => {
              const sel = w.id === selectedFreeWallId;
              const d = freeWallPath(w.x1, w.y1, w.x2, w.y2, w.bulge);
              // Cut the wall where its openings sit and mark the jambs — exactly
              // what WallLines does for a straight wall. Without the cut, an
              // opening was just a pale patch laid over an unbroken wall, which
              // read as a blank square (a window's own colour is near-white; on a
              // straight wall it's the gap + orange jambs that make it legible).
              const ends = (() => {
                const a = { x: w.x1, y: w.y1, bulge: w.bulge || 0 }, b = { x: w.x2, y: w.y2 };
                const NS = 64;
                const pts = [];
                let L = 0;
                for (let i = 0; i <= NS; i++) {
                  const q = edgePointAt(a, b, i / NS);
                  if (i > 0) L += Math.hypot(q.x - pts[i - 1].x, q.y - pts[i - 1].y);
                  pts.push({ x: q.x, y: q.y, ang: q.angle, s: L });
                }
                const total = L || 1;
                // Opening spans as t-ranges, and the solid runs left between them.
                // Decor mounts on the face and must not break the wall line.
                const gaps = (w.openings || [])
                  .filter(o => !isWallDecorType(o.type))
                  .map(o => {
                    const half = ((o.width || 0.9) / 2) / total;
                    return { lo: Math.max(0, (o.t ?? 0.5) - half), hi: Math.min(1, (o.t ?? 0.5) + half) };
                  })
                  .sort((p, q2) => p.lo - q2.lo);
                const runs = [];
                let cur = 0;
                for (const g of gaps) { if (g.lo > cur + 0.001) runs.push([cur, g.lo]); cur = Math.max(cur, g.hi); }
                if (cur < 0.999) runs.push([cur, 1]);
                const at = (tt) => {
                  const s = tt * total;
                  let i = 1;
                  while (i < pts.length - 1 && pts[i].s < s) i++;
                  const p0 = pts[i - 1], p1 = pts[i];
                  const f = (s - p0.s) / ((p1.s - p0.s) || 1);
                  return { x: p0.x + (p1.x - p0.x) * f, y: p0.y + (p1.y - p0.y) * f, ang: p0.ang };
                };
                const paths = runs.map(([t0, t1]) => {
                  const seg = [at(t0)];
                  for (const p of pts) if (p.s / total > t0 && p.s / total < t1) seg.push(p);
                  seg.push(at(t1));
                  return seg.map((p, i) => `${i ? 'L' : 'M'} ${p.x * CELL} ${p.y * CELL}`).join(' ');
                });
                // A tick across the wall at each cut edge.
                const jambs = gaps.flatMap((g, gi) => [g.lo, g.hi].map((tt, k) => {
                  const p = at(tt);
                  const J = 7;
                  const nx = -Math.sin(p.ang) * J, ny = Math.cos(p.ang) * J;
                  return { key: `${gi}-${k}`, x1: p.x * CELL - nx, y1: p.y * CELL - ny, x2: p.x * CELL + nx, y2: p.y * CELL + ny };
                }));
                return { paths: (w.openings || []).length ? paths : [d], jambs };
              })();
              return (
                <g key={`fw-${w.id}`}>
                  {ends.paths.map((pd, i) => (
                    <React.Fragment key={i}>
                      <path d={pd} fill="none" stroke={sel ? '#c04000' : '#26211c'} strokeWidth={10} strokeLinecap="butt" style={{ pointerEvents: 'none' }} />
                      <path d={pd} fill="none" stroke={sel ? '#f0a060' : '#e2ddd7'} strokeWidth={5}  strokeLinecap="butt" style={{ pointerEvents: 'none' }} />
                    </React.Fragment>
                  ))}
                  {ends.jambs.map(j => (
                    <line key={j.key} x1={j.x1} y1={j.y1} x2={j.x2} y2={j.y2}
                      stroke="#e07830" strokeWidth={3} strokeLinecap="round" style={{ pointerEvents: 'none' }} />
                  ))}
                  <path d={d} fill="none" stroke="transparent" strokeWidth={20}
                    style={{ cursor: 'pointer', pointerEvents: freeWallMode ? 'none' : 'stroke' }}
                    /* With an opening tool armed, clicking the wall PLACES it at
                       that point — same as a straight wall — instead of selecting. */
                    onMouseMove={(e) => {
                      if (!wallOpeningTool) return;
                      const p = freePointFromEvent(e);
                      if (p) setWallHoverInfo({ freeWallId: w.id, t: nearestTOnFreeWall(w, p) });
                    }}
                    onMouseLeave={() => setWallHoverInfo(prev => (prev?.freeWallId === w.id ? null : prev))}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (wallOpeningTool) {
                        const p = freePointFromEvent(e);
                        if (p) {
                          dispatch(addFreeWallOpening({ wallId: w.id, type: wallOpeningTool, t: nearestTOnFreeWall(w, p), ...(wallOpeningSize[wallOpeningTool] || {}) }));
                          setWallOpeningTool(null);
                          setWallHoverInfo(null);
                          return;
                        }
                      }
                      setSelectedFreeWallId(w.id); setSelectedRoomId?.(null);
                    }} />
                  {sel && [[w.x1, w.y1], [w.x2, w.y2]].map(([px, py], i) => (
                    <circle key={i} cx={px * CELL} cy={py * CELL} r={4} fill="#5096dc" stroke="white" strokeWidth="1.5" />
                  ))}
                  {/* Openings on the wall — the SAME symbol a straight wall uses
                      (select ring, armed dashes, door swing / window bars), just
                      angled to the wall and placed by `t`, so a curved wall
                      carries them round the arc. Select → resize / move / delete
                      all run through the shared opening panel. */}
                  {(w.openings || []).map(op => {
                    const isOpSelected = selectedWallOpeningInfo?.freeWallId === w.id && selectedWallOpeningInfo?.openingId === op.id;
                    // While dragging, follow the live preview instead of the stored spot.
                    const displayT = (openingDragPreview?.freeWallId === w.id && openingDragPreview?.openingId === op.id)
                      ? openingDragPreview.t : (op.t ?? 0.5);
                    const q = edgePointAt({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }, displayT);
                    const isArmed = openingMoveArmed && isOpSelected;
                    return (
                      <WallOpeningSymbol key={`fwo-${op.id}`}
                        type={op.type} cx={q.x * CELL} cy={q.y * CELL}
                        angle={q.angle * 180 / Math.PI}
                        width={op.width} color={op.color}
                        isSelected={isOpSelected}
                        armed={isArmed}
                        onClick={e => { e.stopPropagation(); selectWallSeg(null); setSelectedFreeWallId(null); setSelectedWallOpeningInfo({ freeWallId: w.id, openingId: op.id }); }}
                        onPointerDown={isArmed ? (e) => startOpeningDrag(e, {
                          freeWallId: w.id, freeWall: w, openingId: op.id,
                          isH: true, totalLen: edgeLength({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }) * CELL,
                          startT: op.t ?? 0.5, width: op.width,
                        }) : undefined}
                      />
                    );
                  })}
                  {/* Ghost of the opening about to be placed, following the cursor
                      along the wall — the straight walls' preview, on an arc. */}
                  {wallOpeningTool && wallHoverInfo?.freeWallId === w.id && (() => {
                    const q = edgePointAt({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }, wallHoverInfo.t);
                    return <WallOpeningSymbol type={wallOpeningTool} cx={q.x * CELL} cy={q.y * CELL}
                      angle={q.angle * 180 / Math.PI} width={wallOpeningSize[wallOpeningTool]?.width} opacity={0.55} />;
                  })()}
                  {/* Length label — same look as the grid walls'. A curved wall
                      reports its ARC length (what you'd actually build), not the
                      chord, and the label rides the arc's midpoint. */}
                  {(() => {
                    const a = { x: w.x1, y: w.y1, bulge: w.bulge || 0 }, b = { x: w.x2, y: w.y2 };
                    const mid = edgePointAt(a, b, 0.5);
                    const dxp = (w.x2 - w.x1) * CELL, dyp = (w.y2 - w.y1) * CELL;
                    const pl = Math.hypot(dxp, dyp) || 1;
                    // Sit on the OUTSIDE of the bow, clear of the 10px stroke.
                    const side = (w.bulge || 0) < 0 ? -1 : 1;
                    const lx = mid.x * CELL + (dyp / pl) * 14 * side;
                    const ly = mid.y * CELL + (-dxp / pl) * 14 * side;
                    // Keep the text upright whichever way the wall runs.
                    let deg = Math.atan2(dyp, dxp) * 180 / Math.PI;
                    if (deg > 90) deg -= 180; else if (deg < -90) deg += 180;
                    return (
                      <text
                        x={lx} y={ly}
                        textAnchor="middle" dominantBaseline="middle"
                        fontSize={11} fontFamily="system-ui, sans-serif" fontWeight="600"
                        fill={sel ? '#c04000' : '#26211c'}
                        stroke="rgba(255,255,255,0.85)" strokeWidth={3} paintOrder="stroke"
                        transform={`rotate(${deg}, ${lx}, ${ly})`}
                        style={{ pointerEvents: 'none' }}
                      >{edgeLength(a, b).toFixed(2)}m</text>
                    );
                  })()}
                </g>
              );
            })}
            {/* Live preview while drawing a free wall — or while re-curving one
                that's already placed (wall style, blue-tinted). */}
            {(freeWallMode || freeWallRecurving) && freeWallPreview && (() => {
              const pv = freeWallPreview;
              const len = Math.hypot(pv.x2 - pv.x1, pv.y2 - pv.y1);
              const angDeg = Math.round(Math.atan2(pv.y2 - pv.y1, pv.x2 - pv.x1) * 180 / Math.PI);
              const dxp = (pv.x2 - pv.x1) * CELL, dyp = (pv.y2 - pv.y1) * CELL;
              const pl = Math.hypot(dxp, dyp) || 1;
              const nx = dyp / pl, ny = -dxp / pl;
              const lx = (pv.x1 + pv.x2) / 2 * CELL + nx * (pv.bulge * CELL + 18);
              const ly = (pv.y1 + pv.y2) / 2 * CELL + ny * (pv.bulge * CELL + 18);
              const label = freeWallDraft?.stage === 'bulge'
                ? `${len.toFixed(2)}m · curve ${(Math.abs(pv.bulge)).toFixed(2)}m`
                : `${len.toFixed(2)}m · ${angDeg}°`;
              const pd = freeWallPath(pv.x1, pv.y1, pv.x2, pv.y2, pv.bulge);
              return (
                <g pointerEvents="none">
                  <path d={pd} fill="none" stroke="#26211c" strokeWidth={10} strokeLinecap="round" opacity="0.7" />
                  <path d={pd} fill="none" stroke="#8fd0ff" strokeWidth={5}  strokeLinecap="round" opacity="0.9" />
                  <circle cx={pv.x1 * CELL} cy={pv.y1 * CELL} r={5} fill="#f0a030" stroke="white" strokeWidth="1.5" />
                  <circle cx={pv.x2 * CELL} cy={pv.y2 * CELL} r={4} fill="#5096dc" stroke="white" strokeWidth="1.5" />
                  {/* Latched onto an existing corner / wall end — the ring says
                      this wall will join it exactly, which is what closes a loop. */}
                  {pv.snapped && (
                    <circle cx={pv.x2 * CELL} cy={pv.y2 * CELL} r={9} fill="none" stroke="#3fbf6f" strokeWidth={2.5} />
                  )}
                  <g transform={`translate(${lx},${ly})`}>
                    <rect x={-46} y={-10} width={92} height={20} rx={4} fill="rgba(20,24,34,0.92)" />
                    <text x={0} y={4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#dfe6f2">{label}</text>
                  </g>
                </g>
              );
            })()}

            {/* Same ring before the FIRST click, so it's clear the wall will
                start exactly on that corner rather than near it. */}
            {freeWallMode && !freeWallDraft && freeWallCursor && (() => {
              const s = snapToFreeEndpoints(freeWallCursor);
              if (s === freeWallCursor) return null;
              return <circle cx={s.x * CELL} cy={s.y * CELL} r={9} fill="none" stroke="#3fbf6f" strokeWidth={2.5} pointerEvents="none" />;
            })()}

            {/* Furniture ↔ wall distance dimension lines (selected or dragging).
                Hidden while drawing walls/rooms so the two overlays don't clash. */}
            {!wallMode && !drawMode && <FurnitureDimensions bbox={dimBBox} rooms={rooms} />}
          </svg>

          {/* Draw mode: capture overlay for rectangle drawing */}
          {drawMode && (
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 80, cursor: 'crosshair' }}
              onMouseDown={handleDrawMouseDown}
            />
          )}

          {/* Draw preview rectangle while dragging */}
          {drawMode && drawing && (
            <>
              <div
                className="draw-preview"
                style={{
                  left:   drawing.col * CELL,
                  top:    drawing.row * CELL,
                  width:  drawing.w   * CELL,
                  height: drawing.h   * CELL,
                }}
              />
              <div className="draw-preview-label" style={{
                left: (drawing.col + drawing.w / 2) * CELL,
                top:  (drawing.row + drawing.h / 2) * CELL,
              }}>
                {drawing.w}×{drawing.h}m
              </div>
            </>
          )}

          {/* Wall mode: capture overlay for tap/click-to-place walls.
              Pointer Events unify mouse + touch: on mouse, pointerdown is a
              no-op and pointerup commits (same moment onClick used to fire —
              desktop behavior is unchanged). On touch, pointerdown arms a
              "tap and drag" gesture, pointermove drags the live preview
              anywhere on the guide (not just the small orange dot), and
              lifting the finger (pointerup) commits the point. touchAction
              'none' plus pointer capture stop the page from scrolling mid-drag. */}
          {wallMode && (
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 80, cursor: 'crosshair', touchAction: 'none' }}
              onPointerDown={handleWallDrawPointerDown}
              onPointerMove={handleWallDrawPointerMove}
              onPointerUp={handleWallDrawPointerUp}
              onPointerCancel={handleWallDrawPointerUp}
              onPointerLeave={handleWallDrawPointerLeave}
              onDoubleClick={(e) => {
                if (wallChain.length < 2) return;
                const pt = getIntersection(e);
                if (!pt) return;
                const last = wallChain[wallChain.length - 1];
                if (pt.x === last.x && pt.y === last.y) {
                  // Commit chain segments but stay in wall mode so user can start a new chain
                  let anyBlocked = false;
                  for (let i = 0; i < wallChain.length - 1; i++) {
                    const a = wallChain[i], b = wallChain[i + 1];
                    if ((a.x === b.x || a.y === b.y) && Math.abs(a.x - b.x) + Math.abs(a.y - b.y) > 0) {
                      const seg = { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
                      if (!wallConflicts(seg, wallSegments, rooms)) dispatch(addWallSegment(seg));
                      else anyBlocked = true;
                    }
                  }
                  if (anyBlocked) flashWallBlocked();
                  setWallChain([]);
                  setWallMousePos(null);
                }
              }}
            />
          )}

          {/* Straight wall drawn, waiting on a choice: place it as-is, or switch
              to bowing it. Sits above the capture layer (z 80) so the buttons
              take the click instead of the canvas underneath. */}
          {freeWallMode && freeWallDraft?.stage === 'confirm' && (() => {
            const d = freeWallDraft;
            const dxp = (d.x2 - d.x1) * CELL, dyp = (d.y2 - d.y1) * CELL;
            const pl = Math.hypot(dxp, dyp) || 1;
            // Offset off the wall, opposite the length label, so neither covers it.
            const nx = dyp / pl, ny = -dxp / pl;
            const bx = (d.x1 + d.x2) / 2 * CELL - nx * 30;
            const by = (d.y1 + d.y2) / 2 * CELL - ny * 30;
            const btn = (label, title, onClick, primary) => (
              <button
                title={title}
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                style={{
                  width: 30, height: 30, borderRadius: 8, cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: primary ? '#3f7fc4' : 'rgba(28,30,40,0.96)',
                  border: `1px solid ${primary ? '#5096dc' : 'rgba(255,255,255,0.22)'}`,
                  color: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                }}
              >{label}</button>
            );
            return (
              <div
                style={{ position: 'absolute', left: bx, top: by, transform: 'translate(-50%,-50%)', zIndex: 90, display: 'flex', gap: 6 }}
                onMouseMove={e => e.stopPropagation()}
              >
                {btn(
                  <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
                    <path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>,
                  'Place this wall', () => commitFreeWall(0), true
                )}
                {btn(
                  <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
                    <path d="M2 11C2 5.5 6 2.5 12 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>,
                  'Bow it into a curve, then click to place',
                  () => setFreeWallDraft({ ...d, stage: 'bulge' }), false
                )}
              </div>
            );
          })()}

          {/* Free-form wall capture layer (click start → end → curve). Also on
              while re-curving a placed wall from its panel, which needs the same
              move-to-bow / click-to-place capture without the tool being armed. */}
          {(freeWallMode || freeWallRecurving) && (
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 80, cursor: 'crosshair', touchAction: 'none' }}
              onClick={handleFreeWallClick}
              onMouseMove={handleFreeWallMove}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (freeWallDraft?.stage === 'bulge') commitFreeWall(0);
              }}
            />
          )}

          {rooms.length === 0 && freeRooms.length === 0 && freeWalls.length === 0 && (
            <div className="grid-empty-state">
              <img
                src="/images/Tunify Original logo png.png"
                alt="Tunify"
                className="grid-empty-icon"
              />
              <p>Drag rooms here to start building</p>
            </div>
          )}
        </div>
        </div>{/* end zoom wrapper */}
      </div>
      </div>{/* end .builder-page */}
    </div>
  );
}

